/**
 * Sales Pipeline Module Page
 * Leads, opportunities, pipeline management, and sales activities
 * Enhanced with organization sync, dynamic stages, and drag-and-drop
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useTabbedChrome } from '@/components/weavein';
import { config } from '@/src/config';
import { AgentAssistantPanel } from '../../shared/components/AgentAssistant';
import {
  DollarSign,
  Users,
  Target,
  Calendar,
  TrendingUp,
  TrendingDown,
  Plus,
  Filter,
  Search,
  MoreVertical,
  Phone,
  Mail,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  X,
  Loader2,
  Edit,
  Eye,
  Trash2,
  Settings,
  GripVertical,
  ChevronDown,
  Save,
  RefreshCw,
  Building2,
  Package,
  BarChart3,
  ListChecks,
  UserPlus,
  PlayCircle,
  PauseCircle,
  XCircle,
  Download,
  Printer,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  useDroppable,
} from '@dnd-kit/core';
import {
  useSortable,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { apiClient } from '@/services/apiClient';
import { EmailIntegrationSettings } from '../../modules/sales-pipeline/components/EmailIntegrationSettings';
import { AssignEmailModal } from '../../modules/sales-pipeline/components/AssignEmailModal';
import { EmailInboxView } from '../../modules/sales-pipeline/components/EmailInboxView';
import { CalendarEventsView } from '../../modules/sales-pipeline/components/CalendarEventsView';
import { OpportunityDetailPanel } from '../../modules/sales-pipeline/components/OpportunityDetailPanel';
import { MeetingProviderSettings } from '@/components/settings/MeetingProviderSettings';
import { userManagementAPI, OrganizationUser } from '@/services/api/userManagement';
import { salesPipelineAPI, opportunitiesAPI, productsAPI, pipelineAPI, activitiesAPI, leadsAPI } from '@/services/api/salesPipelineAPI';
import { contactsApi, DirectoryContact } from '../../shared/services/platformApi';

// ============================================================================
// TYPES
// ============================================================================

type SalesTab = 'pipeline' | 'leads' | 'opportunities' | 'sales_intel' | 'products' | 'activities' | 'forecasts' | 'settings';

// Dynamic pipeline stage (replaces hardcoded PipelineStage type)
interface PipelineStageConfig {
  id: string;
  name: string;
  position: number;
  probability: number;  // 0-100%
  color: string;
  rottingDays?: number;  // Days before deal is "rotting"
  isWon?: boolean;
  isLost?: boolean;
}

// Pipeline configuration
interface Pipeline {
  id: string;
  name: string;
  description?: string;
  isDefault: boolean;
  stages: PipelineStageConfig[];
  createdAt: string;
  updatedAt: string;
}

// Organization from /api/cbos
interface Organization {
  id: number;
  name: string;
  short_name?: string;
  customer_status?: 'DEMO' | 'TRIAL' | 'ACTIVE' | 'INACTIVE';
  created_at?: string;
  city?: string;
  state?: string;
  website?: string;
  logo_url?: string;
  mrr?: number;  // Monthly Recurring Revenue / deal value
}

// Pricing model types for line items
type PricingModel = 'flat_rate' | 'per_user' | 'per_member' | 'tiered' | 'volume' | 'usage_based' | 'transaction' | 'outcome_based' | 'percentage_of_billing' | 'percentage_credit';
type BillingType = 'one_time' | 'recurring' | 'usage' | 'transaction' | 'outcome' | 'percentage';
type BillingInterval = 'monthly' | 'quarterly' | 'semi_annual' | 'annual';
// Basis for percentage calculations
type PercentageBasis = 'total_billing' | 'claims_processed' | 'chargebacks' | 'revenue' | 'custom';

// Tiered pricing structure (graduated tiers where each tier applies to its range)
interface PriceTier {
  id: string;
  minQuantity: number;
  maxQuantity: number | null; // null means unlimited
  unitPrice: number;
  flatFee?: number; // Optional flat fee for this tier
}

// Volume pricing structure (quantity thresholds where one price applies to all units)
interface VolumeThreshold {
  id: string;
  minQuantity: number;
  maxQuantity: number | null; // null means unlimited
  unitPrice: number; // Price per unit when within this threshold
}

// Line item in an opportunity
interface OpportunityLineItem {
  id: string;
  productId?: string;  // Reference to Product catalog item
  itemName: string;
  description?: string;
  pricingModel: PricingModel;
  quantity: number;
  unitPrice: number;
  billingType: BillingType;
  billingInterval?: BillingInterval;
  termMonths?: number;
  // Discounts
  discountType?: 'percent' | 'fixed';
  discountValue: number;
  // For transaction pricing
  estimatedTransactionVolume?: number;
  transactionRate?: number;
  // For outcome pricing
  estimatedOutcomes?: number;
  pricePerOutcome?: number;
  // For usage pricing
  estimatedUsage?: number;
  usagePrice?: number;
  includedUsage?: number;
  // For percentage-based pricing (% of billing, % credit for chargebacks)
  percentageRate?: number;           // The % rate (e.g., 3 for 3%)
  percentageBasis?: PercentageBasis; // What the % is calculated on
  percentageBasisCustomLabel?: string; // Custom label if basis is 'custom'
  estimatedBasisAmount?: number;     // Estimated annual amount the % applies to
  isCredit?: boolean;                // True if this is a credit (reduces total)
  // Calculated values
  lineTotal: number;
  discountAmount: number;
  netAmount: number;
  monthlyRecurring: number;
  annualRecurring: number;
}

// Product catalog item (template for line items)
interface Product {
  id: string;
  name: string;
  description?: string;
  sku?: string;
  category?: string;
  pricingModel: PricingModel;
  unitPrice: number;  // Base price for flat_rate, per_user, per_member
  billingType: BillingType;
  billingInterval?: BillingInterval;
  // For tiered pricing (graduated - each tier applies to its range)
  priceTiers?: PriceTier[];
  // For volume pricing (all units get same price based on quantity threshold)
  volumeThresholds?: VolumeThreshold[];
  // For transaction/usage pricing
  transactionRate?: number;  // Rate per transaction (e.g., $0.50 per transaction)
  usageRate?: number;        // Rate per usage unit
  minimumFee?: number;       // Minimum fee regardless of usage
  // For percentage-based products
  percentageRate?: number;           // The % rate (e.g., 3 for 3%)
  percentageBasis?: PercentageBasis; // What the % is calculated on
  percentageBasisCustomLabel?: string; // Custom label if basis is 'custom'
  isCredit?: boolean;                // True if this is a credit product (reduces total)
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Opportunity financials summary
interface OpportunityFinancials {
  totalOneTime: number;
  totalMrr: number;
  totalArr: number;
  estimatedUsageRevenue: number;
  estimatedTransactionRevenue: number;
  estimatedOutcomeRevenue: number;
  estimatedPercentageRevenue: number;  // Revenue from % of billing fees
  totalCredits: number;                 // Credits (chargebacks, etc.)
  subtotal: number;
  totalDiscount: number;
  grandTotal: number;
  totalContractValue: number;
}

interface Opportunity {
  id: string;
  name: string;
  company: string;
  companyId?: number;  // Links to organization ID
  amount: number;
  stage: string;  // Now dynamic, not hardcoded type
  probability: number;
  closeDate: string;
  owner: string;
  lastActivity: string;
  source?: string;
  notes?: string;
  contacts?: string[];
  createdAt?: string;
  // Pipeline association
  pipelineId?: string;  // Which pipeline this opportunity belongs to
  // Demo flag — hides from pipeline views unless explicitly toggled
  isDemoRecord?: boolean;
  // Line items and financials
  lineItems?: OpportunityLineItem[];
  financials?: OpportunityFinancials;
  // Contract terms
  contractTermMonths?: number;
  // Customer for billing
  customerId?: string;
  // Sales opportunity enhancement fields
  referredByContactId?: string;
  referredByName?: string;
  referralSource?: string;
  prospectRelationshipType?: string;
  prelimContext?: string;
  needsOverview?: string;
  demoCompleted?: boolean;
}

interface Lead {
  id: string;
  name: string;
  email: string;
  company: string;
  companyId?: number;
  source: string;
  score: number;
  status: 'new' | 'contacted' | 'qualified' | 'unqualified';
  createdAt: string;
}

interface Activity {
  id: string;
  type: 'call' | 'email' | 'meeting' | 'task' | 'note' | 'stage_change';
  title: string;
  description?: string;
  contact?: string;
  opportunityId?: string;
  organizationId?: number;
  dueDate?: string;
  completedAt?: string;
  completed: boolean;
  priority: 'high' | 'medium' | 'low';
  createdBy?: string;
  createdAt?: string;
  direction?: 'inbound' | 'outbound';
  emailId?: string;
  activityDate?: string;
  assignedToUserId?: number;
  assignedToName?: string;
  taskStatus?: 'not_started' | 'in_progress' | 'completed' | 'deferred' | 'cancelled';
  sourceActivityId?: string;
  sharedWithOrg?: boolean;
  metadata?: {
    from_email?: string;
    to_emails?: string[];
    cc_emails?: string[];
    provider?: string;
    folder?: string;
    importance?: string;
    has_attachments?: boolean;
  };
}

// ============================================================================
// DEFAULT PIPELINE CONFIGURATIONS
// ============================================================================

// Default sales pipeline for new business
const DEFAULT_NEW_BUSINESS_PIPELINE: Pipeline = {
  id: 'new-business',
  name: 'New Business',
  description: 'Pipeline for new customer acquisition',
  isDefault: true,
  stages: [
    { id: 'prospecting', name: 'Prospecting', position: 0, probability: 10, color: 'bg-gray-100' },
    { id: 'qualification', name: 'Qualification', position: 1, probability: 25, color: 'bg-blue-100' },
    { id: 'demo', name: 'Demo', position: 2, probability: 40, color: 'bg-yellow-100' },
    { id: 'proposal', name: 'Proposal', position: 3, probability: 60, color: 'bg-purple-100' },
    { id: 'negotiation', name: 'Negotiation', position: 4, probability: 80, color: 'bg-orange-100' },
    { id: 'closed_won', name: 'Closed Won', position: 5, probability: 100, color: 'bg-green-100', isWon: true },
    { id: 'closed_lost', name: 'Closed Lost', position: 6, probability: 0, color: 'bg-red-100', isLost: true },
  ],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

// Expansion pipeline for upsells and cross-sells
const DEFAULT_EXPANSION_PIPELINE: Pipeline = {
  id: 'expansion',
  name: 'Expansion',
  description: 'Pipeline for upsells and cross-sells to existing customers',
  isDefault: false,
  stages: [
    { id: 'identified', name: 'Opportunity Identified', position: 0, probability: 20, color: 'bg-gray-100' },
    { id: 'discovery', name: 'Discovery', position: 1, probability: 40, color: 'bg-blue-100' },
    { id: 'proposal', name: 'Proposal Sent', position: 2, probability: 60, color: 'bg-purple-100' },
    { id: 'negotiation', name: 'Negotiation', position: 3, probability: 80, color: 'bg-orange-100' },
    { id: 'closed_won', name: 'Closed Won', position: 4, probability: 100, color: 'bg-green-100', isWon: true },
    { id: 'closed_lost', name: 'Closed Lost', position: 5, probability: 0, color: 'bg-red-100', isLost: true },
  ],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

// Partnerships pipeline
const DEFAULT_PARTNERSHIPS_PIPELINE: Pipeline = {
  id: 'partnerships',
  name: 'Partnerships',
  description: 'Pipeline for strategic partnerships and channel deals',
  isDefault: false,
  stages: [
    { id: 'initial_contact', name: 'Initial Contact', position: 0, probability: 10, color: 'bg-gray-100' },
    { id: 'exploration', name: 'Exploration', position: 1, probability: 25, color: 'bg-blue-100' },
    { id: 'due_diligence', name: 'Due Diligence', position: 2, probability: 40, color: 'bg-yellow-100' },
    { id: 'terms_negotiation', name: 'Terms Negotiation', position: 3, probability: 60, color: 'bg-purple-100' },
    { id: 'legal_review', name: 'Legal Review', position: 4, probability: 80, color: 'bg-orange-100' },
    { id: 'partnership_active', name: 'Partnership Active', position: 5, probability: 100, color: 'bg-green-100', isWon: true },
    { id: 'declined', name: 'Declined', position: 6, probability: 0, color: 'bg-red-100', isLost: true },
  ],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

// Renewals pipeline
const DEFAULT_RENEWALS_PIPELINE: Pipeline = {
  id: 'renewals',
  name: 'Renewals',
  description: 'Pipeline for contract renewals',
  isDefault: false,
  stages: [
    { id: 'upcoming', name: 'Upcoming Renewal', position: 0, probability: 70, color: 'bg-gray-100' },
    { id: 'outreach', name: 'Outreach', position: 1, probability: 75, color: 'bg-blue-100' },
    { id: 'in_discussion', name: 'In Discussion', position: 2, probability: 80, color: 'bg-yellow-100' },
    { id: 'renewal_sent', name: 'Renewal Sent', position: 3, probability: 85, color: 'bg-purple-100' },
    { id: 'renewed', name: 'Renewed', position: 4, probability: 100, color: 'bg-green-100', isWon: true },
    { id: 'churned', name: 'Churned', position: 5, probability: 0, color: 'bg-red-100', isLost: true },
  ],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

// All default pipelines
const DEFAULT_PIPELINES: Pipeline[] = [
  DEFAULT_NEW_BUSINESS_PIPELINE,
  DEFAULT_EXPANSION_PIPELINE,
  DEFAULT_PARTNERSHIPS_PIPELINE,
  DEFAULT_RENEWALS_PIPELINE,
];

// Legacy default for backward compatibility
const DEFAULT_PIPELINE = DEFAULT_NEW_BUSINESS_PIPELINE;

// Map organization customer_status (4-value lifecycle, see backend migration 286)
// to a pipeline stage when seeding org-derived opportunities. Note: the
// backend now mirrors stage→status forward-only, so this mapping is only used
// for seed/display, not for write-back.
const STATUS_TO_STAGE_MAP: Record<string, string> = {
  'DEMO':     'qualification',
  'TRIAL':    'proposal',
  'ACTIVE':   'closed_won',
  'INACTIVE': 'closed_lost',
};

// Map marketing contact lifecycle stage to sales lead status
function mapLifecycleToLeadStatus(lifecycle_stage?: string): 'new' | 'contacted' | 'qualified' | 'unqualified' {
  switch (lifecycle_stage) {
    case 'subscriber':
      return 'new';
    case 'lead':
      return 'contacted';
    case 'mql':
    case 'sql':
      return 'qualified';
    case 'opportunity':
    case 'customer':
    case 'evangelist':
      return 'qualified';
    default:
      return 'new';
  }
}

// Initial mock data for activities (these will be stored per opportunity)
const mockActivities: Activity[] = [];

// Helper to get stage config by ID
const getStageConfig = (pipeline: Pipeline, stageId: string): PipelineStageConfig | undefined => {
  return pipeline.stages.find(s => s.id === stageId);
};

// ============================================================================
// MODAL COMPONENTS
// ============================================================================

interface AddOpportunityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (opp: Omit<Opportunity, 'id' | 'lastActivity'>, createNewOrg?: boolean) => void;
  pipeline: Pipeline;
  organizations: Organization[];
  users: OrganizationUser[];
  defaultStage?: string;
}

function AddOpportunityModal({ isOpen, onClose, onSubmit, pipeline, organizations, users, defaultStage }: AddOpportunityModalProps) {
  const { t } = useAppTranslation();
  const [name, setName] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState<number | ''>('');
  const [company, setCompany] = useState('');
  const [amount, setAmount] = useState('');
  const [stage, setStage] = useState(defaultStage || 'prospecting');
  const [probability, setProbability] = useState('25');
  const [closeDate, setCloseDate] = useState('');
  // Default to first user if available
  const [owner, setOwner] = useState(users.length > 0 ? users[0].name : '');
  const [searchQuery, setSearchQuery] = useState('');
  const [createNewOrg, setCreateNewOrg] = useState(false);
  const [isDemoRecord, setIsDemoRecord] = useState(false);

  // Filter organizations based on search
  const filteredOrgs = organizations.filter(org =>
    org.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (org.short_name && org.short_name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Update company when organization is selected
  const handleOrgSelect = (orgId: number) => {
    setSelectedOrgId(orgId);
    setCreateNewOrg(false);
    const org = organizations.find(o => o.id === orgId);
    if (org) {
      setCompany(org.name);
      if (!name) {
        setName(`${org.name} Deal`);
      }
    }
  };

  // Handle creating a new organization with the search query
  const handleCreateNewOrg = () => {
    setCompany(searchQuery);
    setCreateNewOrg(true);
    setSelectedOrgId('');
    if (!name) {
      setName(`${searchQuery} Deal`);
    }
    setSearchQuery('');
  };

  // Update probability when stage changes
  const handleStageChange = (newStage: string) => {
    setStage(newStage);
    const stageConfig = pipeline.stages.find(s => s.id === newStage);
    if (stageConfig) {
      setProbability(String(stageConfig.probability));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name,
      company,
      companyId: selectedOrgId || undefined,
      amount: parseFloat(amount) || 0,
      stage,
      probability: parseInt(probability),
      closeDate,
      owner,
      isDemoRecord: isDemoRecord || undefined,
    }, createNewOrg);
    // Reset form
    setName('');
    setSelectedOrgId('');
    setCompany('');
    setAmount('');
    setStage(defaultStage || 'prospecting');
    setProbability('25');
    setCloseDate('');
    setOwner(users.length > 0 ? users[0].name : '');
    setSearchQuery('');
    setCreateNewOrg(false);
    setIsDemoRecord(false);
    onClose();
  };

  // Get stages that are not won/lost for selection
  const selectableStages = pipeline.stages.filter(s => !s.isWon && !s.isLost);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('platform.salesPipeline.dialogs.addOpportunity.title', 'Add Opportunity')}</DialogTitle>
          <DialogDescription>
            {t('platform.salesPipeline.dialogs.addOpportunity.description', 'Create a new sales opportunity. Select an existing organization or create a new one.')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('platform.salesPipeline.dialogs.addOpportunity.organizationLabel', 'Organization')} *</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('platform.salesPipeline.dialogs.addOpportunity.searchPlaceholder', 'Search or type new organization name...')}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {searchQuery && (
              <div className="mt-1 border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
                {filteredOrgs.length > 0 && filteredOrgs.slice(0, 5).map(org => (
                  <button
                    key={org.id}
                    type="button"
                    onClick={() => {
                      handleOrgSelect(org.id);
                      setSearchQuery('');
                    }}
                    className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Building2 className="h-4 w-4 text-gray-400" />
                    <span className="text-sm">{org.name}</span>
                    {org.customer_status && (
                      <span className="ml-auto text-xs text-gray-500">{org.customer_status}</span>
                    )}
                  </button>
                ))}
                {/* Organizations must be created separately — not from the opportunity dialog */}
              </div>
            )}
            {selectedOrgId && (
              <div className="mt-2 flex items-center gap-2 text-sm text-gray-600 bg-blue-50 px-3 py-2 rounded-lg">
                <Building2 className="h-4 w-4" />
                <span>{organizations.find(o => o.id === selectedOrgId)?.name}</span>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedOrgId('');
                    setCompany('');
                  }}
                  className="ml-auto text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('platform.salesPipeline.dialogs.addOpportunity.nameLabel', 'Opportunity Name')} *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder={t('platform.salesPipeline.dialogs.addOpportunity.namePlaceholder', 'e.g., Enterprise Deal')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('platform.salesPipeline.common.amount', 'Amount')}</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  min="0"
                  placeholder="0"
                  className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('platform.salesPipeline.common.closeDate', 'Close Date')}</label>
              <input
                type="date"
                value={closeDate}
                onChange={(e) => setCloseDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('platform.salesPipeline.common.stage', 'Stage')}</label>
              <select
                value={stage}
                onChange={(e) => handleStageChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {selectableStages.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('platform.salesPipeline.common.probability', 'Probability')} (%)</label>
              <input
                type="number"
                value={probability}
                onChange={(e) => setProbability(e.target.value)}
                min="0"
                max="100"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('platform.salesPipeline.common.owner', 'Owner')}</label>
            <select
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {users.length > 0 ? (
                <>
                  <option value="">{t('platform.salesPipeline.common.unassigned', 'Unassigned')}</option>
                  {users.map(user => (
                    <option key={user.id} value={user.name}>{user.name}</option>
                  ))}
                </>
              ) : (
                <option value="" disabled>{t('platform.common.noUsersAvailable', 'No users available')}</option>
              )}
            </select>
          </div>
          <div className="flex items-center gap-2 py-2 px-3 bg-amber-50 border border-amber-200 rounded-lg">
            <input
              type="checkbox"
              id="isDemoRecord"
              checked={isDemoRecord}
              onChange={(e) => setIsDemoRecord(e.target.checked)}
              className="h-4 w-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
            />
            <label htmlFor="isDemoRecord" className="text-sm text-amber-800 cursor-pointer">
              {t('platform.salesPipeline.demo.flagLabel', 'Used for platform demo')}
              <span className="block text-xs text-amber-600">{t('platform.salesPipeline.demo.flagDescription', 'This record will be hidden from pipeline views unless the flag is removed')}</span>
            </label>
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('platform.salesPipeline.common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {t('platform.salesPipeline.dialogs.addOpportunity.submitButton', 'Add Opportunity')}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface AddLeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (lead: Omit<Lead, 'id' | 'createdAt'>) => void;
}

function AddLeadModal({ isOpen, onClose, onSubmit }: AddLeadModalProps) {
  const { t } = useAppTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [source, setSource] = useState('Website');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name,
      email,
      company,
      source,
      score: 50,
      status: 'new',
    });
    setName('');
    setEmail('');
    setCompany('');
    setSource('Website');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('platform.salesPipeline.dialogs.addLead.title', 'Add Lead')}</DialogTitle>
          <DialogDescription>
            {t('platform.salesPipeline.dialogs.addLead.description', 'Add a new lead to the pipeline.')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('platform.salesPipeline.common.name', 'Name')} *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder={t('platform.salesPipeline.dialogs.addLead.namePlaceholder', 'Full name')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('platform.salesPipeline.common.email', 'Email')} *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder={t('platform.salesPipeline.dialogs.addLead.emailPlaceholder', 'email@company.com')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('platform.salesPipeline.common.company', 'Company')} *</label>
            <input
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              required
              placeholder={t('platform.salesPipeline.dialogs.addLead.companyPlaceholder', 'Company name')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('platform.salesPipeline.common.source', 'Source')}</label>
            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="Website">{t('platform.salesPipeline.sources.website', 'Website')}</option>
              <option value="LinkedIn">{t('platform.salesPipeline.sources.linkedin', 'LinkedIn')}</option>
              <option value="Referral">{t('platform.salesPipeline.sources.referral', 'Referral')}</option>
              <option value="Webinar">{t('platform.salesPipeline.sources.webinar', 'Webinar')}</option>
              <option value="Trade Show">{t('platform.salesPipeline.sources.tradeShow', 'Trade Show')}</option>
              <option value="Cold Outreach">{t('platform.salesPipeline.sources.coldOutreach', 'Cold Outreach')}</option>
            </select>
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('platform.salesPipeline.common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {t('platform.salesPipeline.dialogs.addLead.submitButton', 'Add Lead')}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface ScheduleActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (activity: Omit<Activity, 'id'>) => void;
  opportunities?: Opportunity[];
}

function ScheduleActivityModal({ isOpen, onClose, onSubmit, opportunities }: ScheduleActivityModalProps) {
  const { t } = useAppTranslation();
  const [type, setType] = useState<Activity['type']>('call');
  const [title, setTitle] = useState('');
  const [selectedOpportunity, setSelectedOpportunity] = useState('');
  const [contact, setContact] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState<Activity['priority']>('medium');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const opp = opportunities?.find(o => o.id === selectedOpportunity);
    onSubmit({
      type,
      title,
      description: description || undefined,
      contact: contact || opp?.company || '',
      opportunityId: selectedOpportunity,
      dueDate,
      completed: false,
      priority,
      createdAt: new Date().toISOString(),
    });
    setType('call');
    setTitle('');
    setSelectedOpportunity('');
    setContact('');
    setDescription('');
    setDueDate('');
    setPriority('medium');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('platform.salesPipeline.dialogs.scheduleActivity.title', 'Schedule Activity')}</DialogTitle>
          <DialogDescription>
            {t('platform.salesPipeline.dialogs.scheduleActivity.description', 'Schedule a new sales activity linked to an opportunity.')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {opportunities && opportunities.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('platform.salesPipeline.common.opportunity', 'Opportunity')} *</label>
              <select
                value={selectedOpportunity}
                onChange={(e) => setSelectedOpportunity(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('platform.salesPipeline.dialogs.scheduleActivity.selectOpportunity', 'Select an opportunity...')}</option>
                {opportunities.map(opp => (
                  <option key={opp.id} value={opp.id}>{opp.name} - {opp.company}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('platform.salesPipeline.common.type', 'Type')}</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as Activity['type'])}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="call">{t('platform.salesPipeline.activityTypes.call', 'Call')}</option>
              <option value="email">{t('platform.salesPipeline.activityTypes.email', 'Email')}</option>
              <option value="meeting">{t('platform.salesPipeline.activityTypes.meeting', 'Meeting')}</option>
              <option value="task">{t('platform.salesPipeline.activityTypes.task', 'Task')}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('platform.salesPipeline.common.title', 'Title')} *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              placeholder={t('platform.salesPipeline.dialogs.scheduleActivity.titlePlaceholder', 'e.g., Follow-up call')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('platform.salesPipeline.common.contact', 'Contact')}</label>
            <input
              type="text"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              placeholder={t('platform.salesPipeline.dialogs.scheduleActivity.contactPlaceholder', 'Contact name (auto-filled from opportunity if blank)')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('platform.salesPipeline.common.description', 'Description')}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder={t('platform.salesPipeline.dialogs.scheduleActivity.descriptionPlaceholder', 'Activity details...')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('platform.salesPipeline.common.dueDate', 'Due Date')} *</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('platform.salesPipeline.common.priority', 'Priority')}</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as Activity['priority'])}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="low">{t('platform.salesPipeline.priorities.low', 'Low')}</option>
                <option value="medium">{t('platform.salesPipeline.priorities.medium', 'Medium')}</option>
                <option value="high">{t('platform.salesPipeline.priorities.high', 'High')}</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('platform.salesPipeline.common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {t('platform.salesPipeline.dialogs.scheduleActivity.submitButton', 'Schedule Activity')}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// ADD NOTE MODAL
// ============================================================================

interface AddNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (note: Omit<Activity, 'id'>) => void;
  opportunityName?: string;
  opportunities?: Opportunity[];
}

function AddNoteModal({ isOpen, onClose, onSubmit, opportunityName, opportunities }: AddNoteModalProps) {
  const { t } = useAppTranslation();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedOpportunity, setSelectedOpportunity] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const contact = opportunityName ||
      opportunities?.find(o => o.id === selectedOpportunity)?.company ||
      'General Note';

    onSubmit({
      type: 'note',
      title: title || 'Note',
      description: content,
      contact,
      opportunityId: selectedOpportunity || undefined,
      dueDate: new Date().toISOString().split('T')[0],
      completed: true,
      completedAt: new Date().toISOString(),
      priority: 'low',
      createdAt: new Date().toISOString(),
    });
    setTitle('');
    setContent('');
    setSelectedOpportunity('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{t('platform.salesPipeline.dialogs.addNote.title', 'Add Note')}</DialogTitle>
          <DialogDescription>
            {t('platform.salesPipeline.dialogs.addNote.description', 'Add a note to track important information about this opportunity.')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {!opportunityName && opportunities && opportunities.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('platform.salesPipeline.dialogs.addNote.relatedOpportunity', 'Related Opportunity')}</label>
              <select
                value={selectedOpportunity}
                onChange={(e) => setSelectedOpportunity(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('platform.salesPipeline.dialogs.addNote.generalNote', 'General Note')}</option>
                {opportunities.map(opp => (
                  <option key={opp.id} value={opp.id}>{opp.name} - {opp.company}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('platform.salesPipeline.common.title', 'Title')}</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('platform.salesPipeline.dialogs.addNote.titlePlaceholder', 'e.g., Meeting Summary, Key Decision, Follow-up Required')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('platform.salesPipeline.dialogs.addNote.contentLabel', 'Note Content')} *</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              required
              rows={6}
              placeholder={t('platform.salesPipeline.dialogs.addNote.contentPlaceholder', 'Write your note here...\n\nYou can include:\n• Key discussion points\n• Action items\n• Important decisions\n• Follow-up requirements')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
            />
            <p className="text-xs text-gray-500 mt-1">{content.length} {t('platform.salesPipeline.common.characters', 'characters')}</p>
          </div>

          {/* Quick Templates */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('platform.salesPipeline.dialogs.addNote.quickTemplates', 'Quick Templates')}</label>
            <div className="flex flex-wrap gap-2">
              {[
                { label: t('platform.salesPipeline.dialogs.addNote.templates.callSummary', 'Call Summary'), template: '📞 Call Summary\n\nDiscussed:\n• \n\nNext Steps:\n• \n\nFollow-up Date: ' },
                { label: t('platform.salesPipeline.dialogs.addNote.templates.meetingNotes', 'Meeting Notes'), template: '📅 Meeting Notes\n\nAttendees:\n• \n\nAgenda:\n1. \n\nKey Decisions:\n• \n\nAction Items:\n• ' },
                { label: t('platform.salesPipeline.dialogs.addNote.templates.emailFollowup', 'Email Follow-up'), template: '📧 Email Follow-up\n\nSubject: \n\nKey Points:\n• \n\nResponse Required: ' },
              ].map(({ label, template }) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => setContent(template)}
                  className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('platform.salesPipeline.common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Save className="h-4 w-4" />
              {t('platform.salesPipeline.dialogs.addNote.submitButton', 'Save Note')}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// LINE ITEMS EDITOR COMPONENT
// ============================================================================

// Helper function to calculate line item totals
function calculateLineItemTotals(item: Partial<OpportunityLineItem>): Partial<OpportunityLineItem> {
  const quantity = item.quantity || 0;
  const unitPrice = item.unitPrice || 0;
  const billingType = item.billingType || 'one_time';
  const billingInterval = item.billingInterval || 'monthly';
  const termMonths = item.termMonths || 12;
  const discountType = item.discountType;
  const discountValue = item.discountValue || 0;
  const isCredit = item.isCredit || false;

  let lineTotal = 0;
  let monthlyRecurring = 0;
  let annualRecurring = 0;

  // Calculate base line total based on pricing model
  if (billingType === 'one_time') {
    lineTotal = quantity * unitPrice;
  } else if (billingType === 'recurring') {
    const monthlyAmount = quantity * unitPrice;
    // Adjust for billing interval
    if (billingInterval === 'monthly') {
      monthlyRecurring = monthlyAmount;
    } else if (billingInterval === 'quarterly') {
      monthlyRecurring = monthlyAmount / 3;
    } else if (billingInterval === 'semi_annual') {
      monthlyRecurring = monthlyAmount / 6;
    } else if (billingInterval === 'annual') {
      monthlyRecurring = monthlyAmount / 12;
    }
    annualRecurring = monthlyRecurring * 12;
    lineTotal = monthlyRecurring * termMonths;
  } else if (billingType === 'transaction') {
    const volume = item.estimatedTransactionVolume || 0;
    const rate = item.transactionRate || 0;
    lineTotal = volume * (rate / 100);
    monthlyRecurring = lineTotal / (termMonths || 12);
    annualRecurring = monthlyRecurring * 12;
  } else if (billingType === 'outcome') {
    const outcomes = item.estimatedOutcomes || 0;
    const pricePerOutcome = item.pricePerOutcome || 0;
    lineTotal = outcomes * pricePerOutcome;
    monthlyRecurring = lineTotal / (termMonths || 12);
    annualRecurring = monthlyRecurring * 12;
  } else if (billingType === 'usage') {
    const usage = item.estimatedUsage || 0;
    const usagePrice = item.usagePrice || 0;
    const included = item.includedUsage || 0;
    const billableUsage = Math.max(0, usage - included);
    lineTotal = billableUsage * usagePrice;
    monthlyRecurring = lineTotal / (termMonths || 12);
    annualRecurring = monthlyRecurring * 12;
  } else if (billingType === 'percentage') {
    // Percentage-based pricing (% of billing, claims, chargebacks, etc.)
    const percentageRate = item.percentageRate || 0;
    const estimatedBasisAmount = item.estimatedBasisAmount || 0;
    // Calculate annual revenue from percentage
    annualRecurring = estimatedBasisAmount * (percentageRate / 100);
    monthlyRecurring = annualRecurring / 12;
    lineTotal = monthlyRecurring * termMonths;
    // If this is a credit, make the values negative
    if (isCredit) {
      lineTotal = -Math.abs(lineTotal);
      monthlyRecurring = -Math.abs(monthlyRecurring);
      annualRecurring = -Math.abs(annualRecurring);
    }
  }

  // Calculate discount (not applicable to credits)
  let discountAmount = 0;
  if (!isCredit) {
    if (discountType === 'percent' && discountValue > 0) {
      discountAmount = lineTotal * (discountValue / 100);
    } else if (discountType === 'fixed' && discountValue > 0) {
      discountAmount = discountValue;
    }
  }

  const netAmount = lineTotal - discountAmount;

  return {
    ...item,
    lineTotal,
    discountAmount,
    netAmount,
    monthlyRecurring: billingType === 'one_time' ? 0 : monthlyRecurring,
    annualRecurring: billingType === 'one_time' ? 0 : annualRecurring,
  };
}

// Helper to calculate opportunity financials from line items
function calculateOpportunityFinancials(lineItems: OpportunityLineItem[]): OpportunityFinancials {
  const financials: OpportunityFinancials = {
    totalOneTime: 0,
    totalMrr: 0,
    totalArr: 0,
    estimatedUsageRevenue: 0,
    estimatedTransactionRevenue: 0,
    estimatedOutcomeRevenue: 0,
    estimatedPercentageRevenue: 0,
    totalCredits: 0,
    subtotal: 0,
    totalDiscount: 0,
    grandTotal: 0,
    totalContractValue: 0,
  };

  lineItems.forEach(item => {
    // Handle credits separately (negative values)
    if (item.isCredit) {
      financials.totalCredits += item.netAmount; // netAmount is already negative for credits
      financials.grandTotal += item.netAmount;
      // Credits reduce MRR/ARR
      financials.totalMrr += item.monthlyRecurring;
      financials.totalArr += item.annualRecurring;
    } else {
      financials.subtotal += item.lineTotal;
      financials.totalDiscount += item.discountAmount;
      financials.grandTotal += item.netAmount;

      if (item.billingType === 'one_time') {
        financials.totalOneTime += item.netAmount;
      } else {
        financials.totalMrr += item.monthlyRecurring;
        financials.totalArr += item.annualRecurring;
      }

      if (item.billingType === 'usage') {
        financials.estimatedUsageRevenue += item.netAmount;
      } else if (item.billingType === 'transaction') {
        financials.estimatedTransactionRevenue += item.netAmount;
      } else if (item.billingType === 'outcome') {
        financials.estimatedOutcomeRevenue += item.netAmount;
      } else if (item.billingType === 'percentage') {
        financials.estimatedPercentageRevenue += item.netAmount;
      }
    }
  });

  // TCV = one-time + annual recurring * years (assume 1 year if not specified)
  financials.totalContractValue = financials.totalOneTime + financials.totalArr;

  return financials;
}

interface LineItemsEditorProps {
  lineItems: OpportunityLineItem[];
  onUpdate: (lineItems: OpportunityLineItem[]) => void;
  products: Product[];
  readOnly?: boolean;
}

function LineItemsEditor({ lineItems, onUpdate, products, readOnly = false }: LineItemsEditorProps) {
  const { t } = useAppTranslation();
  const [showAddItem, setShowAddItem] = useState(false);
  const [editingItem, setEditingItem] = useState<OpportunityLineItem | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [newItem, setNewItem] = useState<Partial<OpportunityLineItem>>({
    pricingModel: 'per_user',
    billingType: 'recurring',
    billingInterval: 'monthly',
    quantity: 1,
    unitPrice: 0,
    discountValue: 0,
    termMonths: 12,
  });

  // When a product is selected, populate the form with product data
  const handleProductSelect = (productId: string) => {
    setSelectedProductId(productId);
    const product = products.find(p => p.id === productId);
    if (product) {
      setNewItem({
        productId: product.id,
        itemName: product.name,
        description: product.description,
        pricingModel: product.pricingModel,
        unitPrice: product.unitPrice,
        billingType: product.billingType,
        billingInterval: product.billingInterval,
        // Percentage-based fields from product
        percentageRate: product.percentageRate,
        percentageBasis: product.percentageBasis,
        percentageBasisCustomLabel: product.percentageBasisCustomLabel,
        isCredit: product.isCredit,
        quantity: 1,
        discountValue: 0,
        termMonths: 12,
      });
    }
  };

  const activeProducts = products.filter(p => p.isActive);

  const financials = calculateOpportunityFinancials(lineItems);

  const handleAddItem = () => {
    // For percentage billing, we need percentageRate and estimatedBasisAmount instead of unitPrice
    const isPercentageBilling = newItem.billingType === 'percentage';
    if (!newItem.itemName) return;
    if (!isPercentageBilling && !newItem.unitPrice) return;
    if (isPercentageBilling && (!newItem.percentageRate || !newItem.estimatedBasisAmount)) return;

    const calculatedItem = calculateLineItemTotals(newItem);
    const item: OpportunityLineItem = {
      id: `li_${Date.now()}`,
      productId: newItem.productId,  // Link to product catalog
      itemName: newItem.itemName || '',
      description: newItem.description,
      pricingModel: newItem.pricingModel || 'flat_rate',
      quantity: newItem.quantity || 1,
      unitPrice: newItem.unitPrice || 0,
      billingType: newItem.billingType || 'one_time',
      billingInterval: newItem.billingInterval,
      termMonths: newItem.termMonths,
      discountType: newItem.discountType,
      discountValue: newItem.discountValue || 0,
      estimatedTransactionVolume: newItem.estimatedTransactionVolume,
      transactionRate: newItem.transactionRate,
      estimatedOutcomes: newItem.estimatedOutcomes,
      pricePerOutcome: newItem.pricePerOutcome,
      estimatedUsage: newItem.estimatedUsage,
      usagePrice: newItem.usagePrice,
      includedUsage: newItem.includedUsage,
      // Percentage-based fields
      percentageRate: newItem.percentageRate,
      percentageBasis: newItem.percentageBasis,
      percentageBasisCustomLabel: newItem.percentageBasisCustomLabel,
      estimatedBasisAmount: newItem.estimatedBasisAmount,
      isCredit: newItem.isCredit,
      // Calculated
      lineTotal: calculatedItem.lineTotal || 0,
      discountAmount: calculatedItem.discountAmount || 0,
      netAmount: calculatedItem.netAmount || 0,
      monthlyRecurring: calculatedItem.monthlyRecurring || 0,
      annualRecurring: calculatedItem.annualRecurring || 0,
    };

    onUpdate([...lineItems, item]);
    setSelectedProductId('');
    setNewItem({
      pricingModel: 'per_user',
      billingType: 'recurring',
      billingInterval: 'monthly',
      quantity: 1,
      unitPrice: 0,
      discountValue: 0,
      termMonths: 12,
    });
    setShowAddItem(false);
  };

  const handleUpdateItem = (updatedItem: OpportunityLineItem) => {
    const recalculated = calculateLineItemTotals(updatedItem);
    const item: OpportunityLineItem = {
      ...updatedItem,
      lineTotal: recalculated.lineTotal || 0,
      discountAmount: recalculated.discountAmount || 0,
      netAmount: recalculated.netAmount || 0,
      monthlyRecurring: recalculated.monthlyRecurring || 0,
      annualRecurring: recalculated.annualRecurring || 0,
    };
    onUpdate(lineItems.map(li => li.id === item.id ? item : li));
    setEditingItem(null);
  };

  const handleDeleteItem = (itemId: string) => {
    onUpdate(lineItems.filter(li => li.id !== itemId));
  };

  const getBillingTypeLabel = (type: BillingType): string => {
    switch (type) {
      case 'one_time': return t('platform.salesPipeline.billingTypes.oneTime', 'One-Time');
      case 'recurring': return t('platform.salesPipeline.billingTypes.recurring', 'Recurring');
      case 'usage': return t('platform.salesPipeline.billingTypes.usageBased', 'Usage-Based');
      case 'transaction': return t('platform.salesPipeline.billingTypes.transaction', 'Transaction');
      case 'outcome': return t('platform.salesPipeline.billingTypes.outcomeBased', 'Outcome-Based');
      case 'percentage': return t('platform.salesPipeline.billingTypes.percentage', '% of Billing');
      default: return type;
    }
  };

  const getPercentageBasisLabel = (basis: PercentageBasis): string => {
    switch (basis) {
      case 'total_billing': return t('platform.salesPipeline.percentageBasis.totalBilling', 'Total Billing');
      case 'claims_processed': return t('platform.salesPipeline.percentageBasis.claimsProcessed', 'Claims Processed');
      case 'chargebacks': return t('platform.salesPipeline.percentageBasis.chargebacks', 'Chargebacks');
      case 'revenue': return t('platform.salesPipeline.percentageBasis.revenue', 'Revenue');
      case 'custom': return t('platform.salesPipeline.percentageBasis.custom', 'Custom');
      default: return basis;
    }
  };

  const getPricingModelLabel = (model: PricingModel): string => {
    switch (model) {
      case 'flat_rate': return t('platform.salesPipeline.pricingModels.flatRate', 'Flat Rate');
      case 'per_user': return t('platform.salesPipeline.pricingModels.perUser', 'Per User');
      case 'per_member': return t('platform.salesPipeline.pricingModels.perMember', 'Per Member');
      case 'tiered': return t('platform.salesPipeline.pricingModels.tiered', 'Tiered');
      case 'volume': return t('platform.salesPipeline.pricingModels.volume', 'Volume');
      case 'usage_based': return t('platform.salesPipeline.pricingModels.usageBased', 'Usage-Based');
      case 'transaction': return t('platform.salesPipeline.pricingModels.transaction', 'Transaction %');
      case 'outcome_based': return t('platform.salesPipeline.pricingModels.outcomeBased', 'Outcome-Based');
      case 'percentage_of_billing': return t('platform.salesPipeline.pricingModels.percentageOfBilling', '% of Billing');
      case 'percentage_credit': return t('platform.salesPipeline.pricingModels.percentageCredit', '% Credit');
      default: return model;
    }
  };

  return (
    <div className="space-y-4">
      {/* Line Items List */}
      <div className="space-y-2">
        {lineItems.length === 0 ? (
          <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
            <DollarSign className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500">{t('platform.salesPipeline.lineItems.noItems', 'No line items added yet')}</p>
            <p className="text-xs text-gray-400 mt-1">{t('platform.salesPipeline.lineItems.noItemsHint', 'Add products, services, or subscriptions to this opportunity')}</p>
          </div>
        ) : (
          lineItems.map((item) => (
            <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-3">
              {editingItem?.id === item.id ? (
                // Edit mode
                <div className="space-y-3">
                  <input
                    type="text"
                    value={editingItem.itemName}
                    onChange={(e) => setEditingItem({ ...editingItem, itemName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder={t('platform.salesPipeline.lineItems.itemNamePlaceholder', 'Item name')}
                  />
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">{t('platform.salesPipeline.lineItems.qty', 'Qty')}</label>
                      <input
                        type="number"
                        value={editingItem.quantity}
                        onChange={(e) => setEditingItem({ ...editingItem, quantity: parseInt(e.target.value) || 0 })}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">{t('platform.salesPipeline.lineItems.unitPrice', 'Unit Price')}</label>
                      <input
                        type="number"
                        value={editingItem.unitPrice}
                        onChange={(e) => setEditingItem({ ...editingItem, unitPrice: parseFloat(e.target.value) || 0 })}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">{t('platform.salesPipeline.lineItems.discountPercent', 'Discount %')}</label>
                      <input
                        type="number"
                        value={editingItem.discountValue}
                        onChange={(e) => setEditingItem({ ...editingItem, discountType: 'percent', discountValue: parseFloat(e.target.value) || 0 })}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleUpdateItem(editingItem)}
                      className="px-3 py-1 bg-blue-600 text-white rounded text-sm"
                    >
                      {t('platform.salesPipeline.common.save', 'Save')}
                    </button>
                    <button
                      onClick={() => setEditingItem(null)}
                      className="px-3 py-1 border border-gray-300 rounded text-sm"
                    >
                      {t('platform.salesPipeline.common.cancel', 'Cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                // View mode
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${item.isCredit ? 'text-red-700' : 'text-gray-900'}`}>{item.itemName}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        item.isCredit ? 'bg-red-100 text-red-700' :
                        item.billingType === 'recurring' ? 'bg-blue-100 text-blue-700' :
                        item.billingType === 'usage' ? 'bg-purple-100 text-purple-700' :
                        item.billingType === 'transaction' ? 'bg-orange-100 text-orange-700' :
                        item.billingType === 'outcome' ? 'bg-green-100 text-green-700' :
                        item.billingType === 'percentage' ? 'bg-indigo-100 text-indigo-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {item.isCredit ? t('platform.salesPipeline.lineItems.credit', 'Credit') : getBillingTypeLabel(item.billingType)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {item.billingType === 'percentage' ? (
                        <span>
                          {item.percentageRate}% of {item.percentageBasis === 'custom' ? item.percentageBasisCustomLabel : getPercentageBasisLabel(item.percentageBasis || 'total_billing')}
                          {item.estimatedBasisAmount ? ` (est. ${formatCurrency(item.estimatedBasisAmount)}/yr)` : ''}
                        </span>
                      ) : item.pricingModel === 'per_user' || item.pricingModel === 'per_member' ? (
                        <span>{item.quantity} × {formatCurrency(item.unitPrice)}/{item.pricingModel === 'per_user' ? 'user' : 'member'}</span>
                      ) : item.billingType === 'transaction' ? (
                        <span>{item.transactionRate}% on {formatCurrency(item.estimatedTransactionVolume || 0)} volume</span>
                      ) : item.billingType === 'outcome' ? (
                        <span>{formatCurrency(item.pricePerOutcome || 0)}/outcome × {item.estimatedOutcomes || 0} est.</span>
                      ) : (
                        <span>{item.quantity} × {formatCurrency(item.unitPrice)}</span>
                      )}
                      {item.billingInterval && item.billingType !== 'percentage' && (
                        <span className="ml-2">/ {item.billingInterval}</span>
                      )}
                      {item.discountValue > 0 && !item.isCredit && (
                        <span className="ml-2 text-green-600">(-{item.discountValue}%)</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`font-medium ${item.isCredit || item.netAmount < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                      {item.isCredit || item.netAmount < 0 ? '(' : ''}{formatCurrency(Math.abs(item.netAmount))}{item.isCredit || item.netAmount < 0 ? ')' : ''}
                    </div>
                    {item.monthlyRecurring !== 0 && (
                      <div className={`text-xs ${item.monthlyRecurring < 0 ? 'text-red-500' : 'text-gray-500'}`}>
                        {item.monthlyRecurring < 0 ? '(' : ''}{formatCurrency(Math.abs(item.monthlyRecurring))}/mo{item.monthlyRecurring < 0 ? ')' : ''} MRR
                      </div>
                    )}
                  </div>
                  {!readOnly && (
                    <div className="ml-3 flex gap-1">
                      <button
                        onClick={() => setEditingItem(item)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteItem(item.id)}
                        className="p-1 text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Add New Line Item */}
      {!readOnly && (
        <>
          {showAddItem ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
              <h4 className="font-medium text-blue-900">{t('platform.salesPipeline.lineItems.addLineItem', 'Add Line Item')}</h4>

              {/* Product Picker - Primary method */}
              {activeProducts.length > 0 && (
                <div>
                  <label className="block text-xs text-gray-600 mb-1">{t('platform.salesPipeline.lineItems.selectFromCatalog', 'Select from Product Catalog')}</label>
                  <select
                    value={selectedProductId}
                    onChange={(e) => handleProductSelect(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  >
                    <option value="">{t('platform.salesPipeline.lineItems.chooseProduct', '-- Choose a product --')}</option>
                    {activeProducts.map(product => (
                      <option key={product.id} value={product.id}>
                        {product.name} - {formatCurrency(product.unitPrice)}
                        {product.billingType === 'recurring' && product.billingInterval ? `/${product.billingInterval}` : ''}
                        {product.sku ? ` (${product.sku})` : ''}
                      </option>
                    ))}
                  </select>
                  {selectedProductId && (
                    <p className="text-xs text-green-600 mt-1">
                      {t('platform.salesPipeline.lineItems.productSelectedHint', 'Product selected. Customize quantity and discount below.')}
                    </p>
                  )}
                </div>
              )}

              {activeProducts.length === 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                  <p className="font-medium">{t('platform.salesPipeline.lineItems.noProductsTitle', 'No products in catalog')}</p>
                  <p className="text-xs mt-1">{t('platform.salesPipeline.lineItems.noProductsHint', 'Add products in the Products & Pricing tab first, or enter details manually below.')}</p>
                </div>
              )}

              {/* Divider if products exist */}
              {activeProducts.length > 0 && (
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="px-2 bg-blue-50 text-gray-500">
                      {selectedProductId ? t('platform.salesPipeline.lineItems.customize', 'Customize') : t('platform.salesPipeline.lineItems.orEnterManually', 'or enter manually')}
                    </span>
                  </div>
                </div>
              )}

              {/* Item Name - editable but pre-filled from product */}
              <div>
                <label className="block text-xs text-gray-600 mb-1">{t('platform.salesPipeline.lineItems.itemName', 'Item Name')} *</label>
                <input
                  type="text"
                  value={newItem.itemName || ''}
                  onChange={(e) => setNewItem({ ...newItem, itemName: e.target.value })}
                  placeholder={t('platform.salesPipeline.lineItems.itemNameExample', 'e.g., Platform License, Implementation Services')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>

              {/* Quantity and Discount - always show for customization */}
              {(newItem.billingType === 'one_time' || newItem.billingType === 'recurring') && (
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      {newItem.pricingModel === 'per_user' ? t('platform.salesPipeline.lineItems.users', 'Users') :
                       newItem.pricingModel === 'per_member' ? t('platform.salesPipeline.lineItems.members', 'Members') : t('platform.salesPipeline.lineItems.quantity', 'Quantity')}
                    </label>
                    <input
                      type="number"
                      value={newItem.quantity || 1}
                      onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 1 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">{t('platform.salesPipeline.lineItems.unitPriceDollar', 'Unit Price ($)')}</label>
                    <input
                      type="number"
                      value={newItem.unitPrice || 0}
                      onChange={(e) => setNewItem({ ...newItem, unitPrice: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">{t('platform.salesPipeline.lineItems.discountPercentLabel', 'Discount (%)')}</label>
                    <input
                      type="number"
                      value={newItem.discountValue || 0}
                      onChange={(e) => setNewItem({ ...newItem, discountType: 'percent', discountValue: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
              )}

              {/* Advanced options - collapsible for manual entry */}
              {!selectedProductId && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">{t('platform.salesPipeline.lineItems.pricingModel', 'Pricing Model')}</label>
                      <select
                        value={newItem.pricingModel}
                        onChange={(e) => setNewItem({ ...newItem, pricingModel: e.target.value as PricingModel })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="flat_rate">{t('platform.salesPipeline.pricingModels.flatRate', 'Flat Rate')}</option>
                        <option value="per_user">{t('platform.salesPipeline.pricingModels.perUserSaas', 'Per User (SaaS)')}</option>
                        <option value="per_member">{t('platform.salesPipeline.pricingModels.perMemberHealthcare', 'Per Member (Healthcare)')}</option>
                        <option value="tiered">{t('platform.salesPipeline.pricingModels.tieredPricing', 'Tiered Pricing')}</option>
                        <option value="volume">{t('platform.salesPipeline.pricingModels.volumePricing', 'Volume Pricing')}</option>
                        <option value="usage_based">{t('platform.salesPipeline.pricingModels.usageBased', 'Usage-Based')}</option>
                        <option value="transaction">{t('platform.salesPipeline.pricingModels.transaction', 'Transaction %')}</option>
                        <option value="outcome_based">{t('platform.salesPipeline.pricingModels.outcomeBased', 'Outcome-Based')}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">{t('platform.salesPipeline.lineItems.billingType', 'Billing Type')}</label>
                      <select
                        value={newItem.billingType}
                        onChange={(e) => setNewItem({ ...newItem, billingType: e.target.value as BillingType })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="one_time">{t('platform.salesPipeline.billingTypes.oneTime', 'One-Time')}</option>
                        <option value="recurring">{t('platform.salesPipeline.billingTypes.recurring', 'Recurring')}</option>
                        <option value="usage">{t('platform.salesPipeline.billingTypes.usageBased', 'Usage-Based')}</option>
                        <option value="transaction">{t('platform.salesPipeline.billingTypes.transaction', 'Transaction')}</option>
                        <option value="outcome">{t('platform.salesPipeline.billingTypes.outcomeBased', 'Outcome-Based')}</option>
                        <option value="percentage">{t('platform.salesPipeline.billingTypes.percentageRevenue', '% of Billing/Revenue')}</option>
                      </select>
                    </div>
                  </div>

                  {/* Percentage-based pricing fields */}
                  {newItem.billingType === 'percentage' && (
                    <div className="space-y-3 bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">{t('platform.salesPipeline.lineItems.percentageRate', 'Percentage Rate (%)')}</label>
                          <input
                            type="number"
                            step="0.01"
                            value={newItem.percentageRate || ''}
                            onChange={(e) => setNewItem({ ...newItem, percentageRate: parseFloat(e.target.value) || 0 })}
                            placeholder={t('platform.salesPipeline.lineItems.percentageRatePlaceholder', 'e.g., 3.0')}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">{t('platform.salesPipeline.lineItems.calculatedOn', 'Calculated On')}</label>
                          <select
                            value={newItem.percentageBasis || 'total_billing'}
                            onChange={(e) => setNewItem({ ...newItem, percentageBasis: e.target.value as PercentageBasis })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          >
                            <option value="total_billing">{t('platform.salesPipeline.percentageBasis.totalBilling', 'Total Billing')}</option>
                            <option value="claims_processed">{t('platform.salesPipeline.percentageBasis.claimsProcessed', 'Claims Processed')}</option>
                            <option value="chargebacks">{t('platform.salesPipeline.percentageBasis.chargebacks', 'Chargebacks')}</option>
                            <option value="revenue">{t('platform.salesPipeline.percentageBasis.revenue', 'Revenue')}</option>
                            <option value="custom">{t('platform.salesPipeline.percentageBasis.custom', 'Custom')}</option>
                          </select>
                        </div>
                      </div>
                      {newItem.percentageBasis === 'custom' && (
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">{t('platform.salesPipeline.lineItems.customBasisLabel', 'Custom Basis Label')}</label>
                          <input
                            type="text"
                            value={newItem.percentageBasisCustomLabel || ''}
                            onChange={(e) => setNewItem({ ...newItem, percentageBasisCustomLabel: e.target.value })}
                            placeholder={t('platform.salesPipeline.lineItems.customBasisPlaceholder', 'e.g., Premium Collections')}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">{t('platform.salesPipeline.lineItems.estAnnualBasis', 'Est. Annual Basis Amount ($)')}</label>
                          <input
                            type="number"
                            value={newItem.estimatedBasisAmount || ''}
                            onChange={(e) => setNewItem({ ...newItem, estimatedBasisAmount: parseFloat(e.target.value) || 0 })}
                            placeholder={t('platform.salesPipeline.lineItems.estAnnualBasisPlaceholder', 'e.g., 5000000')}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            {t('platform.salesPipeline.lineItems.estRevenue', 'Est. revenue:')} {formatCurrency((newItem.estimatedBasisAmount || 0) * ((newItem.percentageRate || 0) / 100))}/{t('platform.salesPipeline.common.yr', 'yr')}
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">{t('platform.salesPipeline.lineItems.contractTerm', 'Contract Term (months)')}</label>
                          <input
                            type="number"
                            value={newItem.termMonths || 12}
                            onChange={(e) => setNewItem({ ...newItem, termMonths: parseInt(e.target.value) || 12 })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="isCredit"
                          checked={newItem.isCredit || false}
                          onChange={(e) => setNewItem({ ...newItem, isCredit: e.target.checked })}
                          className="rounded border-gray-300"
                        />
                        <label htmlFor="isCredit" className="text-sm text-gray-700">
                          {t('platform.salesPipeline.lineItems.isCreditLabel', 'This is a credit (reduces total, e.g., chargeback credit)')}
                        </label>
                      </div>
                    </div>
                  )}

                  {newItem.billingType === 'recurring' && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">{t('platform.salesPipeline.lineItems.billingInterval', 'Billing Interval')}</label>
                        <select
                          value={newItem.billingInterval}
                          onChange={(e) => setNewItem({ ...newItem, billingInterval: e.target.value as BillingInterval })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="monthly">{t('platform.salesPipeline.billingIntervals.monthly', 'Monthly')}</option>
                          <option value="quarterly">{t('platform.salesPipeline.billingIntervals.quarterly', 'Quarterly')}</option>
                          <option value="semi_annual">{t('platform.salesPipeline.billingIntervals.semiAnnual', 'Semi-Annual')}</option>
                          <option value="annual">{t('platform.salesPipeline.billingIntervals.annual', 'Annual')}</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">{t('platform.salesPipeline.lineItems.contractTerm', 'Contract Term (months)')}</label>
                        <input
                          type="number"
                          value={newItem.termMonths || 12}
                          onChange={(e) => setNewItem({ ...newItem, termMonths: parseInt(e.target.value) || 12 })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                    </div>
                  )}

                  {/* Transaction pricing fields */}
                  {newItem.billingType === 'transaction' && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">{t('platform.salesPipeline.lineItems.estTransactionVolume', 'Est. Transaction Volume ($)')}</label>
                        <input
                          type="number"
                          value={newItem.estimatedTransactionVolume || 0}
                          onChange={(e) => setNewItem({ ...newItem, estimatedTransactionVolume: parseFloat(e.target.value) || 0 })}
                          placeholder={t('platform.salesPipeline.lineItems.estTransactionVolumePlaceholder', 'e.g., 100000')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">{t('platform.salesPipeline.lineItems.transactionRate', 'Transaction Rate (%)')}</label>
                        <input
                          type="number"
                          step="0.01"
                          value={newItem.transactionRate || 0}
                          onChange={(e) => setNewItem({ ...newItem, transactionRate: parseFloat(e.target.value) || 0 })}
                          placeholder={t('platform.salesPipeline.lineItems.transactionRatePlaceholder', 'e.g., 2.9')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                    </div>
                  )}

                  {/* Outcome pricing fields */}
                  {newItem.billingType === 'outcome' && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">{t('platform.salesPipeline.lineItems.estOutcomes', 'Est. Outcomes (annually)')}</label>
                        <input
                          type="number"
                          value={newItem.estimatedOutcomes || 0}
                          onChange={(e) => setNewItem({ ...newItem, estimatedOutcomes: parseInt(e.target.value) || 0 })}
                          placeholder={t('platform.salesPipeline.lineItems.estOutcomesPlaceholder', 'e.g., 500 members enrolled')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">{t('platform.salesPipeline.lineItems.pricePerOutcome', 'Price per Outcome ($)')}</label>
                        <input
                          type="number"
                          value={newItem.pricePerOutcome || 0}
                          onChange={(e) => setNewItem({ ...newItem, pricePerOutcome: parseFloat(e.target.value) || 0 })}
                          placeholder={t('platform.salesPipeline.lineItems.pricePerOutcomePlaceholder', 'e.g., 50')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                    </div>
                  )}

                  {/* Usage pricing fields */}
                  {newItem.billingType === 'usage' && (
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">{t('platform.salesPipeline.lineItems.estMonthlyUsage', 'Est. Monthly Usage')}</label>
                        <input
                          type="number"
                          value={newItem.estimatedUsage || 0}
                          onChange={(e) => setNewItem({ ...newItem, estimatedUsage: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">{t('platform.salesPipeline.lineItems.includedUsage', 'Included Usage')}</label>
                        <input
                          type="number"
                          value={newItem.includedUsage || 0}
                          onChange={(e) => setNewItem({ ...newItem, includedUsage: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">{t('platform.salesPipeline.lineItems.overagePrice', 'Overage Price ($)')}</label>
                        <input
                          type="number"
                          step="0.01"
                          value={newItem.usagePrice || 0}
                          onChange={(e) => setNewItem({ ...newItem, usagePrice: parseFloat(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Recurring term for product-selected items */}
              {selectedProductId && newItem.billingType === 'recurring' && (
                <div>
                  <label className="block text-xs text-gray-600 mb-1">{t('platform.salesPipeline.lineItems.contractTerm', 'Contract Term (months)')}</label>
                  <input
                    type="number"
                    value={newItem.termMonths || 12}
                    onChange={(e) => setNewItem({ ...newItem, termMonths: parseInt(e.target.value) || 12 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              )}

              {/* Preview */}
              {(newItem.unitPrice || newItem.estimatedTransactionVolume || newItem.estimatedOutcomes || newItem.estimatedUsage) && (
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('platform.salesPipeline.lineItems.estimatedValue', 'Estimated Value:')}</span>
                    <span className="font-medium">{formatCurrency(calculateLineItemTotals(newItem).netAmount || 0)}</span>
                  </div>
                  {newItem.billingType === 'recurring' && (
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>{t('platform.salesPipeline.lineItems.mrrContribution', 'MRR Contribution:')}</span>
                      <span>{formatCurrency(calculateLineItemTotals(newItem).monthlyRecurring || 0)}/{t('platform.salesPipeline.common.mo', 'mo')}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <button
                  onClick={handleAddItem}
                  disabled={!newItem.itemName}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                >
                  {t('platform.salesPipeline.lineItems.addItem', 'Add Item')}
                </button>
                <button
                  onClick={() => {
                    setShowAddItem(false);
                    setSelectedProductId('');
                    setNewItem({
                      pricingModel: 'per_user',
                      billingType: 'recurring',
                      billingInterval: 'monthly',
                      quantity: 1,
                      unitPrice: 0,
                      discountValue: 0,
                      termMonths: 12,
                    });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                >
                  {t('platform.salesPipeline.common.cancel', 'Cancel')}
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowAddItem(true)}
              className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-blue-400 hover:text-blue-600"
            >
              <Plus className="h-4 w-4 inline mr-1" />
              {t('platform.salesPipeline.lineItems.addLineItem', 'Add Line Item')}
            </button>
          )}
        </>
      )}

      {/* Financials Summary */}
      {lineItems.length > 0 && (
        <div className="bg-gray-100 rounded-lg p-4 space-y-2">
          <h4 className="font-medium text-gray-900 text-sm">{t('platform.salesPipeline.lineItems.dealSummary', 'Deal Summary')}</h4>
          <div className="space-y-1 text-sm">
            {financials.totalOneTime > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">{t('platform.salesPipeline.financials.oneTimeRevenue', 'One-Time Revenue')}</span>
                <span className="font-medium">{formatCurrency(financials.totalOneTime)}</span>
              </div>
            )}
            {financials.totalMrr > 0 && (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('platform.salesPipeline.financials.monthlyRecurring', 'Monthly Recurring (MRR)')}</span>
                  <span className="font-medium text-blue-600">{formatCurrency(financials.totalMrr)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('platform.salesPipeline.financials.annualRecurring', 'Annual Recurring (ARR)')}</span>
                  <span className="font-medium text-blue-600">{formatCurrency(financials.totalArr)}</span>
                </div>
              </>
            )}
            {financials.estimatedUsageRevenue > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">{t('platform.salesPipeline.financials.estUsageRevenue', 'Est. Usage Revenue')}</span>
                <span className="font-medium text-purple-600">{formatCurrency(financials.estimatedUsageRevenue)}</span>
              </div>
            )}
            {financials.estimatedTransactionRevenue > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">{t('platform.salesPipeline.financials.estTransactionRevenue', 'Est. Transaction Revenue')}</span>
                <span className="font-medium text-orange-600">{formatCurrency(financials.estimatedTransactionRevenue)}</span>
              </div>
            )}
            {financials.estimatedOutcomeRevenue > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">{t('platform.salesPipeline.financials.estOutcomeRevenue', 'Est. Outcome Revenue')}</span>
                <span className="font-medium text-green-600">{formatCurrency(financials.estimatedOutcomeRevenue)}</span>
              </div>
            )}
            {financials.totalDiscount > 0 && (
              <div className="flex justify-between text-green-600">
                <span>{t('platform.salesPipeline.financials.totalDiscount', 'Total Discount')}</span>
                <span>-{formatCurrency(financials.totalDiscount)}</span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t border-gray-300">
              <span className="font-medium text-gray-900">{t('platform.salesPipeline.financials.totalContractValue', 'Total Contract Value')}</span>
              <span className="font-bold text-gray-900">{formatCurrency(financials.totalContractValue)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// OPPORTUNITY DETAIL MODAL
// ============================================================================

// Subscription created from won opportunity
interface CreatedSubscription {
  id: string;
  opportunityId: string;
  organizationId: number;
  organizationName: string;
  status: 'active' | 'pending' | 'trial';
  startDate: string;
  lineItems: OpportunityLineItem[];
  financials: OpportunityFinancials;
  invoiceId?: string;
  createdAt: string;
}

interface OpportunityDetailPageProps {
  onClose: () => void;
  opportunity: Opportunity | null;
  pipeline: Pipeline;
  pipelines: Pipeline[];
  activities: Activity[];
  products: Product[];
  users: OrganizationUser[];
  onUpdate: (opportunity: Opportunity) => void;
  onStageChange: (opportunityId: string, newStage: string) => void;
  onAddActivity: (activity: Omit<Activity, 'id'>) => void;
  onCreateSubscription?: (subscription: CreatedSubscription) => void;
  onMoveToPipeline?: (opportunityId: string, pipelineId: string) => void;
}

function OpportunityDetailPage({
  onClose,
  opportunity,
  pipeline,
  pipelines,
  activities,
  products,
  onUpdate,
  onStageChange,
  onAddActivity,
  onCreateSubscription,
  onMoveToPipeline,
  users,
}: OpportunityDetailPageProps) {
  const { t } = useAppTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<Opportunity>>({});
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const titleInputRef = useRef<HTMLInputElement>(null);
  const [showWinLoss, setShowWinLoss] = useState<'won' | 'lost' | null>(null);
  const [lossReason, setLossReason] = useState('');
  const [winDetails, setWinDetails] = useState('');
  const [lineItems, setLineItems] = useState<OpportunityLineItem[]>([]);
  const [activeTab, setActiveTab] = useState<'details' | 'pricing' | 'activity' | 'sales_intel'>('details');
  // Subscription creation options for won deals
  const [createSubscription, setCreateSubscription] = useState(true);
  const [generateInvoice, setGenerateInvoice] = useState(true);
  const [subscriptionStartDate, setSubscriptionStartDate] = useState(new Date().toISOString().split('T')[0]);

  // Reset state when opportunity changes
  useEffect(() => {
    if (opportunity) {
      setEditData({
        name: opportunity.name,
        amount: opportunity.amount,
        closeDate: opportunity.closeDate,
        owner: opportunity.owner,
        notes: opportunity.notes,
        contractTermMonths: opportunity.contractTermMonths,
        isDemoRecord: opportunity.isDemoRecord,
      });
      setLineItems(opportunity.lineItems || []);
    }
    setIsEditing(false);
    setIsEditingTitle(false);
    setShowWinLoss(null);
    setLossReason('');
    setWinDetails('');
    setActiveTab('details');
  }, [opportunity]);

  const handleTitleEdit = () => {
    setTitleDraft(opportunity.name);
    setIsEditingTitle(true);
    setTimeout(() => titleInputRef.current?.select(), 0);
  };

  const handleTitleSave = () => {
    const trimmed = titleDraft.trim();
    if (trimmed && trimmed !== opportunity.name) {
      onUpdate({ ...opportunity, name: trimmed, lastActivity: new Date().toISOString().split('T')[0] });
      setEditData(prev => ({ ...prev, name: trimmed }));
    }
    setIsEditingTitle(false);
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleTitleSave();
    if (e.key === 'Escape') setIsEditingTitle(false);
  };

  if (!opportunity) return null;

  const stage = pipeline.stages.find(s => s.id === opportunity.stage);
  const oppActivities = activities.filter(a => a.opportunityId === opportunity.id || a.contact === opportunity.company);

  // Calculate financials from line items
  const calculatedFinancials = calculateOpportunityFinancials(lineItems);

  // Auto-update amount when line items change
  const handleLineItemsUpdate = (newLineItems: OpportunityLineItem[]) => {
    setLineItems(newLineItems);
    const financials = calculateOpportunityFinancials(newLineItems);
    // Update the opportunity amount to total contract value if line items exist
    if (newLineItems.length > 0) {
      setEditData(prev => ({ ...prev, amount: financials.totalContractValue }));
    }
  };

  const handleSave = () => {
    const financials = calculateOpportunityFinancials(lineItems);
    onUpdate({
      ...opportunity,
      ...editData,
      lineItems,
      financials,
      // Update amount to match TCV if line items exist
      amount: lineItems.length > 0 ? financials.totalContractValue : (editData.amount || opportunity.amount),
      lastActivity: new Date().toISOString().split('T')[0],
    });
    setIsEditing(false);
  };

  const handleWin = () => {
    const wonStage = pipeline.stages.find(s => s.isWon);
    if (wonStage) {
      onStageChange(opportunity.id, wonStage.id);

      // Calculate financials for this deal
      const financials = calculateOpportunityFinancials(lineItems);

      // Create subscription if enabled and has line items
      if (createSubscription && lineItems.length > 0 && onCreateSubscription && opportunity.companyId) {
        const subscription: CreatedSubscription = {
          id: `sub_${Date.now()}`,
          opportunityId: opportunity.id,
          organizationId: opportunity.companyId,
          organizationName: opportunity.company,
          status: 'active',
          startDate: subscriptionStartDate,
          lineItems: lineItems,
          financials: financials,
          invoiceId: generateInvoice ? `inv_${Date.now()}` : undefined,
          createdAt: new Date().toISOString(),
        };
        onCreateSubscription(subscription);

        // Add activity for subscription creation
        onAddActivity({
          type: 'stage_change',
          title: `Subscription created for ${opportunity.company}`,
          description: `MRR: ${formatCurrency(financials.totalMrr)} | ARR: ${formatCurrency(financials.totalArr)}${generateInvoice ? ' | Invoice generated' : ''}`,
          contact: opportunity.company,
          opportunityId: opportunity.id,
          dueDate: new Date().toISOString().split('T')[0],
          completed: true,
          completedAt: new Date().toISOString(),
          priority: 'low',
          createdAt: new Date().toISOString(),
        });
      }

      // Add activity for win
      onAddActivity({
        type: 'stage_change',
        title: `Deal Won! ${winDetails ? `- ${winDetails}` : ''}`,
        description: `${winDetails}${lineItems.length > 0 ? ` | TCV: ${formatCurrency(financials.totalContractValue)}` : ''}`,
        contact: opportunity.company,
        opportunityId: opportunity.id,
        dueDate: new Date().toISOString().split('T')[0],
        completed: true,
        completedAt: new Date().toISOString(),
        priority: 'low',
        createdAt: new Date().toISOString(),
      });
    }
    setShowWinLoss(null);
    onClose();
  };

  const handleLose = () => {
    const lostStage = pipeline.stages.find(s => s.isLost);
    if (lostStage) {
      onStageChange(opportunity.id, lostStage.id);
      // Add activity for loss
      onAddActivity({
        type: 'stage_change',
        title: `Deal Lost - ${lossReason || 'No reason provided'}`,
        description: lossReason,
        contact: opportunity.company,
        opportunityId: opportunity.id,
        dueDate: new Date().toISOString().split('T')[0],
        completed: true,
        completedAt: new Date().toISOString(),
        priority: 'low',
        createdAt: new Date().toISOString(),
      });
    }
    setShowWinLoss(null);
    onClose();
  };

  const selectableStages = pipeline.stages.filter(s => !s.isWon && !s.isLost);

  return (
    <div className="space-y-6">
      {/* Back Navigation */}
      <div className="flex items-center gap-4">
        <button
          onClick={onClose}
          className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-blue-600 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Opportunities
        </button>
      </div>

      {/* Page Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              {isEditingTitle ? (
                <input
                  ref={titleInputRef}
                  type="text"
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onBlur={handleTitleSave}
                  onKeyDown={handleTitleKeyDown}
                  className="text-2xl font-bold border-b-2 border-blue-500 outline-none bg-transparent px-0 py-0"
                  autoFocus
                />
              ) : (
                <h1
                  onClick={handleTitleEdit}
                  className="text-2xl font-bold text-gray-900 cursor-pointer hover:text-blue-600 transition-colors"
                  title={t('platform.salesPipeline.opportunities.clickToEditName', 'Click to edit name')}
                >
                  {opportunity.name}
                </h1>
              )}
              <span className={`text-sm font-medium px-3 py-1 rounded-full shrink-0 ${stage?.color || 'bg-gray-100'}`}>
                {stage?.name || opportunity.stage}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              <Building2 className="h-3.5 w-3.5 inline mr-1" />
              {opportunity.company} &bull; {t('platform.salesPipeline.opportunities.created', 'Created')} {opportunity.createdAt?.split('T')[0] || t('platform.salesPipeline.common.unknown', 'Unknown')}
              &bull; <DollarSign className="h-3.5 w-3.5 inline" />{formatCurrency(opportunity.amount)}
            </p>
          </div>
        </div>
      </div>

        {/* Win/Loss Confirmation */}
        {showWinLoss === 'won' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <h4 className="font-medium text-green-800 mb-2">{t('platform.salesPipeline.opportunities.markAsWon', 'Mark as Won')}</h4>
            <textarea
              value={winDetails}
              onChange={(e) => setWinDetails(e.target.value)}
              placeholder={t('platform.salesPipeline.opportunities.winDetailsPlaceholder', 'Add details about this win (optional)...')}
              className="w-full px-3 py-2 border border-green-300 rounded-lg text-sm"
              rows={2}
            />

            {/* Subscription & Billing Options - Only show if there are line items */}
            {lineItems.length > 0 && (
              <div className="mt-4 pt-3 border-t border-green-200">
                <h5 className="text-sm font-medium text-green-800 mb-3">{t('platform.salesPipeline.opportunities.revOpsActions', 'RevOps Actions')}</h5>

                {/* Deal Summary */}
                <div className="bg-white rounded-lg p-3 mb-3 border border-green-200">
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <span className="text-gray-500 block">{t('platform.salesPipeline.common.mrr', 'MRR')}</span>
                      <span className="font-medium text-green-700">{formatCurrency(calculatedFinancials.totalMrr)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">{t('platform.salesPipeline.common.arr', 'ARR')}</span>
                      <span className="font-medium text-green-700">{formatCurrency(calculatedFinancials.totalArr)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">{t('platform.salesPipeline.common.tcv', 'TCV')}</span>
                      <span className="font-medium text-green-700">{formatCurrency(calculatedFinancials.totalContractValue)}</span>
                    </div>
                  </div>
                </div>

                {/* Subscription Creation Toggle */}
                <label className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    checked={createSubscription}
                    onChange={(e) => setCreateSubscription(e.target.checked)}
                    className="h-4 w-4 text-green-600 rounded border-gray-300"
                  />
                  <span className="text-sm text-green-800">{t('platform.salesPipeline.opportunities.createSubscription', 'Create subscription from line items')}</span>
                </label>

                {createSubscription && (
                  <>
                    {/* Invoice Generation Toggle */}
                    <label className="flex items-center gap-2 mb-3 ml-6">
                      <input
                        type="checkbox"
                        checked={generateInvoice}
                        onChange={(e) => setGenerateInvoice(e.target.checked)}
                        className="h-4 w-4 text-green-600 rounded border-gray-300"
                      />
                      <span className="text-sm text-green-800">{t('platform.salesPipeline.opportunities.generateFirstInvoice', 'Generate first invoice')}</span>
                    </label>

                    {/* Subscription Start Date */}
                    <div className="ml-6">
                      <label className="block text-xs text-green-700 mb-1">{t('platform.salesPipeline.opportunities.subscriptionStartDate', 'Subscription Start Date')}</label>
                      <input
                        type="date"
                        value={subscriptionStartDate}
                        onChange={(e) => setSubscriptionStartDate(e.target.value)}
                        className="px-3 py-1.5 border border-green-300 rounded-lg text-sm w-auto"
                      />
                    </div>
                  </>
                )}

                {!opportunity.companyId && createSubscription && (
                  <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">
                    {t('platform.salesPipeline.opportunities.noOrgLinkedNote', 'Note: No organization linked. Subscription will not be created.')}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2 mt-3">
              <button
                onClick={handleWin}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                {lineItems.length > 0 && createSubscription ? t('platform.salesPipeline.opportunities.confirmWinCreateSubscription', 'Confirm Win & Create Subscription') : t('platform.salesPipeline.opportunities.confirmWin', 'Confirm Win')}
              </button>
              <button
                onClick={() => setShowWinLoss(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {t('platform.salesPipeline.common.cancel', 'Cancel')}
              </button>
            </div>
          </div>
        )}

        {showWinLoss === 'lost' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <h4 className="font-medium text-red-800 mb-2">{t('platform.salesPipeline.opportunities.markAsLost', 'Mark as Lost')}</h4>
            <select
              value={lossReason}
              onChange={(e) => setLossReason(e.target.value)}
              className="w-full px-3 py-2 border border-red-300 rounded-lg text-sm mb-2"
            >
              <option value="">{t('platform.salesPipeline.opportunities.selectReason', 'Select reason...')}</option>
              <option value="Lost to competitor">{t('platform.salesPipeline.lossReasons.lostToCompetitor', 'Lost to competitor')}</option>
              <option value="No budget">{t('platform.salesPipeline.lossReasons.noBudget', 'No budget')}</option>
              <option value="No decision">{t('platform.salesPipeline.lossReasons.noDecision', 'No decision / Stalled')}</option>
              <option value="Timing">{t('platform.salesPipeline.lossReasons.badTiming', 'Bad timing')}</option>
              <option value="Lost contact">{t('platform.salesPipeline.lossReasons.lostContact', 'Lost contact')}</option>
              <option value="Other">{t('platform.salesPipeline.lossReasons.other', 'Other')}</option>
            </select>
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleLose}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                {t('platform.salesPipeline.opportunities.confirmLoss', 'Confirm Loss')}
              </button>
              <button
                onClick={() => setShowWinLoss(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {t('platform.salesPipeline.common.cancel', 'Cancel')}
              </button>
            </div>
          </div>
        )}

      {/* Tab Navigation & Content */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="border-b border-gray-200 px-6 pt-2">
          <nav className="flex gap-4" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-2 px-1 border-b-2 text-sm font-medium ${
                activeTab === 'details'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('platform.salesPipeline.opportunities.tabs.details', 'Details')}
            </button>
            <button
              onClick={() => setActiveTab('pricing')}
              className={`py-2 px-1 border-b-2 text-sm font-medium flex items-center gap-1 ${
                activeTab === 'pricing'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('platform.salesPipeline.opportunities.tabs.dealEstimate', 'Deal Estimate')}
              {lineItems.length > 0 && (
                <span className="bg-blue-100 text-blue-600 text-xs px-1.5 py-0.5 rounded-full">
                  {lineItems.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('activity')}
              className={`py-2 px-1 border-b-2 text-sm font-medium ${
                activeTab === 'activity'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('platform.salesPipeline.opportunities.tabs.activity', 'Activity')}
            </button>
            <button
              onClick={() => setActiveTab('sales_intel')}
              className={`py-2 px-1 border-b-2 text-sm font-medium flex items-center gap-1 ${
                activeTab === 'sales_intel'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Sales Intel
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Details Tab */}
          {activeTab === 'details' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900">{t('platform.salesPipeline.opportunities.dealDetails', 'Deal Details')}</h4>
                    {!isEditing && (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="text-sm text-blue-600 hover:text-blue-700"
                      >
                        {t('platform.salesPipeline.common.edit', 'Edit')}
                      </button>
                    )}
                  </div>

                  {isEditing ? (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">{t('platform.salesPipeline.common.name', 'Name')}</label>
                        <input
                          type="text"
                          value={editData.name || ''}
                          onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">{t('platform.salesPipeline.common.amount', 'Amount')} {lineItems.length > 0 && t('platform.salesPipeline.opportunities.fromLineItems', '(from line items)')}</label>
                        <input
                          type="number"
                          value={editData.amount || 0}
                          onChange={(e) => setEditData({ ...editData, amount: parseFloat(e.target.value) || 0 })}
                          disabled={lineItems.length > 0}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100"
                        />
                        {lineItems.length > 0 && (
                          <p className="text-xs text-gray-500 mt-1">{t('platform.salesPipeline.opportunities.amountCalculatedNote', 'Amount is calculated from line items')}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">{t('platform.salesPipeline.common.closeDate', 'Close Date')}</label>
                        <input
                          type="date"
                          value={editData.closeDate || ''}
                          onChange={(e) => setEditData({ ...editData, closeDate: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">{t('platform.salesPipeline.opportunities.contractTermMonths', 'Contract Term (months)')}</label>
                        <input
                          type="number"
                          value={editData.contractTermMonths || 12}
                          onChange={(e) => setEditData({ ...editData, contractTermMonths: parseInt(e.target.value) || 12 })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">{t('platform.salesPipeline.common.owner', 'Owner')}</label>
                        <select
                          value={editData.owner || ''}
                          onChange={(e) => setEditData({ ...editData, owner: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          {users.length > 0 ? (
                            users.map(user => (
                              <option key={user.id} value={user.name}>{user.name}</option>
                            ))
                          ) : (
                            <option value={editData.owner || 'Unassigned'}>{editData.owner || 'Unassigned'}</option>
                          )}
                        </select>
                      </div>
                      <div className="flex items-center gap-2 py-2 px-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <input
                          type="checkbox"
                          id="editDemoFlag"
                          checked={editData.isDemoRecord || false}
                          onChange={(e) => setEditData({ ...editData, isDemoRecord: e.target.checked })}
                          className="h-4 w-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                        />
                        <label htmlFor="editDemoFlag" className="text-xs text-amber-800 cursor-pointer">
                          {t('platform.salesPipeline.demo.flagLabel', 'Used for platform demo')}
                        </label>
                      </div>
                      {pipelines.length > 1 && (
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">{t('platform.salesPipeline.common.pipeline', 'Pipeline')}</label>
                          <select
                            value={opportunity.pipelineId || pipeline.id}
                            onChange={(e) => onMoveToPipeline?.(opportunity.id, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          >
                            {pipelines.map(p => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">{t('platform.salesPipeline.common.notes', 'Notes')}</label>
                        <textarea
                          value={editData.notes || ''}
                          onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={handleSave}
                          className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                        >
                          {t('platform.salesPipeline.common.save', 'Save')}
                        </button>
                        <button
                          onClick={() => setIsEditing(false)}
                          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                        >
                          {t('platform.salesPipeline.common.cancel', 'Cancel')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">{t('platform.salesPipeline.common.amount', 'Amount')}</span>
                        <span className="text-sm font-medium">{formatCurrency(lineItems.length > 0 ? calculatedFinancials.totalContractValue : opportunity.amount)}</span>
                      </div>
                      {lineItems.length > 0 && calculatedFinancials.totalMrr > 0 && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">{t('platform.salesPipeline.common.mrr', 'MRR')}</span>
                          <span className="text-sm font-medium text-blue-600">{formatCurrency(calculatedFinancials.totalMrr)}</span>
                        </div>
                      )}
                      {lineItems.length > 0 && calculatedFinancials.totalArr > 0 && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">{t('platform.salesPipeline.common.arr', 'ARR')}</span>
                          <span className="text-sm font-medium text-blue-600">{formatCurrency(calculatedFinancials.totalArr)}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">{t('platform.salesPipeline.common.probability', 'Probability')}</span>
                        <span className="text-sm font-medium">{opportunity.probability}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">{t('platform.salesPipeline.common.closeDate', 'Close Date')}</span>
                        <span className="text-sm font-medium">{opportunity.closeDate || t('platform.salesPipeline.common.notSet', 'Not set')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">{t('platform.salesPipeline.opportunities.contractTerm', 'Contract Term')}</span>
                        <span className="text-sm font-medium">{opportunity.contractTermMonths || 12} {t('platform.salesPipeline.common.months', 'months')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">{t('platform.salesPipeline.common.owner', 'Owner')}</span>
                        <span className="text-sm font-medium">{opportunity.owner}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">{t('platform.salesPipeline.common.source', 'Source')}</span>
                        <span className="text-sm font-medium">{opportunity.source || t('platform.salesPipeline.sources.manual', 'Manual')}</span>
                      </div>
                      {pipelines.length > 1 && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-500">{t('platform.salesPipeline.common.pipeline', 'Pipeline')}</span>
                          <select
                            value={opportunity.pipelineId || pipeline.id}
                            onChange={(e) => onMoveToPipeline?.(opportunity.id, e.target.value)}
                            className="text-sm font-medium px-2 py-1 border border-gray-200 rounded-md bg-white hover:border-blue-400 cursor-pointer"
                          >
                            {pipelines.map(p => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      {opportunity.isDemoRecord && (
                        <div className="flex items-center justify-between pt-2 border-t border-amber-200">
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 text-xs font-medium rounded-full">
                            {t('platform.salesPipeline.demo.badge', 'Platform Demo')}
                          </span>
                          <button
                            onClick={() => onUpdate({ ...opportunity, isDemoRecord: false, lastActivity: new Date().toISOString().split('T')[0] })}
                            className="text-xs text-amber-700 hover:text-amber-900 underline"
                          >
                            {t('platform.salesPipeline.demo.removeFlag', 'Remove flag')}
                          </button>
                        </div>
                      )}
                      {opportunity.notes && (
                        <div className="pt-2 border-t border-gray-200">
                          <span className="text-sm text-gray-500">{t('platform.salesPipeline.common.notes', 'Notes')}</span>
                          <p className="text-sm mt-1">{opportunity.notes}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Stage Change */}
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">{t('platform.salesPipeline.opportunities.moveToStage', 'Move to Stage')}</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {selectableStages.map(s => (
                      <button
                        key={s.id}
                        onClick={() => onStageChange(opportunity.id, s.id)}
                        disabled={s.id === opportunity.stage}
                        className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                          s.id === opportunity.stage
                            ? 'bg-blue-100 border-blue-300 text-blue-800'
                            : `${s.color} hover:opacity-80 border-transparent`
                        }`}
                      >
                        {s.name}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-3 pt-3 border-t border-gray-200">
                    <button
                      onClick={() => setShowWinLoss('won')}
                      className="flex-1 px-3 py-2 bg-green-100 text-green-700 rounded-lg text-sm font-medium hover:bg-green-200"
                    >
                      {t('platform.salesPipeline.opportunities.markWon', 'Mark Won')}
                    </button>
                    <button
                      onClick={() => setShowWinLoss('lost')}
                      className="flex-1 px-3 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200"
                    >
                      {t('platform.salesPipeline.opportunities.markLost', 'Mark Lost')}
                    </button>
                  </div>
                </div>

                {/* Quick Financials Summary */}
                {lineItems.length > 0 && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-3">{t('platform.salesPipeline.financials.revenueSummary', 'Revenue Summary')}</h4>
                    <div className="space-y-2 text-sm">
                      {calculatedFinancials.totalOneTime > 0 && (
                        <div className="flex justify-between">
                          <span className="text-blue-700">{t('platform.salesPipeline.financials.oneTime', 'One-Time')}</span>
                          <span className="font-medium">{formatCurrency(calculatedFinancials.totalOneTime)}</span>
                        </div>
                      )}
                      {calculatedFinancials.totalMrr > 0 && (
                        <div className="flex justify-between">
                          <span className="text-blue-700">{t('platform.salesPipeline.financials.monthlyRecurring', 'Monthly Recurring')}</span>
                          <span className="font-medium">{formatCurrency(calculatedFinancials.totalMrr)}</span>
                        </div>
                      )}
                      {calculatedFinancials.estimatedTransactionRevenue > 0 && (
                        <div className="flex justify-between">
                          <span className="text-blue-700">{t('platform.salesPipeline.financials.estTransaction', 'Est. Transaction')}</span>
                          <span className="font-medium">{formatCurrency(calculatedFinancials.estimatedTransactionRevenue)}</span>
                        </div>
                      )}
                      {calculatedFinancials.estimatedOutcomeRevenue > 0 && (
                        <div className="flex justify-between">
                          <span className="text-blue-700">{t('platform.salesPipeline.financials.estOutcome', 'Est. Outcome')}</span>
                          <span className="font-medium">{formatCurrency(calculatedFinancials.estimatedOutcomeRevenue)}</span>
                        </div>
                      )}
                      <div className="flex justify-between pt-2 border-t border-blue-200">
                        <span className="font-medium text-blue-900">{t('platform.salesPipeline.financials.totalContract', 'Total Contract')}</span>
                        <span className="font-bold text-blue-900">{formatCurrency(calculatedFinancials.totalContractValue)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Pricing Tab */}
          {activeTab === 'pricing' && (
            <div>
              <div className="mb-4">
                <h4 className="font-medium text-gray-900">{t('platform.salesPipeline.pricing.dealEstimate', 'Deal Estimate')}</h4>
                <p className="text-sm text-gray-500">
                  {t('platform.salesPipeline.pricing.description', 'Sketch line items here for the deal estimate. The actual subscription is created at close-won from the assigned plan tier in RevOps — these line items inform the estimate but do not bill.')}
                </p>
              </div>
              <LineItemsEditor
                lineItems={lineItems}
                onUpdate={handleLineItemsUpdate}
                products={products}
              />
              {lineItems.length > 0 && (
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 flex items-center gap-2"
                  >
                    <Save className="h-4 w-4" />
                    {t('platform.salesPipeline.common.saveChanges', 'Save Changes')}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Activity Tab */}
          {activeTab === 'activity' && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900">{t('platform.salesPipeline.activities.activityHistory', 'Activity History')}</h4>
                <span className="text-xs text-gray-500">{oppActivities.length} activities</span>
              </div>
              <ActivityTimeline
                activities={oppActivities}
                onAddActivity={(type) => {
                  // Quick-add: pre-fill an activity scoped to this opportunity
                  const titleByType: Record<Activity['type'], string> = {
                    note: 'Note',
                    call: 'Call',
                    email: 'Email',
                    meeting: 'Meeting',
                    task: 'Task',
                    stage_change: 'Stage change',
                  };
                  onAddActivity({
                    type,
                    title: titleByType[type] || 'Activity',
                    contact: opportunity.company,
                    opportunityId: opportunity.id,
                    completed: type === 'note',
                    priority: 'medium',
                    activityDate: new Date().toISOString(),
                    createdAt: new Date().toISOString(),
                  });
                }}
                onComplete={() => {}}
                showFilters={false}
                maxItems={50}
              />
              {oppActivities.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-6">
                  No activities yet. Use the buttons above to log a note, call, email, or meeting for this opportunity.
                </p>
              )}
            </div>
          )}
          {activeTab === 'sales_intel' && (
            <OpportunityDetailPanel
              opportunityId={opportunity.id}
              onClose={() => setActiveTab('details')}
              onUpdated={() => {
                // Refresh data after updates
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// PIPELINE SETTINGS MODAL
// ============================================================================

interface PipelineSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  pipeline: Pipeline;
  onSave: (pipeline: Pipeline) => void;
}

const STAGE_COLORS = [
  'bg-gray-100', 'bg-blue-100', 'bg-yellow-100', 'bg-purple-100',
  'bg-orange-100', 'bg-pink-100', 'bg-cyan-100', 'bg-indigo-100',
  'bg-green-100', 'bg-red-100',
];

function PipelineSettingsModal({ isOpen, onClose, pipeline, onSave }: PipelineSettingsModalProps) {
  const { t } = useAppTranslation();
  const [stages, setStages] = useState<PipelineStageConfig[]>(pipeline.stages);
  const [pipelineName, setPipelineName] = useState(pipeline.name);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStages(pipeline.stages);
      setPipelineName(pipeline.name);
    }
  }, [isOpen, pipeline]);

  const handleAddStage = () => {
    const newStage: PipelineStageConfig = {
      id: `stage_${Date.now()}`,
      name: 'New Stage',
      position: stages.filter(s => !s.isWon && !s.isLost).length,
      probability: 50,
      color: STAGE_COLORS[stages.length % STAGE_COLORS.length],
    };
    // Insert before closed stages
    const closedStages = stages.filter(s => s.isWon || s.isLost);
    const openStages = stages.filter(s => !s.isWon && !s.isLost);
    setStages([...openStages, newStage, ...closedStages]);
  };

  const handleUpdateStage = (stageId: string, updates: Partial<PipelineStageConfig>) => {
    setStages(stages.map(s => s.id === stageId ? { ...s, ...updates } : s));
  };

  const handleDeleteStage = (stageId: string) => {
    const stage = stages.find(s => s.id === stageId);
    if (stage?.isWon || stage?.isLost) {
      alert(t('platform.salesPipeline.settings.cannotDeleteWonLost', 'Cannot delete Won/Lost stages'));
      return;
    }
    setStages(stages.filter(s => s.id !== stageId));
  };

  const handleMoveStage = (stageId: string, direction: 'up' | 'down') => {
    const openStages = stages.filter(s => !s.isWon && !s.isLost);
    const closedStages = stages.filter(s => s.isWon || s.isLost);
    const idx = openStages.findIndex(s => s.id === stageId);

    if (direction === 'up' && idx > 0) {
      [openStages[idx - 1], openStages[idx]] = [openStages[idx], openStages[idx - 1]];
    } else if (direction === 'down' && idx < openStages.length - 1) {
      [openStages[idx], openStages[idx + 1]] = [openStages[idx + 1], openStages[idx]];
    }

    // Update positions
    openStages.forEach((s, i) => s.position = i);
    setStages([...openStages, ...closedStages]);
  };

  const handleSave = () => {
    onSave({
      ...pipeline,
      name: pipelineName,
      stages: stages.map((s, idx) => ({ ...s, position: idx })),
      updatedAt: new Date().toISOString(),
    });
    onClose();
  };

  const openStages = stages.filter(s => !s.isWon && !s.isLost);
  const closedStages = stages.filter(s => s.isWon || s.isLost);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('platform.salesPipeline.settings.title', 'Pipeline Settings')}</DialogTitle>
          <DialogDescription>
            {t('platform.salesPipeline.settings.description', 'Customize your pipeline stages, probabilities, and settings.')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 py-4">
          {/* Pipeline Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('platform.salesPipeline.settings.pipelineName', 'Pipeline Name')}</label>
            <input
              type="text"
              value={pipelineName}
              onChange={(e) => setPipelineName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Stages */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">{t('platform.salesPipeline.settings.pipelineStages', 'Pipeline Stages')}</label>
              <button
                type="button"
                onClick={handleAddStage}
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
              >
                <Plus className="h-4 w-4" /> {t('platform.salesPipeline.settings.addStage', 'Add Stage')}
              </button>
            </div>
            <div className="space-y-2">
              {openStages.map((stage, idx) => (
                <div
                  key={stage.id}
                  className={`flex items-center gap-3 p-3 ${stage.color} rounded-lg`}
                >
                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      onClick={() => handleMoveStage(stage.id, 'up')}
                      disabled={idx === 0}
                      className="p-0.5 hover:bg-white/50 rounded disabled:opacity-30"
                    >
                      <ChevronDown className="h-3 w-3 rotate-180" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMoveStage(stage.id, 'down')}
                      disabled={idx === openStages.length - 1}
                      className="p-0.5 hover:bg-white/50 rounded disabled:opacity-30"
                    >
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  </div>
                  <input
                    type="text"
                    value={stage.name}
                    onChange={(e) => handleUpdateStage(stage.id, { name: e.target.value })}
                    className="flex-1 px-2 py-1 bg-white/70 border border-gray-200 rounded text-sm"
                  />
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      value={stage.probability}
                      onChange={(e) => handleUpdateStage(stage.id, { probability: parseInt(e.target.value) || 0 })}
                      min="0"
                      max="100"
                      className="w-16 px-2 py-1 bg-white/70 border border-gray-200 rounded text-sm text-center"
                    />
                    <span className="text-sm text-gray-500">%</span>
                  </div>
                  <select
                    value={stage.color}
                    onChange={(e) => handleUpdateStage(stage.id, { color: e.target.value })}
                    className={`w-10 h-8 ${stage.color} border border-gray-200 rounded cursor-pointer`}
                  >
                    {STAGE_COLORS.map(color => (
                      <option key={color} value={color}>{color.replace('bg-', '').replace('-100', '')}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => handleDeleteStage(stage.id)}
                    className="p-1 text-gray-400 hover:text-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}

              {/* Closed Stages (non-editable order) */}
              <div className="border-t border-gray-200 pt-3 mt-3">
                <p className="text-xs text-gray-500 mb-2">{t('platform.salesPipeline.settings.closingStagesFixed', 'Closing Stages (fixed)')}</p>
                {closedStages.map(stage => (
                  <div
                    key={stage.id}
                    className={`flex items-center gap-3 p-3 ${stage.color} rounded-lg mb-2`}
                  >
                    <GripVertical className="h-4 w-4 text-gray-300" />
                    <input
                      type="text"
                      value={stage.name}
                      onChange={(e) => handleUpdateStage(stage.id, { name: e.target.value })}
                      className="flex-1 px-2 py-1 bg-white/70 border border-gray-200 rounded text-sm"
                    />
                    <span className="text-sm text-gray-500">
                      {stage.isWon ? '100%' : '0%'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t('platform.salesPipeline.common.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Save className="h-4 w-4" />
            {t('platform.salesPipeline.common.saveChanges', 'Save Changes')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// DRAG AND DROP COMPONENTS
// ============================================================================

interface DraggableOpportunityCardProps {
  opportunity: Opportunity;
  pipeline: Pipeline;
  onClick?: (opportunity: Opportunity) => void;
  onEdit?: (opportunity: Opportunity) => void;
  onDelete?: (opportunity: Opportunity) => void;
  onMoveToStage?: (opportunity: Opportunity, stage: string) => void;
}

function DraggableOpportunityCard({ opportunity, pipeline, onClick, onEdit, onDelete, onMoveToStage }: DraggableOpportunityCardProps) {
  const { t } = useAppTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: opportunity.id, data: { stage: opportunity.stage } });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const stageConfig = getStageConfig(pipeline, opportunity.stage);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const handleClick = (e: React.MouseEvent) => {
    // Prevent click when dragging or when menu is open
    if (!isDragging && onClick && !menuOpen) {
      onClick(opportunity);
    }
  };

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(!menuOpen);
  };

  const handleMenuAction = (action: string, stageId?: string) => {
    setMenuOpen(false);
    switch (action) {
      case 'view':
        onClick?.(opportunity);
        break;
      case 'edit':
        onEdit?.(opportunity);
        break;
      case 'delete':
        onDelete?.(opportunity);
        break;
      case 'move':
        if (stageId) onMoveToStage?.(opportunity, stageId);
        break;
    }
  };

  // Get available stages for move menu (exclude current stage and won/lost)
  const moveableStages = pipeline.stages.filter(
    s => s.id !== opportunity.stage && !s.isWon && !s.isLost
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={handleClick}
      className="bg-white rounded-lg border border-gray-200 p-3 cursor-grab hover:shadow-md transition-shadow active:cursor-grabbing relative"
    >
      <div className="flex items-start justify-between mb-2">
        <h5 className="font-medium text-gray-900 text-sm">{opportunity.name}</h5>
        <div className="relative" ref={menuRef}>
          <button
            className="p-1 hover:bg-gray-100 rounded"
            onClick={handleMenuClick}
          >
            <MoreVertical className="h-4 w-4 text-gray-400" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-1">
              <button
                onClick={() => handleMenuAction('view')}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
              >
                <Eye className="h-4 w-4" />
                {t('platform.salesPipeline.opportunities.viewDetails', 'View Details')}
              </button>
              <button
                onClick={() => handleMenuAction('edit')}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
              >
                <Edit className="h-4 w-4" />
                {t('platform.salesPipeline.opportunities.editOpportunity', 'Edit Opportunity')}
              </button>
              {moveableStages.length > 0 && (
                <div className="border-t border-gray-100 my-1">
                  <div className="px-4 py-1 text-xs text-gray-500 font-medium">{t('platform.salesPipeline.opportunities.moveToStage', 'Move to Stage')}</div>
                  {moveableStages.map(stage => (
                    <button
                      key={stage.id}
                      onClick={() => handleMenuAction('move', stage.id)}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <ArrowRight className="h-4 w-4" />
                      {stage.name}
                    </button>
                  ))}
                </div>
              )}
              <div className="border-t border-gray-100 my-1" />
              <button
                onClick={() => handleMenuAction('delete')}
                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                {t('platform.salesPipeline.common.delete', 'Delete')}
              </button>
            </div>
          )}
        </div>
      </div>
      <p className="text-sm text-gray-500 mb-2">{opportunity.company}</p>
      <p className="text-lg font-bold text-gray-900 mb-2">
        {formatCurrency(opportunity.amount)}
      </p>
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>{opportunity.probability}% {t('platform.salesPipeline.common.probability', 'probability')}</span>
        {opportunity.closeDate && <span>{t('platform.salesPipeline.common.closeLabel', 'Close')}: {opportunity.closeDate}</span>}
      </div>
    </div>
  );
}

interface DroppableStageColumnProps {
  stage: PipelineStageConfig;
  opportunities: Opportunity[];
  pipeline: Pipeline;
  onAddOpportunity: (stage: string) => void;
  onOpportunityClick: (opportunity: Opportunity) => void;
  onEditOpportunity?: (opportunity: Opportunity) => void;
  onDeleteOpportunity?: (opportunity: Opportunity) => void;
  onMoveToStage?: (opportunity: Opportunity, stage: string) => void;
}

function DroppableStageColumn({ stage, opportunities, pipeline, onAddOpportunity, onOpportunityClick, onEditOpportunity, onDeleteOpportunity, onMoveToStage }: DroppableStageColumnProps) {
  const { t } = useAppTranslation();
  const { setNodeRef, isOver } = useDroppable({
    id: stage.id,
    data: { stage: stage.id },
  });

  const total = opportunities.reduce((sum, opp) => sum + opp.amount, 0);

  return (
    <div className="flex-shrink-0 w-64 sm:w-72">
      <div className={`rounded-t-lg p-3 ${stage.color}`}>
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-gray-900">{stage.name}</h4>
          <span className="text-sm text-gray-600">{opportunities.length}</span>
        </div>
        <div className="flex items-center justify-between mt-1">
          <p className="text-sm font-medium text-gray-700">{formatCurrency(total)}</p>
          <span className="text-xs text-gray-500">{stage.probability}%</span>
        </div>
      </div>
      <div
        ref={setNodeRef}
        className={`bg-gray-50 rounded-b-lg p-2 min-h-[400px] space-y-2 transition-colors ${
          isOver ? 'bg-blue-50 ring-2 ring-blue-300' : ''
        }`}
      >
        <SortableContext items={opportunities.map(o => o.id)} strategy={verticalListSortingStrategy}>
          {opportunities.map((opp) => (
            <DraggableOpportunityCard
              key={opp.id}
              opportunity={opp}
              pipeline={pipeline}
              onClick={onOpportunityClick}
              onEdit={onEditOpportunity}
              onDelete={onDeleteOpportunity}
              onMoveToStage={onMoveToStage}
            />
          ))}
        </SortableContext>
        <button
          onClick={() => onAddOpportunity(stage.id)}
          className="w-full p-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded border border-dashed border-gray-300"
        >
          + {t('platform.salesPipeline.opportunities.addOpportunity', 'Add Opportunity')}
        </button>
      </div>
    </div>
  );
}

// Droppable column for Won/Lost stages
function DroppableClosedColumn({ stage, opportunities, pipeline, colorScheme, onOpportunityClick, onEditOpportunity, onDeleteOpportunity, onMoveToStage }: {
  stage: PipelineStageConfig;
  opportunities: Opportunity[];
  pipeline: Pipeline;
  colorScheme: 'green' | 'red';
  onOpportunityClick?: (opportunity: Opportunity) => void;
  onEditOpportunity?: (opportunity: Opportunity) => void;
  onDeleteOpportunity?: (opportunity: Opportunity) => void;
  onMoveToStage?: (opportunity: Opportunity, stage: string) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: stage.id,
    data: { stage: stage.id },
  });

  const colors = colorScheme === 'green'
    ? { header: 'text-green-800', value: 'text-green-700', bg: 'bg-green-50', ring: 'ring-green-300' }
    : { header: 'text-red-800', value: 'text-red-700', bg: 'bg-red-50', ring: 'ring-red-300' };

  const total = opportunities.reduce((sum, o) => sum + o.amount, 0);

  return (
    <div className="flex-shrink-0 w-64 sm:w-72">
      <div className={`${stage.color} rounded-t-lg p-3`}>
        <div className="flex items-center justify-between">
          <h4 className={`font-medium ${colors.header}`}>{stage.name}</h4>
          <span className={`text-sm ${colors.header}`}>{opportunities.length}</span>
        </div>
        <p className={`text-sm font-medium ${colors.value} mt-1`}>
          {formatCurrency(total)}
        </p>
      </div>
      <div
        ref={setNodeRef}
        className={`${colors.bg} rounded-b-lg p-2 min-h-[400px] space-y-2 transition-colors ${
          isOver ? `ring-2 ${colors.ring} bg-opacity-80` : ''
        }`}
      >
        <SortableContext items={opportunities.map(o => o.id)} strategy={verticalListSortingStrategy}>
          {opportunities.map((opp) => (
            <DraggableOpportunityCard
              key={opp.id}
              opportunity={opp}
              pipeline={pipeline}
              onClick={onOpportunityClick}
              onEdit={onEditOpportunity}
              onDelete={onDeleteOpportunity}
              onMoveToStage={onMoveToStage}
            />
          ))}
        </SortableContext>
      </div>
    </div>
  );
}

// ============================================================================
// COMPONENTS
// ============================================================================

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

// ----- Export helpers (CSV download + iframe-based print) -----

function safeFilename(name: string): string {
  const base = name.replace(/[^a-z0-9-_]+/gi, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'opportunities';
  const today = new Date().toISOString().slice(0, 10);
  return `${base}-${today}`;
}

function csvCell(value: unknown): string {
  const s = value == null ? '' : String(value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadOpportunitiesCsv(opportunities: Opportunity[], pipeline: Pipeline, baseName: string) {
  const stageName = new Map(pipeline.stages.map(s => [s.id, s.name]));
  const header = ['Name', 'Company', 'Stage', 'Amount', 'Probability', 'Owner', 'Close Date', 'Source', 'Last Activity'];
  const rows = opportunities.map(o => [
    o.name,
    o.company,
    stageName.get(o.stage) || o.stage,
    o.amount,
    o.probability,
    o.owner,
    o.closeDate || '',
    o.source || '',
    o.lastActivity || '',
  ]);
  const csv = [header, ...rows].map(r => r.map(csvCell).join(',')).join('\r\n');
  // Excel-friendly UTF-8 BOM
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${safeFilename(baseName)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function escapeHtml(value: unknown): string {
  return String(value ?? '').replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[c] as string);
}

const PRINT_BASE_STYLES = `
  @page { margin: 12mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #111; margin: 0; padding: 16px; }
  h1 { font-size: 18px; margin: 0; }
  .sub { color: #666; font-size: 12px; margin: 4px 0 16px; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  th, td { padding: 6px 8px; border-bottom: 1px solid #e5e7eb; text-align: left; vertical-align: top; }
  th { background: #f9fafb; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; font-size: 10px; color: #6b7280; }
  tr { page-break-inside: avoid; }
  .kanban { display: grid; gap: 8px; }
  .col { border: 1px solid #d1d5db; border-radius: 4px; overflow: hidden; }
  .col-h { background: #f3f4f6; padding: 6px 8px; font-weight: 600; font-size: 11px; display: flex; justify-content: space-between; align-items: baseline; }
  .col-h .count { color: #6b7280; font-weight: 500; }
  .col-h .total { color: #374151; font-size: 10px; }
  .col-b { padding: 6px; min-height: 40px; }
  .card { border: 1px solid #e5e7eb; border-radius: 4px; padding: 6px 8px; margin-bottom: 6px; page-break-inside: avoid; background: #fff; }
  .card .n { font-weight: 600; font-size: 11px; }
  .card .c { font-size: 10px; color: #6b7280; }
  .card .row { display: flex; justify-content: space-between; margin-top: 4px; font-size: 10px; color: #374151; }
`;

function printHtml(title: string, bodyHtml: string, orientation: 'portrait' | 'landscape') {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
  document.body.appendChild(iframe);
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>@page { size: A4 ${orientation}; }${PRINT_BASE_STYLES}</style></head><body>${bodyHtml}</body></html>`;
  const cleanup = () => { try { iframe.remove(); } catch {} };
  iframe.onload = () => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } finally {
      setTimeout(cleanup, 2000);
    }
  };
  iframe.srcdoc = html;
}

function printOpportunitiesKanban(opportunities: Opportunity[], pipeline: Pipeline) {
  const stages = pipeline.stages.slice().sort((a, b) => a.position - b.position);
  const cols = stages.map(stage => {
    const stageOpps = opportunities.filter(o => o.stage === stage.id);
    const total = stageOpps.reduce((s, o) => s + (o.amount || 0), 0);
    const cards = stageOpps.map(o => `
      <div class="card">
        <div class="n">${escapeHtml(o.name)}</div>
        <div class="c">${escapeHtml(o.company)}</div>
        <div class="row"><span>${escapeHtml(formatCurrency(o.amount || 0))}</span><span>${escapeHtml(o.owner || '')}</span></div>
        ${o.closeDate ? `<div class="row"><span>Close ${escapeHtml(o.closeDate)}</span><span>${escapeHtml(String(o.probability))}%</span></div>` : ''}
      </div>`).join('');
    return `
      <div class="col">
        <div class="col-h">
          <span>${escapeHtml(stage.name)} <span class="count">(${stageOpps.length})</span></span>
          <span class="total">${escapeHtml(formatCurrency(total))}</span>
        </div>
        <div class="col-b">${cards || '<div class="c">No deals</div>'}</div>
      </div>`;
  }).join('');
  const generated = new Date().toLocaleString();
  const body = `
    <h1>${escapeHtml(pipeline.name)} — Pipeline</h1>
    <div class="sub">${opportunities.length} opportunit${opportunities.length === 1 ? 'y' : 'ies'} · Printed ${escapeHtml(generated)}</div>
    <div class="kanban" style="grid-template-columns: repeat(${stages.length}, minmax(0, 1fr));">${cols}</div>`;
  printHtml(`${pipeline.name} — Pipeline`, body, 'landscape');
}

function printOpportunitiesList(opportunities: Opportunity[], pipeline: Pipeline, title: string) {
  const stageName = new Map(pipeline.stages.map(s => [s.id, s.name]));
  const rows = opportunities.map(o => `
    <tr>
      <td><div style="font-weight:600;">${escapeHtml(o.name)}</div><div style="font-size:10px;color:#6b7280;">${escapeHtml(o.company)}</div></td>
      <td>${escapeHtml(formatCurrency(o.amount || 0))}</td>
      <td>${escapeHtml(stageName.get(o.stage) || o.stage)}</td>
      <td>${escapeHtml(o.closeDate || '')}</td>
      <td>${escapeHtml(o.owner || '')}</td>
      <td>${escapeHtml(String(o.probability))}%</td>
    </tr>`).join('');
  const generated = new Date().toLocaleString();
  const total = opportunities.reduce((s, o) => s + (o.amount || 0), 0);
  const body = `
    <h1>${escapeHtml(title)}</h1>
    <div class="sub">${opportunities.length} opportunit${opportunities.length === 1 ? 'y' : 'ies'} · ${escapeHtml(formatCurrency(total))} total · Printed ${escapeHtml(generated)}</div>
    <table>
      <thead><tr><th>Opportunity</th><th>Amount</th><th>Stage</th><th>Close Date</th><th>Owner</th><th>Probability</th></tr></thead>
      <tbody>${rows || '<tr><td colspan="6" style="text-align:center;color:#9ca3af;padding:24px;">No opportunities</td></tr>'}</tbody>
    </table>`;
  printHtml(title, body, 'portrait');
}

// ----- Forecast / Performance / Analytics exports -----

interface ForecastReportData {
  pipelineName: string;
  period: string;
  totalPipeline: number;
  weightedPipeline: number;
  pipelineCoverage: number;
  wonRevenue: number;
  winRate: number;
  wonDealsCount: number;
  monthlyProjections: Array<{ month: string; committed: number; bestCase: number; worstCase: number }>;
  forecastByStage: Array<{ name: string; probability: number; deals: number; total: number; weighted: number; percentage: number }>;
  wonDeals: Array<{ name: string; amount: number }>;
  lostDeals: Array<{ name: string; amount: number }>;
  wonStageName?: string;
  lostStageName?: string;
}

function downloadForecastCsv(data: ForecastReportData) {
  const lines: string[][] = [];
  lines.push(['Sales Forecast', data.pipelineName, data.period]);
  lines.push([]);
  lines.push(['Summary']);
  lines.push(['Total Pipeline', String(data.totalPipeline)]);
  lines.push(['Weighted Pipeline', String(data.weightedPipeline)]);
  lines.push(['Pipeline Coverage %', String(data.pipelineCoverage)]);
  lines.push(['Won Revenue', String(data.wonRevenue)]);
  lines.push(['Win Rate %', String(data.winRate)]);
  lines.push([]);
  lines.push(['Monthly Projections']);
  lines.push(['Month', 'Committed', 'Best Case', 'Worst Case', 'Confidence %']);
  data.monthlyProjections.forEach(p => {
    const confidence = p.bestCase > 0 ? Math.round((p.committed / p.bestCase) * 100) : 0;
    lines.push([p.month, String(p.committed), String(p.bestCase), String(p.worstCase), String(confidence)]);
  });
  lines.push([]);
  lines.push(['Forecast By Stage']);
  lines.push(['Stage', 'Probability %', 'Deals', 'Total', 'Weighted', '% of Pipeline']);
  data.forecastByStage.forEach(s => {
    lines.push([s.name, String(s.probability), String(s.deals), String(s.total), String(s.weighted), String(Math.round(s.percentage))]);
  });
  const csv = lines.map(r => r.map(csvCell).join(',')).join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${safeFilename(`${data.pipelineName}-forecast-${data.period}`)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function printForecastView(data: ForecastReportData) {
  const metric = (label: string, value: string, sub = '') =>
    `<div class="kpi"><div class="kpi-l">${escapeHtml(label)}</div><div class="kpi-v">${escapeHtml(value)}</div>${sub ? `<div class="kpi-s">${escapeHtml(sub)}</div>` : ''}</div>`;
  const monthlyRows = data.monthlyProjections.map(p => {
    const confidence = p.bestCase > 0 ? Math.round((p.committed / p.bestCase) * 100) : 0;
    return `<tr><td><strong>${escapeHtml(p.month)}</strong></td><td style="text-align:right;">${escapeHtml(formatCurrency(p.committed))}</td><td style="text-align:right;color:#15803d;">${escapeHtml(formatCurrency(p.bestCase))}</td><td style="text-align:right;color:#c2410c;">${escapeHtml(formatCurrency(p.worstCase))}</td><td style="text-align:center;">${confidence}%</td></tr>`;
  }).join('');
  const totalCommitted = data.monthlyProjections.reduce((s, p) => s + p.committed, 0);
  const totalBest = data.monthlyProjections.reduce((s, p) => s + p.bestCase, 0);
  const totalWorst = data.monthlyProjections.reduce((s, p) => s + p.worstCase, 0);
  const stageRows = data.forecastByStage.map(s => `
    <tr>
      <td><strong>${escapeHtml(s.name)}</strong> <span style="color:#6b7280;font-size:10px;">(${s.probability}%)</span></td>
      <td style="text-align:right;">${s.deals}</td>
      <td style="text-align:right;">${escapeHtml(formatCurrency(s.total))}</td>
      <td style="text-align:right;color:#374151;">${escapeHtml(formatCurrency(s.weighted))}</td>
      <td>
        <div style="height:8px;width:100%;background:#e5e7eb;border-radius:4px;overflow:hidden;">
          <div style="height:100%;width:${Math.min(100, s.percentage).toFixed(1)}%;background:#3b82f6;"></div>
        </div>
      </td>
      <td style="text-align:right;font-size:10px;color:#6b7280;">${Math.round(s.percentage)}%</td>
    </tr>`).join('');
  const renderClosed = (title: string, deals: Array<{ name: string; amount: number }>, color: string) => {
    if (!deals.length) return '';
    const top = deals.slice(0, 10);
    const total = deals.reduce((s, d) => s + d.amount, 0);
    return `
      <div style="border:1px solid ${color}40;background:${color}10;border-radius:6px;padding:10px;">
        <div style="font-weight:600;color:${color};margin-bottom:6px;">${escapeHtml(title)} (${deals.length}) — ${escapeHtml(formatCurrency(total))}</div>
        ${top.map(d => `<div style="display:flex;justify-content:space-between;font-size:11px;padding:2px 0;"><span>${escapeHtml(d.name)}</span><span>${escapeHtml(formatCurrency(d.amount))}</span></div>`).join('')}
        ${deals.length > 10 ? `<div style="font-size:10px;color:#6b7280;margin-top:4px;">+${deals.length - 10} more</div>` : ''}
      </div>`;
  };
  const generated = new Date().toLocaleString();
  const body = `
    <style>
      .kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 12px; }
      .kpi { border: 1px solid #e5e7eb; border-radius: 6px; padding: 10px; background: #fff; }
      .kpi-l { font-size: 10px; color: #6b7280; }
      .kpi-v { font-size: 18px; font-weight: 700; color: #111; margin-top: 4px; }
      .kpi-s { font-size: 10px; color: #6b7280; margin-top: 2px; }
      .section { margin-top: 14px; }
      .section h2 { font-size: 13px; margin: 0 0 6px; }
    </style>
    <h1>${escapeHtml(data.pipelineName)} — Sales Forecast</h1>
    <div class="sub">${escapeHtml(data.period)} · Printed ${escapeHtml(generated)}</div>
    <div class="kpi-grid">
      ${metric('Total Pipeline', formatCurrency(data.totalPipeline))}
      ${metric('Weighted Pipeline', formatCurrency(data.weightedPipeline), 'Based on probability')}
      ${metric('Pipeline Coverage', `${data.pipelineCoverage}%`, '3x target')}
      ${metric('Won Revenue', formatCurrency(data.wonRevenue), `${data.wonDealsCount} deals · ${data.winRate}% win rate`)}
    </div>
    <div class="section">
      <h2>Monthly Revenue Projections</h2>
      <table>
        <thead><tr><th>Month</th><th style="text-align:right;">Committed</th><th style="text-align:right;">Best Case</th><th style="text-align:right;">Worst Case</th><th style="text-align:center;">Confidence</th></tr></thead>
        <tbody>${monthlyRows}</tbody>
        <tfoot><tr style="background:#f9fafb;font-weight:600;">
          <td>Total</td>
          <td style="text-align:right;">${escapeHtml(formatCurrency(totalCommitted))}</td>
          <td style="text-align:right;color:#15803d;">${escapeHtml(formatCurrency(totalBest))}</td>
          <td style="text-align:right;color:#c2410c;">${escapeHtml(formatCurrency(totalWorst))}</td>
          <td style="text-align:center;">—</td>
        </tr></tfoot>
      </table>
    </div>
    <div class="section">
      <h2>Forecast by Stage</h2>
      <table>
        <thead><tr><th>Stage</th><th style="text-align:right;">Deals</th><th style="text-align:right;">Total</th><th style="text-align:right;">Weighted</th><th>Share</th><th style="text-align:right;">%</th></tr></thead>
        <tbody>${stageRows}</tbody>
      </table>
    </div>
    ${(data.wonDeals.length || data.lostDeals.length) ? `
      <div class="section">
        <h2>Closed Deals</h2>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">
          ${renderClosed(data.wonStageName || 'Won', data.wonDeals, '#16a34a')}
          ${renderClosed(data.lostStageName || 'Lost', data.lostDeals, '#dc2626')}
        </div>
      </div>
    ` : ''}`;
  printHtml(`${data.pipelineName} — Forecast`, body, 'portrait');
}

interface PerformanceReportData {
  pipelineName: string;
  period: string;
  winRate: number;
  wonDealsCount: number;
  closedDealsCount: number;
  avgDealSize: number;
  avgCycleDays: number;
  wonRevenue: number;
  salesReps: Array<{ name: string; deals: number; won: number; value: number; quota: number }>;
  wonDeals: Array<{ name: string; company: string; owner: string; amount: number; source?: string }>;
}

function downloadPerformanceCsv(data: PerformanceReportData) {
  const lines: string[][] = [];
  lines.push(['Sales Performance', data.pipelineName, data.period]);
  lines.push([]);
  lines.push(['Summary']);
  lines.push(['Win Rate %', String(data.winRate)]);
  lines.push(['Won Deals', String(data.wonDealsCount)]);
  lines.push(['Closed Deals', String(data.closedDealsCount)]);
  lines.push(['Avg Deal Size', String(data.avgDealSize)]);
  lines.push(['Avg Sales Cycle (days)', String(data.avgCycleDays)]);
  lines.push(['Total Won Revenue', String(data.wonRevenue)]);
  lines.push([]);
  lines.push(['Sales Rep Performance']);
  lines.push(['Rep', 'Open Deals', 'Won', 'Win Rate %', 'Pipeline Value', 'Quota', 'Attainment %']);
  data.salesReps.forEach(r => {
    const winRate = r.deals > 0 ? Math.round((r.won / r.deals) * 100) : 0;
    const attainment = r.quota > 0 ? Math.round((r.value / r.quota) * 100) : 0;
    lines.push([r.name, String(r.deals), String(r.won), String(winRate), String(r.value), String(r.quota), String(attainment)]);
  });
  if (data.wonDeals.length) {
    lines.push([]);
    lines.push(['Won Deals']);
    lines.push(['Deal', 'Company', 'Owner', 'Amount', 'Source']);
    data.wonDeals.forEach(d => lines.push([d.name, d.company, d.owner, String(d.amount), d.source || '']));
  }
  const csv = lines.map(r => r.map(csvCell).join(',')).join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${safeFilename(`${data.pipelineName}-performance-${data.period}`)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function printPerformanceView(data: PerformanceReportData) {
  const metric = (label: string, value: string, sub = '') =>
    `<div class="kpi"><div class="kpi-l">${escapeHtml(label)}</div><div class="kpi-v">${escapeHtml(value)}</div>${sub ? `<div class="kpi-s">${escapeHtml(sub)}</div>` : ''}</div>`;
  const repRows = data.salesReps.map(r => {
    const winRate = r.deals > 0 ? Math.round((r.won / r.deals) * 100) : 0;
    const attainment = r.quota > 0 ? Math.round((r.value / r.quota) * 100) : 0;
    const color = attainment >= 100 ? '#16a34a' : attainment >= 75 ? '#ca8a04' : '#dc2626';
    return `
      <tr>
        <td><strong>${escapeHtml(r.name)}</strong></td>
        <td style="text-align:right;">${r.deals}</td>
        <td style="text-align:right;">${r.won}</td>
        <td style="text-align:right;">${winRate}%</td>
        <td style="text-align:right;">${escapeHtml(formatCurrency(r.value))}</td>
        <td style="text-align:right;">${escapeHtml(formatCurrency(r.quota))}</td>
        <td style="text-align:right;color:${color};font-weight:600;">${attainment}%</td>
      </tr>`;
  }).join('');
  const wonRows = data.wonDeals.map(d => `
    <tr>
      <td><strong>${escapeHtml(d.name)}</strong></td>
      <td>${escapeHtml(d.company)}</td>
      <td>${escapeHtml(d.owner)}</td>
      <td style="text-align:right;color:#15803d;font-weight:600;">${escapeHtml(formatCurrency(d.amount))}</td>
      <td>${escapeHtml(d.source || '—')}</td>
    </tr>`).join('');
  const generated = new Date().toLocaleString();
  const body = `
    <style>
      .kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 12px; }
      .kpi { border: 1px solid #e5e7eb; border-radius: 6px; padding: 10px; background: #fff; }
      .kpi-l { font-size: 10px; color: #6b7280; }
      .kpi-v { font-size: 18px; font-weight: 700; color: #111; margin-top: 4px; }
      .kpi-s { font-size: 10px; color: #6b7280; margin-top: 2px; }
      .section { margin-top: 14px; }
      .section h2 { font-size: 13px; margin: 0 0 6px; }
    </style>
    <h1>${escapeHtml(data.pipelineName)} — Sales Performance</h1>
    <div class="sub">${escapeHtml(data.period)} · Printed ${escapeHtml(generated)}</div>
    <div class="kpi-grid">
      ${metric('Win Rate', `${data.winRate}%`, `${data.wonDealsCount} won / ${data.closedDealsCount} closed`)}
      ${metric('Avg Deal Size', formatCurrency(data.avgDealSize))}
      ${metric('Avg Sales Cycle', `${data.avgCycleDays} days`)}
      ${metric('Total Won Revenue', formatCurrency(data.wonRevenue), `${data.wonDealsCount} closed deals`)}
    </div>
    <div class="section">
      <h2>Sales Rep Performance</h2>
      <table>
        <thead><tr><th>Rep</th><th style="text-align:right;">Open Deals</th><th style="text-align:right;">Won</th><th style="text-align:right;">Win Rate</th><th style="text-align:right;">Pipeline Value</th><th style="text-align:right;">Quota</th><th style="text-align:right;">Attainment</th></tr></thead>
        <tbody>${repRows || '<tr><td colspan="7" style="text-align:center;color:#9ca3af;padding:16px;">No reps</td></tr>'}</tbody>
      </table>
    </div>
    ${data.wonDeals.length ? `
      <div class="section">
        <h2>Won Deals</h2>
        <table>
          <thead><tr><th>Deal</th><th>Company</th><th>Owner</th><th style="text-align:right;">Amount</th><th>Source</th></tr></thead>
          <tbody>${wonRows}</tbody>
        </table>
      </div>
    ` : ''}`;
  printHtml(`${data.pipelineName} — Performance`, body, 'portrait');
}

interface AnalyticsReportData {
  pipelineName: string;
  period: string;
  dealsByAge: Array<{ label: string; count: number; value: number; min: number }>;
  atRiskCount: number;
  atRiskValue: number;
  winCount: number;
  wonRevenue: number;
  avgWonDealSize: number;
  winRate: number;
  lossCount: number;
  lostRevenue: number;
  avgLostDealSize: number;
  lossRate: number;
  healthScores: Array<{ label: string; score: number; target: string; status: 'healthy' | 'warning' }>;
}

function downloadAnalyticsCsv(data: AnalyticsReportData) {
  const lines: string[][] = [];
  lines.push(['Pipeline Analytics', data.pipelineName, data.period]);
  lines.push([]);
  lines.push(['Deal Aging']);
  lines.push(['Bucket', 'Deals', 'Value']);
  data.dealsByAge.forEach(b => lines.push([b.label, String(b.count), String(b.value)]));
  lines.push([]);
  lines.push(['At Risk (60+ days)']);
  lines.push(['Deals', String(data.atRiskCount)]);
  lines.push(['Value', String(data.atRiskValue)]);
  lines.push([]);
  lines.push(['Win Analysis']);
  lines.push(['Won Deals', String(data.winCount)]);
  lines.push(['Won Revenue', String(data.wonRevenue)]);
  lines.push(['Avg Won Deal Size', String(data.avgWonDealSize)]);
  lines.push(['Win Rate %', String(data.winRate)]);
  lines.push([]);
  lines.push(['Loss Analysis']);
  lines.push(['Lost Deals', String(data.lossCount)]);
  lines.push(['Lost Revenue', String(data.lostRevenue)]);
  lines.push(['Avg Lost Deal Size', String(data.avgLostDealSize)]);
  lines.push(['Loss Rate %', String(data.lossRate)]);
  lines.push([]);
  lines.push(['Pipeline Health']);
  lines.push(['Metric', 'Score', 'Target', 'Status']);
  data.healthScores.forEach(h => lines.push([h.label, String(h.score), h.target, h.status]));
  const csv = lines.map(r => r.map(csvCell).join(',')).join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${safeFilename(`${data.pipelineName}-analytics-${data.period}`)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function printAnalyticsView(data: AnalyticsReportData) {
  const ageRows = data.dealsByAge.map(b => `
    <tr>
      <td><strong>${escapeHtml(b.label)}</strong></td>
      <td style="text-align:right;">${b.count}</td>
      <td style="text-align:right;">${escapeHtml(formatCurrency(b.value))}</td>
    </tr>`).join('');
  const wlRow = (label: string, w: string, l: string) =>
    `<tr><td>${escapeHtml(label)}</td><td style="text-align:right;color:#15803d;">${escapeHtml(w)}</td><td style="text-align:right;color:#dc2626;">${escapeHtml(l)}</td></tr>`;
  const healthRows = data.healthScores.map(h => {
    const color = h.score >= 70 ? '#16a34a' : h.score >= 50 ? '#ca8a04' : '#dc2626';
    const statusColor = h.status === 'healthy' ? '#16a34a' : '#ca8a04';
    return `
      <tr>
        <td><strong>${escapeHtml(h.label)}</strong></td>
        <td style="text-align:right;color:${color};font-weight:700;">${h.score} / 100</td>
        <td>${escapeHtml(h.target)}</td>
        <td style="color:${statusColor};font-weight:600;">${escapeHtml(h.status === 'healthy' ? 'Healthy' : 'Needs Attention')}</td>
      </tr>`;
  }).join('');
  const generated = new Date().toLocaleString();
  const body = `
    <style>
      .section { margin-top: 14px; }
      .section h2 { font-size: 13px; margin: 0 0 6px; }
      .callout { border:1px solid #fde68a; background:#fefce8; border-radius:6px; padding:8px 12px; font-size:11px; margin-top:8px; }
    </style>
    <h1>${escapeHtml(data.pipelineName)} — Pipeline Analytics</h1>
    <div class="sub">${escapeHtml(data.period)} · Printed ${escapeHtml(generated)}</div>
    <div class="section">
      <h2>Deal Aging</h2>
      <table>
        <thead><tr><th>Age Bucket</th><th style="text-align:right;">Deals</th><th style="text-align:right;">Value</th></tr></thead>
        <tbody>${ageRows}</tbody>
      </table>
      <div class="callout"><strong>At-Risk Deals:</strong> ${data.atRiskCount} deals have been open for over 60 days with ${escapeHtml(formatCurrency(data.atRiskValue))} at stake.</div>
    </div>
    <div class="section">
      <h2>Win / Loss Analysis</h2>
      <table>
        <thead><tr><th>Metric</th><th style="text-align:right;">Won</th><th style="text-align:right;">Lost</th></tr></thead>
        <tbody>
          ${wlRow('Deals', String(data.winCount), String(data.lossCount))}
          ${wlRow('Revenue', formatCurrency(data.wonRevenue), formatCurrency(data.lostRevenue))}
          ${wlRow('Avg Deal Size', formatCurrency(data.avgWonDealSize), formatCurrency(data.avgLostDealSize))}
          ${wlRow('Rate', `${data.winRate}%`, `${data.lossRate}%`)}
        </tbody>
      </table>
    </div>
    <div class="section">
      <h2>Pipeline Health Score</h2>
      <table>
        <thead><tr><th>Metric</th><th style="text-align:right;">Score</th><th>Target</th><th>Status</th></tr></thead>
        <tbody>${healthRows}</tbody>
      </table>
    </div>`;
  printHtml(`${data.pipelineName} — Analytics`, body, 'portrait');
}

interface ExportMenuProps {
  onDownloadCsv: () => void;
  onPrint: () => void;
  printLabel?: string;
}

function ExportMenu({ onDownloadCsv, onPrint, printLabel }: ExportMenuProps) {
  const { t } = useAppTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm text-gray-700"
        title={t('platform.salesPipeline.export.menu', 'Export')}
      >
        <Download className="h-4 w-4" />
        {t('platform.salesPipeline.export.menu', 'Export')}
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-1">
          <button
            type="button"
            onClick={() => { setOpen(false); onDownloadCsv(); }}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            {t('platform.salesPipeline.export.downloadCsv', 'Download CSV')}
          </button>
          <button
            type="button"
            onClick={() => { setOpen(false); onPrint(); }}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
          >
            <Printer className="h-4 w-4" />
            {printLabel || t('platform.salesPipeline.export.print', 'Print')}
          </button>
        </div>
      )}
    </div>
  );
}

interface PipelineTabProps {
  opportunities: Opportunity[];
  pipeline: Pipeline;
  onAddOpportunity: (stage?: string) => void;
  onStageChange: (opportunityId: string, newStage: string) => void;
  onOpenSettings: () => void;
  onOpportunityClick: (opportunity: Opportunity) => void;
  onEditOpportunity?: (opportunity: Opportunity) => void;
  onDeleteOpportunity?: (opportunity: Opportunity) => void;
  loading?: boolean;
}

function PipelineTab({ opportunities, pipeline, onAddOpportunity, onStageChange, onOpenSettings, onOpportunityClick, onEditOpportunity, onDeleteOpportunity, loading }: PipelineTabProps) {
  const { t } = useAppTranslation();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [filterOwner, setFilterOwner] = useState<string>('all');
  const [filterSource, setFilterSource] = useState<string>('all');
  const [filterAmountMin, setFilterAmountMin] = useState<string>('');
  const [filterAmountMax, setFilterAmountMax] = useState<string>('');
  const [filterCloseBy, setFilterCloseBy] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    // Resolve the destination stage from either a column droppable or a card sortable.
    const overStage = over.data.current?.stage as string | undefined;
    if (!overStage) return;

    const activeOpp = opportunities.find(o => o.id === active.id);
    if (activeOpp && activeOpp.stage !== overStage) {
      onStageChange(active.id as string, overStage);
    }
  };

  // Filter opportunities
  const filteredOpportunities = opportunities.filter(o => {
    if (filterOwner !== 'all' && o.owner !== filterOwner) return false;
    if (filterSource !== 'all' && (o.source || '') !== filterSource) return false;
    if (filterAmountMin && o.amount < parseFloat(filterAmountMin)) return false;
    if (filterAmountMax && o.amount > parseFloat(filterAmountMax)) return false;
    if (filterCloseBy && o.closeDate && o.closeDate > filterCloseBy) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!o.name.toLowerCase().includes(q) && !o.company.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const activeFilterCount = [
    filterOwner !== 'all',
    filterSource !== 'all',
    filterAmountMin !== '',
    filterAmountMax !== '',
    filterCloseBy !== '',
    searchQuery !== '',
  ].filter(Boolean).length;

  const getOpportunitiesByStage = (stageId: string) =>
    filteredOpportunities.filter((opp) => opp.stage === stageId);

  // Get active stages (not won/lost)
  const activeStages = pipeline.stages.filter(s => !s.isWon && !s.isLost);
  const wonStage = pipeline.stages.find(s => s.isWon);
  const lostStage = pipeline.stages.find(s => s.isLost);

  // Get unique owners and sources
  const owners = Array.from(new Set(opportunities.map(o => o.owner)));
  const sources = Array.from(new Set(opportunities.map(o => o.source).filter(Boolean))) as string[];

  const activeOpportunity = activeId ? opportunities.find(o => o.id === activeId) : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">{t('platform.salesPipeline.pipeline.loading', 'Loading pipeline...')}</span>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-900">{pipeline.name}</h3>
          <button
            onClick={onOpenSettings}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
            title={t('platform.salesPipeline.settings.title', 'Pipeline Settings')}
          >
            <Settings className="h-4 w-4" />
          </button>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('platform.salesPipeline.filters.searchDeals', 'Search deals...')}
              className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm w-48"
            />
          </div>
          <select
            value={filterOwner}
            onChange={(e) => setFilterOwner(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">{t('platform.salesPipeline.filters.allOwners', 'All Owners')}</option>
            {owners.map(owner => (
              <option key={owner} value={owner}>{owner}</option>
            ))}
          </select>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-gray-50 ${
              activeFilterCount > 0 ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-gray-300'
            }`}
          >
            <Filter className="h-4 w-4" />
            {t('platform.salesPipeline.common.filter', 'Filter')}
            {activeFilterCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-blue-600 text-white text-xs rounded-full">{activeFilterCount}</span>
            )}
          </button>
          <ExportMenu
            onDownloadCsv={() => downloadOpportunitiesCsv(filteredOpportunities, pipeline, `${pipeline.name}-pipeline`)}
            onPrint={() => printOpportunitiesKanban(filteredOpportunities, pipeline)}
            printLabel={t('platform.salesPipeline.export.printKanban', 'Print Kanban')}
          />
          <button
            onClick={() => onAddOpportunity()}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            {t('platform.salesPipeline.opportunities.addOpportunity', 'Add Opportunity')}
          </button>
        </div>
      </div>

      {/* Expanded Filter Panel */}
      {showFilters && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-700">{t('platform.salesPipeline.filters.title', 'Filters')}</h4>
            <button
              onClick={() => {
                setFilterOwner('all');
                setFilterSource('all');
                setFilterAmountMin('');
                setFilterAmountMax('');
                setFilterCloseBy('');
                setSearchQuery('');
              }}
              className="text-xs text-blue-600 hover:text-blue-700"
            >
              {t('platform.salesPipeline.filters.clearAll', 'Clear All')}
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('platform.salesPipeline.common.source', 'Source')}</label>
              <select
                value={filterSource}
                onChange={(e) => setFilterSource(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="all">{t('platform.salesPipeline.filters.allSources', 'All Sources')}</option>
                {sources.map(src => (
                  <option key={src} value={src}>{src}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('platform.salesPipeline.filters.amountMin', 'Min Amount')}</label>
              <input
                type="number"
                value={filterAmountMin}
                onChange={(e) => setFilterAmountMin(e.target.value)}
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('platform.salesPipeline.filters.amountMax', 'Max Amount')}</label>
              <input
                type="number"
                value={filterAmountMax}
                onChange={(e) => setFilterAmountMax(e.target.value)}
                placeholder={t('platform.salesPipeline.filters.noLimit', 'No limit')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('platform.salesPipeline.filters.closeBy', 'Close By')}</label>
              <input
                type="date"
                value={filterCloseBy}
                onChange={(e) => setFilterCloseBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          </div>
        </div>
      )}

      {/* Pipeline Board with Drag and Drop */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex gap-4 overflow-x-auto pb-4">
          {activeStages.map((stage) => (
            <DroppableStageColumn
              key={stage.id}
              stage={stage}
              opportunities={getOpportunitiesByStage(stage.id)}
              pipeline={pipeline}
              onAddOpportunity={onAddOpportunity}
              onOpportunityClick={onOpportunityClick}
              onEditOpportunity={onEditOpportunity}
              onDeleteOpportunity={onDeleteOpportunity}
              onMoveToStage={(opp, newStage) => onStageChange(opp.id, newStage)}
            />
          ))}

          {/* Closed columns - droppable */}
          {wonStage && (
            <DroppableClosedColumn
              stage={wonStage}
              opportunities={getOpportunitiesByStage(wonStage.id)}
              pipeline={pipeline}
              colorScheme="green"
              onOpportunityClick={onOpportunityClick}
              onEditOpportunity={onEditOpportunity}
              onDeleteOpportunity={onDeleteOpportunity}
              onMoveToStage={(opp, newStage) => onStageChange(opp.id, newStage)}
            />
          )}
          {lostStage && (
            <DroppableClosedColumn
              stage={lostStage}
              opportunities={getOpportunitiesByStage(lostStage.id)}
              pipeline={pipeline}
              colorScheme="red"
              onOpportunityClick={onOpportunityClick}
              onEditOpportunity={onEditOpportunity}
              onDeleteOpportunity={onDeleteOpportunity}
              onMoveToStage={(opp, newStage) => onStageChange(opp.id, newStage)}
            />
          )}
        </div>

        {/* Drag Overlay */}
        <DragOverlay>
          {activeOpportunity ? (
            <div className="bg-white rounded-lg border-2 border-blue-400 p-3 shadow-lg w-72">
              <h5 className="font-medium text-gray-900 text-sm">{activeOpportunity.name}</h5>
              <p className="text-sm text-gray-500">{activeOpportunity.company}</p>
              <p className="text-lg font-bold text-gray-900">{formatCurrency(activeOpportunity.amount)}</p>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}

interface LeadsTabProps {
  leads: Lead[];
  onAddLead: () => void;
  onConvertLead: (lead: Lead) => void;
  onEmailLead: (lead: Lead) => void;
  onCallLead: (lead: Lead) => void;
}

function LeadsTab({ leads, onAddLead, onConvertLead, onEmailLead, onCallLead }: LeadsTabProps) {
  const { t } = useAppTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string>('all');

  // Filter leads by search term and source
  const filteredLeads = leads.filter(lead => {
    const matchesSearch = searchTerm === '' ||
      lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.company.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesSource = sourceFilter === 'all' ||
      (sourceFilter === 'marketing' && lead.source === 'Marketing Contact') ||
      (sourceFilter === 'organization' && lead.source !== 'Marketing Contact');

    return matchesSearch && matchesSource;
  });

  // Count leads by source
  const marketingCount = leads.filter(l => l.source === 'Marketing Contact').length;
  const orgCount = leads.filter(l => l.source !== 'Marketing Contact').length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold text-gray-900">{t('platform.salesPipeline.leads.title', 'Leads')}</h3>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">
              {leads.length} {t('platform.salesPipeline.common.total', 'total')}
            </span>
            <span className="text-gray-300">|</span>
            <span className="text-sm text-purple-600">
              {marketingCount} {t('platform.salesPipeline.leads.fromMarketing', 'from Marketing')}
            </span>
            <span className="text-gray-300">|</span>
            <span className="text-sm text-blue-600">
              {orgCount} {t('platform.salesPipeline.leads.fromOrganizations', 'from Organizations')}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">{t('platform.salesPipeline.filters.allSources', 'All Sources')}</option>
            <option value="marketing">{t('platform.salesPipeline.sources.marketingContacts', 'Marketing Contacts')}</option>
            <option value="organization">{t('platform.salesPipeline.sources.organizations', 'Organizations')}</option>
          </select>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('platform.salesPipeline.leads.searchPlaceholder', 'Search leads...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-64"
            />
          </div>
          <button
            onClick={onAddLead}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            {t('platform.salesPipeline.leads.addLead', 'Add Lead')}
          </button>
        </div>
      </div>

      {filteredLeads.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('platform.salesPipeline.leads.noLeadsFound', 'No Leads Found')}</h3>
          <p className="text-gray-500 mb-4">
            {searchTerm || sourceFilter !== 'all'
              ? t('platform.salesPipeline.leads.adjustSearchCriteria', 'Try adjusting your search or filter criteria.')
              : t('platform.salesPipeline.leads.leadsAutoCreated', 'Leads are automatically created from Marketing Contacts and Organizations.')}
          </p>
          <p className="text-sm text-gray-400">
            {t('platform.salesPipeline.leads.addContactsHelp', 'Add contacts in Marketing Automation or create Organizations to see them as leads here.')}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('platform.salesPipeline.leads.columns.lead', 'Lead')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('platform.salesPipeline.common.company', 'Company')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('platform.salesPipeline.common.source', 'Source')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('platform.salesPipeline.leads.columns.score', 'Score')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('platform.salesPipeline.common.status', 'Status')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('platform.salesPipeline.common.actions', 'Actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredLeads.map((lead) => (
                <tr key={lead.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-gray-900">{lead.name}</div>
                      <div className="text-sm text-gray-500">{lead.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">{lead.company}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      lead.source === 'Marketing Contact' ? 'bg-purple-100 text-purple-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {lead.source}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                        lead.score >= 80 ? 'bg-green-100 text-green-700' :
                        lead.score >= 50 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {lead.score}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      lead.status === 'qualified' ? 'bg-green-100 text-green-800' :
                      lead.status === 'contacted' ? 'bg-blue-100 text-blue-800' :
                      lead.status === 'new' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {lead.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); onCallLead(lead); }}
                        className="p-2 hover:bg-green-100 rounded-lg transition-colors"
                        title={t('platform.salesPipeline.leads.actions.call', 'Call')}
                      >
                        <Phone className="h-4 w-4 text-green-600" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); onEmailLead(lead); }}
                        className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
                        title={t('platform.salesPipeline.leads.actions.email', 'Email')}
                      >
                        <Mail className="h-4 w-4 text-blue-600" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); onConvertLead(lead); }}
                        className="p-2 hover:bg-purple-100 rounded-lg transition-colors"
                        title={t('platform.salesPipeline.leads.actions.convertToOpportunity', 'Convert to Opportunity')}
                      >
                        <ArrowRight className="h-4 w-4 text-purple-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

interface OpportunitiesTabProps {
  opportunities: Opportunity[];
  pipeline: Pipeline;
  onAddOpportunity: () => void;
  onOpportunityClick: (opportunity: Opportunity) => void;
}

function OpportunitiesTab({ opportunities, pipeline, onAddOpportunity, onOpportunityClick }: OpportunitiesTabProps) {
  const { t } = useAppTranslation();
  const getStage = (stageId: string) => pipeline.stages.find(s => s.id === stageId);
  const listTitle = t('platform.salesPipeline.opportunities.allOpportunities', 'All Opportunities');

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">{listTitle}</h3>
        <div className="flex items-center gap-3">
          <ExportMenu
            onDownloadCsv={() => downloadOpportunitiesCsv(opportunities, pipeline, `${pipeline.name}-opportunities`)}
            onPrint={() => printOpportunitiesList(opportunities, pipeline, listTitle)}
            printLabel={t('platform.salesPipeline.export.printList', 'Print List')}
          />
          <button
            onClick={onAddOpportunity}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            {t('platform.salesPipeline.opportunities.newOpportunity', 'New Opportunity')}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('platform.salesPipeline.opportunities.columns.opportunity', 'Opportunity')}</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('platform.salesPipeline.common.amount', 'Amount')}</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('platform.salesPipeline.common.stage', 'Stage')}</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('platform.salesPipeline.common.closeDate', 'Close Date')}</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('platform.salesPipeline.common.owner', 'Owner')}</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('platform.salesPipeline.common.probability', 'Probability')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {opportunities.map((opp) => {
              const stage = getStage(opp.stage);
              return (
                <tr key={opp.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => onOpportunityClick(opp)}>
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-gray-900">{opp.name}</div>
                      <div className="text-sm text-gray-500">{opp.company}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {formatCurrency(opp.amount)}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${stage?.color || 'bg-gray-100'}`}>
                      {stage?.name || opp.stage}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">{opp.closeDate || '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{opp.owner}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500 rounded-full"
                          style={{ width: `${opp.probability}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-600">{opp.probability}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// PRODUCTS & PRICING TAB
// ============================================================================

interface ProductsTabProps {
  products: Product[];
  opportunities: Opportunity[];
  onAddProduct: (product: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onUpdateProduct: (product: Product) => void;
  onDeleteProduct: (productId: string) => void;
  onAssignToOpportunity: (product: Product, opportunityId: string) => void;
}

function ProductsTab({ products, opportunities, onAddProduct, onUpdateProduct, onDeleteProduct, onAssignToOpportunity }: ProductsTabProps) {
  const { t } = useAppTranslation();
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [assigningProduct, setAssigningProduct] = useState<Product | null>(null);
  const [filterCategory, setFilterCategory] = useState<string>('all');

  // New product form state
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    sku: '',
    category: '',
    pricingModel: 'flat_rate' as PricingModel,
    unitPrice: 0,
    billingType: 'recurring' as BillingType,
    billingInterval: 'monthly' as BillingInterval,
    // Tiered pricing
    priceTiers: [] as PriceTier[],
    // Volume pricing
    volumeThresholds: [] as VolumeThreshold[],
    // Transaction/usage pricing
    transactionRate: 0,
    usageRate: 0,
    minimumFee: 0,
    // Percentage-based
    percentageRate: 0,
    percentageBasis: 'total_billing' as PercentageBasis,
    percentageBasisCustomLabel: '',
    isCredit: false,
    isActive: true,
  });

  const categories = Array.from(new Set(products.map(p => p.category).filter(Boolean)));

  const filteredProducts = filterCategory === 'all'
    ? products
    : products.filter(p => p.category === filterCategory);

  const handleAddProduct = () => {
    onAddProduct({
      ...newProduct,
    });
    setNewProduct({
      name: '',
      description: '',
      sku: '',
      category: '',
      pricingModel: 'flat_rate',
      unitPrice: 0,
      billingType: 'recurring',
      billingInterval: 'monthly',
      priceTiers: [],
      volumeThresholds: [],
      transactionRate: 0,
      usageRate: 0,
      minimumFee: 0,
      percentageRate: 0,
      percentageBasis: 'total_billing',
      percentageBasisCustomLabel: '',
      isCredit: false,
      isActive: true,
    });
    setShowAddModal(false);
  };

  const formatPricingModel = (model: PricingModel) => {
    const labels: Record<PricingModel, string> = {
      flat_rate: t('platform.salesPipeline.pricingModels.flatRate', 'Flat Rate'),
      per_user: t('platform.salesPipeline.pricingModels.perUser', 'Per User'),
      per_member: t('platform.salesPipeline.pricingModels.perMember', 'Per Member'),
      tiered: t('platform.salesPipeline.pricingModels.tiered', 'Tiered'),
      volume: t('platform.salesPipeline.pricingModels.volume', 'Volume'),
      usage_based: t('platform.salesPipeline.pricingModels.usageBased', 'Usage-Based'),
      transaction: t('platform.salesPipeline.pricingModels.perTransaction', 'Per Transaction'),
      outcome_based: t('platform.salesPipeline.pricingModels.outcomeBased', 'Outcome-Based'),
      percentage_of_billing: t('platform.salesPipeline.pricingModels.percentageOfBilling', '% of Billing'),
      percentage_credit: t('platform.salesPipeline.pricingModels.percentageCredit', '% Credit'),
    };
    return labels[model] || model;
  };

  const formatBillingType = (type: BillingType) => {
    const labels: Record<BillingType, string> = {
      one_time: t('platform.salesPipeline.billingTypes.oneTime', 'One-Time'),
      recurring: t('platform.salesPipeline.billingTypes.recurring', 'Recurring'),
      usage: t('platform.salesPipeline.billingTypes.usage', 'Usage'),
      transaction: t('platform.salesPipeline.billingTypes.transaction', 'Transaction'),
      outcome: t('platform.salesPipeline.billingTypes.outcome', 'Outcome'),
      percentage: t('platform.salesPipeline.billingTypes.percentageOfBilling', '% of Billing'),
    };
    return labels[type] || type;
  };

  const formatPercentageBasis = (basis: PercentageBasis) => {
    const labels: Record<PercentageBasis, string> = {
      total_billing: t('platform.salesPipeline.percentageBasis.totalBilling', 'Total Billing'),
      claims_processed: t('platform.salesPipeline.percentageBasis.claimsProcessed', 'Claims Processed'),
      chargebacks: t('platform.salesPipeline.percentageBasis.chargebacks', 'Chargebacks'),
      revenue: t('platform.salesPipeline.percentageBasis.revenue', 'Revenue'),
      custom: t('platform.salesPipeline.percentageBasis.custom', 'Custom'),
    };
    return labels[basis] || basis;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{t('platform.salesPipeline.products.title', 'Products & Pricing Catalog')}</h3>
          <p className="text-sm text-gray-500 mt-1">{t('platform.salesPipeline.products.description', 'Manage your product catalog and assign products to opportunities')}</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          {t('platform.salesPipeline.products.addProduct', 'Add Product')}
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="all">{t('platform.salesPipeline.filters.allCategories', 'All Categories')}</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
        <span className="text-sm text-gray-500">{filteredProducts.length} {filteredProducts.length !== 1 ? t('platform.salesPipeline.products.productsPlural', 'products') : t('platform.salesPipeline.products.productSingular', 'product')}</span>
      </div>

      {/* Products Grid */}
      {filteredProducts.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">{t('platform.salesPipeline.products.noProductsYet', 'No products yet')}</h4>
          <p className="text-gray-500 mb-4">{t('platform.salesPipeline.products.createProductsHelp', 'Create products to add to your opportunities')}</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {t('platform.salesPipeline.products.addFirstProduct', 'Add Your First Product')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProducts.map((product) => (
            <div key={product.id} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-medium text-gray-900">{product.name}</h4>
                  {product.sku && <p className="text-xs text-gray-500">{t('platform.salesPipeline.products.sku', 'SKU')}: {product.sku}</p>}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setEditingProduct(product)}
                    className="p-1 hover:bg-gray-100 rounded"
                    title={t('platform.salesPipeline.common.edit', 'Edit')}
                  >
                    <Edit className="h-4 w-4 text-gray-500" />
                  </button>
                  <button
                    onClick={() => onDeleteProduct(product.id)}
                    className="p-1 hover:bg-red-100 rounded"
                    title={t('platform.salesPipeline.common.delete', 'Delete')}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </button>
                </div>
              </div>

              {product.description && (
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{product.description}</p>
              )}

              <div className="space-y-2 mb-4">
                {product.billingType === 'percentage' ? (
                  <>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">{t('platform.salesPipeline.products.rate', 'Rate')}</span>
                      <span className={`font-medium ${product.isCredit ? 'text-red-600' : 'text-gray-900'}`}>
                        {product.isCredit ? '-' : ''}{product.percentageRate}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">{t('platform.salesPipeline.products.calculatedOn', 'Calculated On')}</span>
                      <span className="text-gray-700">
                        {product.percentageBasis === 'custom' ? product.percentageBasisCustomLabel : formatPercentageBasis(product.percentageBasis || 'total_billing')}
                      </span>
                    </div>
                    {product.isCredit && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">{t('platform.salesPipeline.products.type', 'Type')}</span>
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">{t('platform.salesPipeline.products.credit', 'Credit')}</span>
                      </div>
                    )}
                  </>
                ) : product.pricingModel === 'tiered' && product.priceTiers?.length ? (
                  <div className="text-sm">
                    <span className="text-gray-500 block mb-1">{t('platform.salesPipeline.products.tieredPricing', 'Tiered Pricing')}:</span>
                    <div className="space-y-0.5">
                      {product.priceTiers.slice(0, 3).map((tier, idx) => (
                        <div key={tier.id} className="text-xs text-gray-600">
                          {tier.minQuantity}-{tier.maxQuantity || '∞'} {t('platform.salesPipeline.products.units', 'units')}: {formatCurrency(tier.unitPrice)}/{t('platform.salesPipeline.products.unit', 'unit')}
                        </div>
                      ))}
                      {product.priceTiers.length > 3 && (
                        <div className="text-xs text-gray-400">+{product.priceTiers.length - 3} {t('platform.salesPipeline.products.moreTiers', 'more tiers')}</div>
                      )}
                    </div>
                  </div>
                ) : product.pricingModel === 'volume' && product.volumeThresholds?.length ? (
                  <div className="text-sm">
                    <span className="text-gray-500 block mb-1">{t('platform.salesPipeline.products.volumePricing', 'Volume Pricing')}:</span>
                    <div className="space-y-0.5">
                      {product.volumeThresholds.slice(0, 3).map((threshold, idx) => (
                        <div key={threshold.id} className="text-xs text-gray-600">
                          {threshold.minQuantity}-{threshold.maxQuantity || '∞'} → {formatCurrency(threshold.unitPrice)}/{t('platform.salesPipeline.products.unit', 'unit')}
                        </div>
                      ))}
                      {product.volumeThresholds.length > 3 && (
                        <div className="text-xs text-gray-400">+{product.volumeThresholds.length - 3} {t('platform.salesPipeline.products.moreThresholds', 'more thresholds')}</div>
                      )}
                    </div>
                  </div>
                ) : product.pricingModel === 'transaction' ? (
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">{t('platform.salesPipeline.products.ratePerTransaction', 'Rate per Transaction')}</span>
                      <span className="font-medium text-gray-900">{formatCurrency(product.transactionRate || 0)}</span>
                    </div>
                    {product.minimumFee && product.minimumFee > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">{t('platform.salesPipeline.products.minimumFee', 'Minimum Fee')}</span>
                        <span className="text-gray-700">{formatCurrency(product.minimumFee)}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">{t('platform.salesPipeline.products.price', 'Price')}</span>
                    <span className="font-medium text-gray-900">
                      {formatCurrency(product.unitPrice)}
                      {product.billingType === 'recurring' && product.billingInterval && (
                        <span className="text-gray-500 text-xs">/{product.billingInterval.replace('_', ' ')}</span>
                      )}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">{t('platform.salesPipeline.products.pricingModel', 'Pricing Model')}</span>
                  <span className="text-gray-700">{formatPricingModel(product.pricingModel)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">{t('platform.salesPipeline.products.billing', 'Billing')}</span>
                  <span className="text-gray-700">{formatBillingType(product.billingType)}</span>
                </div>
                {product.category && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">{t('platform.salesPipeline.products.category', 'Category')}</span>
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">{product.category}</span>
                  </div>
                )}
              </div>

              <div className="pt-3 border-t border-gray-100">
                <button
                  onClick={() => setAssigningProduct(product)}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
                >
                  <ArrowRight className="h-4 w-4" />
                  {t('platform.salesPipeline.products.assignToOpportunity', 'Assign to Opportunity')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Product Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t('platform.salesPipeline.products.addNewProduct', 'Add New Product')}</DialogTitle>
            <DialogDescription>
              {t('platform.salesPipeline.products.createProductTemplate', 'Create a product template that can be added to opportunities.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('platform.salesPipeline.products.productName', 'Product Name')} *</label>
              <input
                type="text"
                value={newProduct.name}
                onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                placeholder={t('platform.salesPipeline.products.placeholders.productName', 'e.g., Platform License')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('platform.salesPipeline.products.productDescription', 'Description')}</label>
              <textarea
                value={newProduct.description}
                onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                placeholder={t('platform.salesPipeline.products.placeholders.description', 'Product description...')}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('platform.salesPipeline.products.sku', 'SKU')}</label>
                <input
                  type="text"
                  value={newProduct.sku}
                  onChange={(e) => setNewProduct({ ...newProduct, sku: e.target.value })}
                  placeholder={t('platform.salesPipeline.products.placeholders.sku', 'e.g., PLT-001')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('platform.salesPipeline.products.category', 'Category')}</label>
                <input
                  type="text"
                  value={newProduct.category}
                  onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
                  placeholder={t('platform.salesPipeline.products.placeholders.category', 'e.g., Software')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('platform.salesPipeline.products.pricingModel', 'Pricing Model')}</label>
                <select
                  value={newProduct.pricingModel}
                  onChange={(e) => {
                    const model = e.target.value as PricingModel;
                    setNewProduct({
                      ...newProduct,
                      pricingModel: model,
                      // Reset tiers/thresholds when changing model
                      priceTiers: model === 'tiered' ? (newProduct.priceTiers.length ? newProduct.priceTiers : [
                        { id: crypto.randomUUID(), minQuantity: 1, maxQuantity: 10, unitPrice: 0 },
                        { id: crypto.randomUUID(), minQuantity: 11, maxQuantity: null, unitPrice: 0 },
                      ]) : [],
                      volumeThresholds: model === 'volume' ? (newProduct.volumeThresholds.length ? newProduct.volumeThresholds : [
                        { id: crypto.randomUUID(), minQuantity: 1, maxQuantity: 100, unitPrice: 0 },
                        { id: crypto.randomUUID(), minQuantity: 101, maxQuantity: null, unitPrice: 0 },
                      ]) : [],
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="flat_rate">Flat Rate</option>
                  <option value="per_user">Per User</option>
                  <option value="per_member">Per Member</option>
                  <option value="tiered">Tiered (Graduated)</option>
                  <option value="volume">Volume (Threshold)</option>
                  <option value="usage_based">Usage-Based</option>
                  <option value="transaction">Per Transaction</option>
                  <option value="outcome_based">Outcome-Based</option>
                </select>
              </div>
              {/* Show Unit Price for flat_rate, per_user, per_member, usage_based, outcome_based */}
              {['flat_rate', 'per_user', 'per_member', 'usage_based', 'outcome_based'].includes(newProduct.pricingModel) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {newProduct.pricingModel === 'per_user' ? 'Price per User *' :
                     newProduct.pricingModel === 'per_member' ? 'Price per Member *' :
                     newProduct.pricingModel === 'usage_based' ? 'Price per Unit *' :
                     newProduct.pricingModel === 'outcome_based' ? 'Price per Outcome *' :
                     'Unit Price *'}
                  </label>
                  <input
                    type="number"
                    value={newProduct.unitPrice}
                    onChange={(e) => setNewProduct({ ...newProduct, unitPrice: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
              {/* Show Transaction Rate for transaction pricing */}
              {newProduct.pricingModel === 'transaction' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rate per Transaction ($) *</label>
                  <input
                    type="number"
                    value={newProduct.transactionRate}
                    onChange={(e) => setNewProduct({ ...newProduct, transactionRate: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="0.01"
                    placeholder="e.g., 0.50"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
            </div>

            {/* Tiered Pricing Configuration */}
            {newProduct.pricingModel === 'tiered' && (
              <div className="space-y-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Tiered Pricing (Graduated)</h4>
                    <p className="text-xs text-gray-500">Each tier's price applies only to units within that tier's range</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const lastTier = newProduct.priceTiers[newProduct.priceTiers.length - 1];
                      const newMin = lastTier && lastTier.maxQuantity ? lastTier.maxQuantity + 1 : 1;
                      setNewProduct({
                        ...newProduct,
                        priceTiers: [...newProduct.priceTiers, {
                          id: crypto.randomUUID(),
                          minQuantity: newMin,
                          maxQuantity: null,
                          unitPrice: 0,
                        }]
                      });
                    }}
                    className="text-xs px-2 py-1 bg-amber-100 text-amber-800 rounded hover:bg-amber-200"
                  >
                    + Add Tier
                  </button>
                </div>
                <div className="space-y-2">
                  {newProduct.priceTiers.map((tier, index) => (
                    <div key={tier.id} className="flex items-center gap-2 text-sm">
                      <span className="text-gray-500 w-12">Tier {index + 1}</span>
                      <input
                        type="number"
                        value={tier.minQuantity}
                        onChange={(e) => {
                          const updated = [...newProduct.priceTiers];
                          updated[index] = { ...tier, minQuantity: parseInt(e.target.value) || 0 };
                          setNewProduct({ ...newProduct, priceTiers: updated });
                        }}
                        className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                        placeholder="Min"
                      />
                      <span className="text-gray-400">to</span>
                      <input
                        type="number"
                        value={tier.maxQuantity || ''}
                        onChange={(e) => {
                          const updated = [...newProduct.priceTiers];
                          updated[index] = { ...tier, maxQuantity: e.target.value ? parseInt(e.target.value) : null };
                          setNewProduct({ ...newProduct, priceTiers: updated });
                        }}
                        className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                        placeholder="∞"
                      />
                      <span className="text-gray-400">units @</span>
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                        <input
                          type="number"
                          value={tier.unitPrice}
                          onChange={(e) => {
                            const updated = [...newProduct.priceTiers];
                            updated[index] = { ...tier, unitPrice: parseFloat(e.target.value) || 0 };
                            setNewProduct({ ...newProduct, priceTiers: updated });
                          }}
                          className="w-24 pl-6 pr-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="0.00"
                          step="0.01"
                        />
                      </div>
                      <span className="text-gray-400">/unit</span>
                      {newProduct.priceTiers.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            setNewProduct({
                              ...newProduct,
                              priceTiers: newProduct.priceTiers.filter(t => t.id !== tier.id)
                            });
                          }}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Volume Pricing Configuration */}
            {newProduct.pricingModel === 'volume' && (
              <div className="space-y-3 bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Volume Pricing (Threshold)</h4>
                    <p className="text-xs text-gray-500">All units get the same price based on total quantity ordered</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const lastThreshold = newProduct.volumeThresholds[newProduct.volumeThresholds.length - 1];
                      const newMin = lastThreshold && lastThreshold.maxQuantity ? lastThreshold.maxQuantity + 1 : 1;
                      setNewProduct({
                        ...newProduct,
                        volumeThresholds: [...newProduct.volumeThresholds, {
                          id: crypto.randomUUID(),
                          minQuantity: newMin,
                          maxQuantity: null,
                          unitPrice: 0,
                        }]
                      });
                    }}
                    className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded hover:bg-green-200"
                  >
                    + Add Threshold
                  </button>
                </div>
                <div className="space-y-2">
                  {newProduct.volumeThresholds.map((threshold, index) => (
                    <div key={threshold.id} className="flex items-center gap-2 text-sm">
                      <span className="text-gray-500 w-12">Qty</span>
                      <input
                        type="number"
                        value={threshold.minQuantity}
                        onChange={(e) => {
                          const updated = [...newProduct.volumeThresholds];
                          updated[index] = { ...threshold, minQuantity: parseInt(e.target.value) || 0 };
                          setNewProduct({ ...newProduct, volumeThresholds: updated });
                        }}
                        className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                        placeholder="Min"
                      />
                      <span className="text-gray-400">to</span>
                      <input
                        type="number"
                        value={threshold.maxQuantity || ''}
                        onChange={(e) => {
                          const updated = [...newProduct.volumeThresholds];
                          updated[index] = { ...threshold, maxQuantity: e.target.value ? parseInt(e.target.value) : null };
                          setNewProduct({ ...newProduct, volumeThresholds: updated });
                        }}
                        className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                        placeholder="∞"
                      />
                      <span className="text-gray-400">→ All units @</span>
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                        <input
                          type="number"
                          value={threshold.unitPrice}
                          onChange={(e) => {
                            const updated = [...newProduct.volumeThresholds];
                            updated[index] = { ...threshold, unitPrice: parseFloat(e.target.value) || 0 };
                            setNewProduct({ ...newProduct, volumeThresholds: updated });
                          }}
                          className="w-24 pl-6 pr-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="0.00"
                          step="0.01"
                        />
                      </div>
                      <span className="text-gray-400">/unit</span>
                      {newProduct.volumeThresholds.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            setNewProduct({
                              ...newProduct,
                              volumeThresholds: newProduct.volumeThresholds.filter(t => t.id !== threshold.id)
                            });
                          }}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Transaction Pricing Additional Fields */}
            {newProduct.pricingModel === 'transaction' && (
              <div className="grid grid-cols-2 gap-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Fee ($)</label>
                  <input
                    type="number"
                    value={newProduct.minimumFee || ''}
                    onChange={(e) => setNewProduct({ ...newProduct, minimumFee: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="0.01"
                    placeholder="Optional minimum"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mt-6">
                    If total transaction fees are below minimum, customer pays the minimum fee instead.
                  </p>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Billing Type</label>
                <select
                  value={newProduct.billingType}
                  onChange={(e) => setNewProduct({ ...newProduct, billingType: e.target.value as BillingType })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="one_time">One-Time</option>
                  <option value="recurring">Recurring</option>
                  <option value="usage">Usage</option>
                  <option value="transaction">Transaction</option>
                  <option value="outcome">Outcome</option>
                  <option value="percentage">% of Billing/Revenue</option>
                </select>
              </div>
              {newProduct.billingType === 'recurring' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Billing Interval</label>
                  <select
                    value={newProduct.billingInterval}
                    onChange={(e) => setNewProduct({ ...newProduct, billingInterval: e.target.value as BillingInterval })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="semi_annual">Semi-Annual</option>
                    <option value="annual">Annual</option>
                  </select>
                </div>
              )}
            </div>
            {/* Percentage-based product fields */}
            {newProduct.billingType === 'percentage' && (
              <div className="space-y-4 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Percentage Rate (%) *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newProduct.percentageRate || ''}
                      onChange={(e) => setNewProduct({ ...newProduct, percentageRate: parseFloat(e.target.value) || 0 })}
                      placeholder="e.g., 3.0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Calculated On</label>
                    <select
                      value={newProduct.percentageBasis}
                      onChange={(e) => setNewProduct({ ...newProduct, percentageBasis: e.target.value as PercentageBasis })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="total_billing">Total Billing</option>
                      <option value="claims_processed">Claims Processed</option>
                      <option value="chargebacks">Chargebacks</option>
                      <option value="revenue">Revenue</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                </div>
                {newProduct.percentageBasis === 'custom' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Custom Basis Label</label>
                    <input
                      type="text"
                      value={newProduct.percentageBasisCustomLabel}
                      onChange={(e) => setNewProduct({ ...newProduct, percentageBasisCustomLabel: e.target.value })}
                      placeholder="e.g., Premium Collections"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="newProductIsCredit"
                    checked={newProduct.isCredit}
                    onChange={(e) => setNewProduct({ ...newProduct, isCredit: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <label htmlFor="newProductIsCredit" className="text-sm text-gray-700">
                    This is a credit (reduces total, e.g., chargeback credit)
                  </label>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setShowAddModal(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAddProduct}
              disabled={
                !newProduct.name ||
                // For tiered: must have at least one tier with price
                (newProduct.pricingModel === 'tiered' && (!newProduct.priceTiers.length || newProduct.priceTiers.every(t => t.unitPrice <= 0))) ||
                // For volume: must have at least one threshold with price
                (newProduct.pricingModel === 'volume' && (!newProduct.volumeThresholds.length || newProduct.volumeThresholds.every(t => t.unitPrice <= 0))) ||
                // For transaction: must have a rate
                (newProduct.pricingModel === 'transaction' && newProduct.transactionRate <= 0) ||
                // For percentage: must have a rate
                (newProduct.billingType === 'percentage' && !newProduct.percentageRate) ||
                // For other models: must have unit price
                (!['tiered', 'volume', 'transaction'].includes(newProduct.pricingModel) && newProduct.billingType !== 'percentage' && newProduct.unitPrice <= 0)
              }
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add Product
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign to Opportunity Modal */}
      <Dialog open={!!assigningProduct} onOpenChange={() => setAssigningProduct(null)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Assign to Opportunity</DialogTitle>
            <DialogDescription>
              Add "{assigningProduct?.name}" to an opportunity as a line item.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {opportunities.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No opportunities available</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {opportunities.map((opp) => (
                  <button
                    key={opp.id}
                    onClick={() => {
                      if (assigningProduct) {
                        onAssignToOpportunity(assigningProduct, opp.id);
                        setAssigningProduct(null);
                      }
                    }}
                    className="w-full text-left p-3 hover:bg-gray-50 rounded-lg border border-gray-200 transition-colors"
                  >
                    <div className="font-medium text-gray-900">{opp.name}</div>
                    <div className="text-sm text-gray-500">{opp.company} • {formatCurrency(opp.amount)}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Product Modal */}
      <Dialog open={!!editingProduct} onOpenChange={() => setEditingProduct(null)}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Product</DialogTitle>
          </DialogHeader>
          {editingProduct && (
            <div className="space-y-4 py-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
                <input
                  type="text"
                  value={editingProduct.name}
                  onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={editingProduct.description || ''}
                  onChange={(e) => setEditingProduct({ ...editingProduct, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unit Price</label>
                  <input
                    type="number"
                    value={editingProduct.unitPrice}
                    onChange={(e) => setEditingProduct({ ...editingProduct, unitPrice: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <input
                    type="text"
                    value={editingProduct.category || ''}
                    onChange={(e) => setEditingProduct({ ...editingProduct, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={editingProduct.isActive}
                  onChange={(e) => setEditingProduct({ ...editingProduct, isActive: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <label htmlFor="isActive" className="text-sm text-gray-700">Active</label>
              </div>
            </div>
          )}
          <DialogFooter>
            <button
              type="button"
              onClick={() => setEditingProduct(null)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                if (editingProduct) {
                  onUpdateProduct({ ...editingProduct, updatedAt: new Date().toISOString() });
                  setEditingProduct(null);
                }
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Save Changes
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Activity Timeline Component - Reusable for both Activities tab and Opportunity detail
interface ActivityTimelineProps {
  activities: Activity[];
  onAddActivity?: (type: Activity['type']) => void;
  onComplete?: (activity: Activity) => void;
  onActivityClick?: (activity: Activity) => void;
  opportunities?: Opportunity[];
  showFilters?: boolean;
  maxItems?: number;
}

function ActivityTimeline({ activities, onAddActivity, onComplete, onActivityClick, opportunities, showFilters = true, maxItems }: ActivityTimelineProps) {
  const { t } = useAppTranslation();
  const [filterType, setFilterType] = useState<Activity['type'] | 'all'>('all');

  const filteredActivities = filterType === 'all'
    ? activities
    : activities.filter(a => a.type === filterType);

  const displayActivities = maxItems
    ? filteredActivities.slice(0, maxItems)
    : filteredActivities;

  const getActivityIcon = (type: Activity['type']) => {
    switch (type) {
      case 'call': return <Phone className="h-4 w-4 text-green-600" />;
      case 'email': return <Mail className="h-4 w-4 text-blue-600" />;
      case 'meeting': return <Calendar className="h-4 w-4 text-purple-600" />;
      case 'note': return <Edit className="h-4 w-4 text-yellow-600" />;
      case 'stage_change': return <ArrowRight className="h-4 w-4 text-orange-600" />;
      default: return <CheckCircle className="h-4 w-4 text-gray-600" />;
    }
  };

  const getActivityColor = (type: Activity['type']) => {
    switch (type) {
      case 'call': return 'bg-green-100 border-green-300';
      case 'email': return 'bg-blue-100 border-blue-300';
      case 'meeting': return 'bg-purple-100 border-purple-300';
      case 'note': return 'bg-yellow-100 border-yellow-300';
      case 'stage_change': return 'bg-orange-100 border-orange-300';
      default: return 'bg-gray-100 border-gray-300';
    }
  };

  return (
    <div>
      {/* Quick Add Buttons */}
      {onAddActivity && (
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={() => onAddActivity('note')}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-yellow-50 text-yellow-700 rounded-lg hover:bg-yellow-100"
          >
            <Edit className="h-3 w-3" /> {t('platform.salesPipeline.activities.types.note', 'Note')}
          </button>
          <button
            onClick={() => onAddActivity('call')}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100"
          >
            <Phone className="h-3 w-3" /> {t('platform.salesPipeline.activities.types.call', 'Call')}
          </button>
          <button
            onClick={() => onAddActivity('email')}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100"
          >
            <Mail className="h-3 w-3" /> {t('platform.salesPipeline.activities.types.email', 'Email')}
          </button>
          <button
            onClick={() => onAddActivity('meeting')}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100"
          >
            <Calendar className="h-3 w-3" /> {t('platform.salesPipeline.activities.types.meeting', 'Meeting')}
          </button>
        </div>
      )}

      {/* Filters */}
      {showFilters && (
        <div className="flex gap-2 mb-4">
          {(['all', 'call', 'email', 'meeting', 'note', 'task', 'stage_change'] as const).map(type => (
            <button
              key={type}
              onClick={() => setFilterType(type)}
              className={`px-3 py-1 text-xs rounded-full transition-colors ${
                filterType === type
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {type === 'all' ? 'All' : type === 'stage_change' ? 'Stage' : type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>
      )}

      {/* Timeline */}
      <div className="relative">
        {displayActivities.length === 0 ? (
          <p className="text-gray-500 text-sm py-4">{t('platform.salesPipeline.activities.noActivitiesYet', 'No activities yet')}</p>
        ) : (
          <div className="border-l-2 border-gray-200 ml-3 space-y-4">
            {displayActivities.map((activity) => (
              <div key={activity.id} className="relative pl-6">
                {/* Timeline dot */}
                <div className={`absolute -left-[9px] w-4 h-4 rounded-full border-2 ${getActivityColor(activity.type)}`}>
                  <span className="sr-only">{activity.type}</span>
                </div>

                {/* Activity card */}
                <div
                  className={`bg-white rounded-lg border p-3 ${activity.completed ? 'opacity-60' : ''} ${
                    activity.type === 'email' && !activity.opportunityId
                      ? 'border-orange-300 bg-orange-50/30 cursor-pointer hover:border-orange-400'
                      : ''
                  }`}
                  onClick={() => {
                    if (activity.type === 'email' && !activity.opportunityId && onActivityClick) {
                      onActivityClick(activity);
                    }
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-1.5 rounded ${getActivityColor(activity.type)}`}>
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <h5 className={`font-medium text-sm truncate ${activity.completed ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                            {activity.title}
                          </h5>
                          {activity.type === 'email' && !activity.opportunityId && (
                            <span className="flex-shrink-0 px-2 py-0.5 text-[10px] font-medium bg-orange-100 text-orange-600 rounded-full">
                              Unassigned
                            </span>
                          )}
                          {activity.type === 'email' && activity.opportunityId && (
                            <span className="flex-shrink-0 px-2 py-0.5 text-[10px] font-medium bg-green-100 text-green-600 rounded-full">
                              Linked
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {activity.priority === 'high' && !activity.completed && (
                            <AlertCircle className="h-3 w-3 text-red-500" />
                          )}
                          {onComplete && !activity.completed && (
                            <button
                              onClick={(e) => { e.stopPropagation(); onComplete(activity); }}
                              className="p-1 hover:bg-gray-100 rounded"
                              title={t('platform.salesPipeline.activities.markComplete', 'Mark complete')}
                            >
                              <CheckCircle className="h-3 w-3 text-gray-400 hover:text-green-500" />
                            </button>
                          )}
                        </div>
                      </div>
                      {activity.description && (
                        <p className="text-xs text-gray-500 mt-1">{activity.description}</p>
                      )}
                      {/* Email metadata (from/to, direction) */}
                      {activity.type === 'email' && activity.metadata && (
                        <div className="mt-1.5 space-y-0.5 text-xs text-gray-500">
                          {activity.metadata.from_email && (
                            <p className="flex items-center gap-1">
                              <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                activity.direction === 'outbound'
                                  ? 'bg-blue-50 text-blue-600'
                                  : 'bg-green-50 text-green-600'
                              }`}>
                                {activity.direction === 'outbound' ? '→ Sent' : '← Received'}
                              </span>
                              <span className="text-gray-400">from</span> {activity.metadata.from_email}
                            </p>
                          )}
                          {activity.metadata.to_emails && activity.metadata.to_emails.length > 0 && (
                            <p className="truncate">
                              <span className="text-gray-400">to</span>{' '}
                              {activity.metadata.to_emails.join(', ')}
                            </p>
                          )}
                        </div>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                        {activity.contact && (
                          <span className="flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {activity.contact}
                          </span>
                        )}
                        {activity.opportunityId && opportunities && (
                          <span className="flex items-center gap-1 text-green-600">
                            <Target className="h-3 w-3" />
                            {opportunities.find(o => o.id === activity.opportunityId)?.name || 'Linked'}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {activity.activityDate
                            ? activity.activityDate.split('T')[0]
                            : activity.completedAt
                              ? `Completed ${activity.completedAt.split('T')[0]}`
                              : activity.dueDate || ''
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// TASKS VIEW COMPONENT
// ============================================================================

interface TasksViewProps {
  activities: Activity[];
  orgUsers: Array<{ id: number; name: string; email: string }>;
  onCreateTask: (task: any) => Promise<void>;
  onUpdateTask: (taskId: string, updates: any) => Promise<void>;
  onCompleteActivity: (activity: Activity) => void;
  opportunities: Opportunity[];
}

function TasksView({ activities, orgUsers, onCreateTask, onUpdateTask, onCompleteActivity, opportunities }: TasksViewProps) {
  const [taskFilter, setTaskFilter] = useState<'all' | 'my_tasks' | 'assigned'>('all');
  const [statusFilter, setStatusFilter] = useState<'open' | 'completed' | 'all'>('open');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTask, setEditingTask] = useState<Activity | null>(null);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    priority: 'medium' as 'high' | 'medium' | 'low',
    dueDate: '',
    assignedToUserId: undefined as number | undefined,
    opportunityId: '' as string,
  });
  const [saving, setSaving] = useState(false);

  const filteredTasks = activities.filter(task => {
    if (statusFilter === 'open' && (task.taskStatus === 'completed' || task.taskStatus === 'cancelled')) return false;
    if (statusFilter === 'completed' && task.taskStatus !== 'completed') return false;
    return true;
  });

  const statusIcon = (status?: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'in_progress': return <PlayCircle className="h-4 w-4 text-blue-500" />;
      case 'deferred': return <PauseCircle className="h-4 w-4 text-yellow-500" />;
      case 'cancelled': return <XCircle className="h-4 w-4 text-gray-400" />;
      default: return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const priorityColor = (p?: string) => {
    switch (p) {
      case 'high': return 'text-red-600 bg-red-50';
      case 'medium': return 'text-amber-600 bg-amber-50';
      case 'low': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const handleCreate = async () => {
    if (!newTask.title.trim()) return;
    setSaving(true);
    try {
      await onCreateTask({
        title: newTask.title,
        description: newTask.description,
        priority: newTask.priority,
        due_date: newTask.dueDate || undefined,
        assigned_to_user_id: newTask.assignedToUserId,
        opportunity_id: newTask.opportunityId || undefined,
      });
      setShowCreateModal(false);
      setNewTask({ title: '', description: '', priority: 'medium', dueDate: '', assignedToUserId: undefined, opportunityId: '' });
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (task: Activity, newStatus: string) => {
    if (newStatus === 'completed') {
      onCompleteActivity(task);
    } else {
      await onUpdateTask(task.id, { task_status: newStatus });
    }
  };

  const handleEditSave = async () => {
    if (!editingTask) return;
    setSaving(true);
    try {
      await onUpdateTask(editingTask.id, {
        title: editingTask.title,
        description: editingTask.description || '',
        priority: editingTask.priority,
        due_date: editingTask.dueDate || null,
        assigned_to_user_id: editingTask.assignedToUserId || null,
        opportunity_id: editingTask.opportunityId || null,
        task_status: editingTask.taskStatus || 'not_started',
      });
      setEditingTask(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Task filters and create button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {(['open', 'completed', 'all'] as const).map(f => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                statusFilter === f ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f === 'open' ? 'Open' : f === 'completed' ? 'Completed' : 'All'}
            </button>
          ))}
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-3.5 w-3.5" />
          New Task
        </button>
      </div>

      {/* Task list */}
      {filteredTasks.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <ListChecks className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No {statusFilter === 'open' ? 'open' : statusFilter === 'completed' ? 'completed' : ''} tasks</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredTasks.map(task => (
            <div
              key={task.id}
              className={`flex items-start gap-3 p-3 rounded-lg border transition-colors hover:bg-gray-50 ${
                task.taskStatus === 'completed' ? 'border-gray-100 bg-gray-50' : 'border-gray-200'
              }`}
            >
              {/* Status toggle */}
              <button
                onClick={() => handleStatusChange(task, task.taskStatus === 'completed' ? 'not_started' : 'completed')}
                className="mt-0.5 flex-shrink-0"
                title={task.taskStatus === 'completed' ? 'Reopen task' : 'Complete task'}
              >
                {statusIcon(task.taskStatus)}
              </button>

              {/* Task content — click to edit */}
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setEditingTask({ ...task })}>
                <div className="flex items-center gap-2">
                  <p className={`text-sm font-medium ${task.taskStatus === 'completed' ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                    {task.title}
                  </p>
                  <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${priorityColor(task.priority)}`}>
                    {task.priority}
                  </span>
                </div>
                {task.description && (
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{task.description}</p>
                )}
                <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-400">
                  {task.assignedToName && (
                    <span className="flex items-center gap-1">
                      <UserPlus className="h-3 w-3" />
                      {task.assignedToName}
                    </span>
                  )}
                  {task.dueDate && (
                    <span className={`flex items-center gap-1 ${
                      new Date(task.dueDate) < new Date() && task.taskStatus !== 'completed' ? 'text-red-500' : ''
                    }`}>
                      <Calendar className="h-3 w-3" />
                      {new Date(task.dueDate).toLocaleDateString()}
                    </span>
                  )}
                  {task.opportunityId && (
                    <span className="flex items-center gap-1 text-blue-500">
                      <Target className="h-3 w-3" />
                      {opportunities.find(o => o.id === task.opportunityId)?.name || 'Linked'}
                    </span>
                  )}
                  {task.sourceActivityId && (
                    <span className="flex items-center gap-1 text-purple-500">
                      <Mail className="h-3 w-3" />
                      From email
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <select
                  value={task.taskStatus || 'not_started'}
                  onChange={(e) => handleStatusChange(task, e.target.value)}
                  className="text-xs border border-gray-200 rounded px-1.5 py-1 bg-white text-gray-600"
                >
                  <option value="not_started">Not Started</option>
                  <option value="in_progress">In Progress</option>
                  <option value="deferred">Deferred</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Task Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Create Task</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={newTask.title}
                  onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Task title..."
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                  placeholder="Task description..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={newTask.priority}
                    onChange={(e) => setNewTask(prev => ({ ...prev, priority: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    value={newTask.dueDate}
                    onChange={(e) => setNewTask(prev => ({ ...prev, dueDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
                <select
                  value={newTask.assignedToUserId || ''}
                  onChange={(e) => setNewTask(prev => ({ ...prev, assignedToUserId: e.target.value ? Number(e.target.value) : undefined }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">Myself</option>
                  {orgUsers.map(u => (
                    <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Link to Opportunity</label>
                <select
                  value={newTask.opportunityId}
                  onChange={(e) => setNewTask(prev => ({ ...prev, opportunityId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">None</option>
                  {opportunities.map(o => (
                    <option key={o.id} value={o.id}>{o.name} - {o.company}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!newTask.title.trim() || saving}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Create Task
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Task Modal */}
      {editingTask && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Edit Task</h3>
              <button onClick={() => setEditingTask(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={editingTask.title}
                  onChange={(e) => setEditingTask(prev => prev ? { ...prev, title: e.target.value } : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={editingTask.description || ''}
                  onChange={(e) => setEditingTask(prev => prev ? { ...prev, description: e.target.value } : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                  placeholder="Add a description..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={editingTask.priority}
                    onChange={(e) => setEditingTask(prev => prev ? { ...prev, priority: e.target.value as any } : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    value={editingTask.dueDate ? editingTask.dueDate.split('T')[0] : ''}
                    onChange={(e) => setEditingTask(prev => prev ? { ...prev, dueDate: e.target.value } : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
                <select
                  value={editingTask.assignedToUserId || ''}
                  onChange={(e) => setEditingTask(prev => prev ? { ...prev, assignedToUserId: e.target.value ? Number(e.target.value) : undefined, assignedToName: orgUsers.find(u => u.id === Number(e.target.value))?.name } : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">Myself</option>
                  {orgUsers.map(u => (
                    <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Link to Opportunity</label>
                <select
                  value={editingTask.opportunityId || ''}
                  onChange={(e) => setEditingTask(prev => prev ? { ...prev, opportunityId: e.target.value || undefined } : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">None</option>
                  {opportunities.map(o => (
                    <option key={o.id} value={o.id}>{o.name} - {o.company}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={editingTask.taskStatus || 'not_started'}
                  onChange={(e) => setEditingTask(prev => prev ? { ...prev, taskStatus: e.target.value as any } : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="not_started">Not Started</option>
                  <option value="in_progress">In Progress</option>
                  <option value="deferred">Deferred</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setEditingTask(null)}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleEditSave}
                disabled={!editingTask.title.trim() || saving}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface ActivitiesTabProps {
  activities: Activity[];
  opportunities: Opportunity[];
  onScheduleActivity: () => void;
  onCompleteActivity: (activity: Activity) => void;
  onAddNote: () => void;
  onOpenEmailIntegration?: () => void;
  onAssignActivity: (activityId: string, opportunityId: string) => void;
  onCreateOpportunityFromEmail: (activity: Activity) => void;
  onAutoAssociate: () => void;
  onDeleteActivities: (ids: string[]) => void;
  onArchiveActivities: (ids: string[]) => void;
  onSyncEmails: () => Promise<void>;
  onCreateTask: (task: any) => Promise<void>;
  onUpdateTask: (taskId: string, updates: any) => Promise<void>;
  onCreateTaskFromEmail: (activity: Activity) => void;
  orgUsers: Array<{ id: number; name: string; email: string }>;
  onRefreshActivities: () => void;
  onAssignOrg?: (activityId: string, organizationId: number) => void;
  onToggleShare?: (activityId: string, share: boolean) => void;
}

function ActivitiesTab({ activities, opportunities, onScheduleActivity, onCompleteActivity, onAddNote, onOpenEmailIntegration, onAssignActivity, onCreateOpportunityFromEmail, onAutoAssociate, onDeleteActivities, onArchiveActivities, onSyncEmails, onCreateTask, onUpdateTask, onCreateTaskFromEmail, orgUsers, onRefreshActivities, onAssignOrg, onToggleShare }: ActivitiesTabProps) {
  const { t } = useAppTranslation();
  const [activityFilter, setActivityFilter] = useState<'all' | 'email' | 'call' | 'meeting' | 'task'>('all');
  const [directionFilter, setDirectionFilter] = useState<'all' | 'inbound' | 'outbound'>('all');
  const [assignmentFilter, setAssignmentFilter] = useState<'all' | 'assigned' | 'unassigned'>('all');
  const [completedFilter, setCompletedFilter] = useState<'all' | 'open' | 'completed'>('open');
  const [selectedEmailActivity, setSelectedEmailActivity] = useState<Activity | null>(null);
  const [activeSubTab, setActiveSubTab] = useState<'inbox' | 'tasks' | 'calendar' | 'timeline'>('inbox');
  const [createTaskFromActivity, setCreateTaskFromActivity] = useState<Activity | null>(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);

  // Email integration status
  const [emailIntegrations, setEmailIntegrations] = useState<Array<{
    id: number; provider: string; connection_type?: string; email_address?: string;
    is_active: boolean; sync_enabled: boolean; last_sync_at: string | null; last_sync_error?: string | null;
  }>>([]);

  useEffect(() => {
    apiClient.get('/api/crm-activities/email-integrations')
      .then(res => {
        const all = res.data?.integrations || [];
        setEmailIntegrations(all.filter((i: any) => i.is_active));
      })
      .catch(() => {});
  }, []);

  // Calculate stats
  const thisWeekActivities = activities.filter(a => {
    const actDate = new Date(a.createdAt || a.dueDate);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return actDate >= weekAgo;
  });

  const callsCount = thisWeekActivities.filter(a => a.type === 'call').length;
  const emailsCount = thisWeekActivities.filter(a => a.type === 'email').length;
  const meetingsCount = thisWeekActivities.filter(a => a.type === 'meeting').length;
  const tasksCount = thisWeekActivities.filter(a => a.type === 'task').length;

  const unassignedEmailCount = activities.filter(a => a.type === 'email' && !a.opportunityId).length;

  // Filter activities
  const completedCount = activities.filter(a => a.completed).length;
  const openCount = activities.filter(a => !a.completed).length;
  const filteredActivities = activities.filter(a => {
    if (activityFilter !== 'all' && a.type !== activityFilter) return false;
    if (directionFilter !== 'all' && a.direction && a.direction !== directionFilter) return false;
    if (assignmentFilter === 'assigned' && !a.opportunityId) return false;
    if (assignmentFilter === 'unassigned' && a.opportunityId) return false;
    if (completedFilter === 'open' && a.completed) return false;
    if (completedFilter === 'completed' && !a.completed) return false;
    return true;
  });

  // Separate auto-captured vs manual activities
  const autoActivities = filteredActivities.filter(a =>
    (a as any).metadata?.provider || (a as any).email_id || (a as any).calendar_event_id
  );
  const manualActivities = filteredActivities.filter(a =>
    !(a as any).metadata?.provider && !(a as any).email_id && !(a as any).calendar_event_id
  );

  // Due soon tasks
  const dueSoonItems = activities
    .filter(a => !a.completed && a.dueDate)
    .sort((a, b) => new Date(a.dueDate!).getTime() - new Date(b.dueDate!).getTime())
    .slice(0, 5);

  const overdueTasks = activities.filter(a =>
    !a.completed && a.type === 'task' && a.dueDate && new Date(a.dueDate) < new Date()
  ).length;

  return (
    <div className="space-y-4">
      {/* Top metrics strip — compact horizontal bar */}
      <div className="bg-white rounded-lg border border-gray-200 px-3 sm:px-4 py-3">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          {/* Week activity metrics */}
          <div className="flex flex-wrap items-center gap-3 sm:gap-6">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">This Week</span>
            <div className="flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5 text-blue-500" />
              <span className="text-sm font-semibold text-gray-900">{emailsCount}</span>
              <span className="text-xs text-gray-400">emails</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Phone className="h-3.5 w-3.5 text-green-500" />
              <span className="text-sm font-semibold text-gray-900">{callsCount}</span>
              <span className="text-xs text-gray-400">calls</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5 text-purple-500" />
              <span className="text-sm font-semibold text-gray-900">{meetingsCount}</span>
              <span className="text-xs text-gray-400">meetings</span>
            </div>
            <div className="flex items-center gap-1.5">
              <ListChecks className="h-3.5 w-3.5 text-amber-500" />
              <span className="text-sm font-semibold text-gray-900">{tasksCount}</span>
              <span className="text-xs text-gray-400">tasks</span>
            </div>
            {overdueTasks > 0 && (
              <span className="flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                <AlertCircle className="h-3 w-3" />
                {overdueTasks} overdue
              </span>
            )}
            {unassignedEmailCount > 0 && (
              <span className="flex items-center gap-1 text-xs font-medium text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                {unassignedEmailCount} unassigned
              </span>
            )}
          </div>

          {/* Right side: integration status + actions */}
          <div className="flex items-center gap-2">
            {/* Email integration indicator */}
            {onOpenEmailIntegration && (
              <button
                onClick={onOpenEmailIntegration}
                className={`flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-lg transition-colors ${
                  emailIntegrations.length > 0
                    ? 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100'
                    : 'bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100'
                }`}
                title={emailIntegrations.length > 0
                  ? `${emailIntegrations.map(i => i.email_address || i.provider).join(', ')} · Last synced ${emailIntegrations[0]?.last_sync_at ? new Date(emailIntegrations[0].last_sync_at).toLocaleTimeString() : 'never'}`
                  : 'Connect email'
                }
              >
                {emailIntegrations.length > 0 ? <CheckCircle className="h-3 w-3" /> : <Mail className="h-3 w-3" />}
                {emailIntegrations.length > 0 ? 'Connected' : 'Connect Email'}
              </button>
            )}
            <button
              onClick={onAddNote}
              className="flex items-center gap-1.5 px-2.5 py-1 text-xs border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50"
            >
              <Edit className="h-3 w-3" />
              Note
            </button>
            <button
              onClick={onScheduleActivity}
              className="flex items-center gap-1.5 px-3 py-1 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="h-3 w-3" />
              Log Activity
            </button>
          </div>
        </div>
      </div>

      {/* Full-width content area */}
      <div className="bg-white rounded-lg border border-gray-200">
        {/* Sub-tab navigation bar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-3 sm:px-4 py-2 border-b border-gray-200 gap-2">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5 overflow-x-auto w-full sm:w-auto">
            <button
              onClick={() => { setActiveSubTab('inbox'); setActivityFilter('email'); }}
              className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 text-sm rounded-md transition-colors whitespace-nowrap ${
                activeSubTab === 'inbox' ? 'bg-white text-gray-900 shadow-sm font-medium' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Mail className="h-4 w-4" />
              Email Inbox
              {activities.filter(a => a.type === 'email').length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-700 rounded-full">
                  {activities.filter(a => a.type === 'email').length}
                </span>
              )}
            </button>
            <button
              onClick={() => { setActiveSubTab('tasks'); setActivityFilter('task'); }}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-md transition-colors ${
                activeSubTab === 'tasks' ? 'bg-white text-gray-900 shadow-sm font-medium' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <ListChecks className="h-4 w-4" />
              Tasks
              {activities.filter(a => a.type === 'task' && a.taskStatus !== 'completed').length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-700 rounded-full">
                  {activities.filter(a => a.type === 'task' && a.taskStatus !== 'completed').length}
                </span>
              )}
            </button>
            <button
              onClick={() => { setActiveSubTab('calendar'); setActivityFilter('meeting'); }}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-md transition-colors ${
                activeSubTab === 'calendar' ? 'bg-white text-gray-900 shadow-sm font-medium' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Calendar className="h-4 w-4" />
              Calendar
              {(() => {
                const meetingEvents = activities.filter(a => a.type === 'meeting');
                const now = new Date();
                const pendingCount = meetingEvents.filter(ev => {
                  const meta = ev.metadata || {};
                  if (meta.is_organizer) return false;
                  const response = (meta as any).my_response || 'none';
                  if (response === 'accepted' || response === 'declined' || response === 'tentativelyAccepted') return false;
                  if (ev.activityDate && new Date(ev.activityDate) < new Date(now.getFullYear(), now.getMonth(), now.getDate())) return false;
                  return true;
                }).length;
                return (
                  <>
                    {pendingCount > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold bg-amber-100 text-amber-700 rounded-full animate-pulse">
                        {pendingCount} pending
                      </span>
                    )}
                    {meetingEvents.length > 0 && pendingCount === 0 && (
                      <span className="ml-1 px-1.5 py-0.5 text-[10px] font-medium bg-purple-100 text-purple-700 rounded-full">
                        {meetingEvents.length}
                      </span>
                    )}
                  </>
                );
              })()}
            </button>
            <button
              onClick={() => { setActiveSubTab('timeline'); setActivityFilter('all'); }}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-md transition-colors ${
                activeSubTab === 'timeline' ? 'bg-white text-gray-900 shadow-sm font-medium' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Clock className="h-4 w-4" />
              Timeline
            </button>
          </div>

          <span className="text-sm text-gray-500">
            {activeSubTab === 'inbox'
              ? `${activities.filter(a => a.type === 'email').length} emails`
              : activeSubTab === 'tasks'
              ? `${activities.filter(a => a.type === 'task').length} tasks`
              : activeSubTab === 'calendar'
              ? `${activities.filter(a => a.type === 'meeting').length} events`
              : `${filteredActivities.length} activities`
            }
          </span>
        </div>

        {/* Content area — full width */}
        <div className="p-4">
          {/* Email Inbox View */}
          {activeSubTab === 'inbox' && (
            <EmailInboxView
              activities={activities}
              opportunities={opportunities}
              onAssign={onAssignActivity}
              onCreateOpportunity={onCreateOpportunityFromEmail}
              onDelete={onDeleteActivities}
              onArchive={onArchiveActivities}
              onAutoAssociate={onAutoAssociate}
              onSyncEmails={onSyncEmails}
              onCreateTaskFromEmail={onCreateTaskFromEmail}
              onRefreshActivities={onRefreshActivities}
              onAssignOrg={onAssignOrg}
              onToggleShare={onToggleShare}
            />
          )}

          {/* Tasks View */}
          {activeSubTab === 'tasks' && (
            <TasksView
              activities={activities.filter(a => a.type === 'task')}
              orgUsers={orgUsers}
              onCreateTask={onCreateTask}
              onUpdateTask={onUpdateTask}
              onCompleteActivity={onCompleteActivity}
              opportunities={opportunities}
            />
          )}

          {/* Calendar Events View */}
          {activeSubTab === 'calendar' && (
            <CalendarEventsView
              activities={activities.filter(a => a.type === 'meeting')}
              opportunities={opportunities}
              onRefreshActivities={onRefreshActivities}
            />
          )}

          {/* Activity Timeline */}
          {activeSubTab === 'timeline' && (
            <ActivityTimeline
              activities={filteredActivities}
              onComplete={onCompleteActivity}
              onActivityClick={(activity) => {
                setSelectedEmailActivity(activity);
                setAssignModalOpen(true);
              }}
              opportunities={opportunities}
              showFilters={false}
            />
          )}
        </div>
      </div>

      {/* Due Soon — compact bottom strip (only if items exist) */}
      {dueSoonItems.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
          <div className="flex items-center gap-3 mb-2">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Due Soon</h4>
          </div>
          <div className="flex flex-wrap gap-2">
            {dueSoonItems.map(item => {
              const isOverdue = item.dueDate && new Date(item.dueDate) < new Date();
              return (
                <div
                  key={item.id}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm ${
                    isOverdue ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  {item.type === 'email' && <Mail className="h-3 w-3 text-blue-500" />}
                  {item.type === 'call' && <Phone className="h-3 w-3 text-green-500" />}
                  {item.type === 'meeting' && <Calendar className="h-3 w-3 text-purple-500" />}
                  {item.type === 'task' && <ListChecks className="h-3 w-3 text-amber-500" />}
                  <span className="truncate max-w-[200px] text-gray-700">{item.title}</span>
                  <span className={`text-xs ${isOverdue ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                    {item.dueDate && new Date(item.dueDate).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Assign Email Modal */}
      <AssignEmailModal
        isOpen={assignModalOpen}
        onClose={() => { setAssignModalOpen(false); setSelectedEmailActivity(null); }}
        activity={selectedEmailActivity}
        opportunities={opportunities}
        onAssign={(activityId, opportunityId) => {
          onAssignActivity(activityId, opportunityId);
          setAssignModalOpen(false);
          setSelectedEmailActivity(null);
        }}
        onCreateOpportunity={(activity) => {
          setAssignModalOpen(false);
          setSelectedEmailActivity(null);
          onCreateOpportunityFromEmail(activity);
        }}
      />
    </div>
  );
}

interface ForecastsTabProps {
  opportunities: Opportunity[];
  pipeline: Pipeline;
}

// Helper function to generate time period options based on current date
function getTimePeriodOptions(): { value: string; label: string }[] {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentQuarter = Math.ceil((now.getMonth() + 1) / 3);

  const options: { value: string; label: string }[] = [];

  // Full year options
  options.push({ value: `FY ${currentYear}`, label: `Full Year ${currentYear}` });
  options.push({ value: `FY ${currentYear + 1}`, label: `Full Year ${currentYear + 1}` });

  // Quarter options: current quarter and next 3
  for (let i = 0; i < 4; i++) {
    let q = currentQuarter + i;
    let year = currentYear;
    if (q > 4) { q -= 4; year += 1; }
    options.push({ value: `Q${q} ${year}`, label: `Q${q} ${year}` });
  }

  return options;
}

function ForecastsTab({ opportunities, pipeline }: ForecastsTabProps) {
  const { t } = useAppTranslation();
  const timePeriodOptions = getTimePeriodOptions();
  const [selectedPeriod, setSelectedPeriod] = useState(timePeriodOptions[0]?.value || `FY ${new Date().getFullYear()}`);
  const [reportView, setReportView] = useState<'forecast' | 'performance' | 'analytics'>('forecast');

  const totalPipeline = opportunities.reduce((sum, opp) => sum + opp.amount, 0);
  const weightedPipeline = opportunities.reduce((sum, opp) => sum + (opp.amount * opp.probability / 100), 0);

  // Get active stages for forecast (not won/lost)
  const activeStages = pipeline.stages.filter(s => !s.isWon && !s.isLost);
  const wonStage = pipeline.stages.find(s => s.isWon);
  const lostStage = pipeline.stages.find(s => s.isLost);

  // Calculate win/loss metrics
  const wonDeals = opportunities.filter(o => o.stage === wonStage?.id);
  const lostDeals = opportunities.filter(o => o.stage === lostStage?.id);
  const closedDeals = wonDeals.length + lostDeals.length;
  const winRate = closedDeals > 0 ? Math.round((wonDeals.length / closedDeals) * 100) : 0;
  const avgDealSize = wonDeals.length > 0
    ? wonDeals.reduce((sum, o) => sum + o.amount, 0) / wonDeals.length
    : 0;

  // Deal velocity (average days to close - simulated)
  const avgCycleDays = 45;

  // Pipeline coverage (pipeline / quota)
  const quarterlyQuota = 150000;
  const pipelineCoverage = Math.round((totalPipeline / quarterlyQuota) * 100);

  // Deal aging analysis
  const getDealAge = (opp: Opportunity) => {
    if (!opp.createdAt) return 0;
    const created = new Date(opp.createdAt);
    const now = new Date();
    return Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));
  };

  const agingBuckets = [
    { label: '0-30 days', min: 0, max: 30, color: 'bg-green-500' },
    { label: '31-60 days', min: 31, max: 60, color: 'bg-yellow-500' },
    { label: '61-90 days', min: 61, max: 90, color: 'bg-orange-500' },
    { label: '90+ days', min: 91, max: Infinity, color: 'bg-red-500' },
  ];

  const openOpportunities = opportunities.filter(o => {
    const stage = pipeline.stages.find(s => s.id === o.stage);
    return !stage?.isWon && !stage?.isLost;
  });

  const dealsByAge = agingBuckets.map(bucket => ({
    ...bucket,
    count: openOpportunities.filter(o => {
      const age = getDealAge(o);
      return age >= bucket.min && age <= bucket.max;
    }).length,
    value: openOpportunities
      .filter(o => {
        const age = getDealAge(o);
        return age >= bucket.min && age <= bucket.max;
      })
      .reduce((sum, o) => sum + o.amount, 0),
  }));

  // Sales rep performance
  // NOTE: Sales reps should be configured through Organization Settings > Team Management
  // This is calculated from opportunity owners assigned to deals
  const salesReps = opportunities.reduce((acc, opp) => {
    if (!opp.owner) return acc;
    const existing = acc.find(r => r.name === opp.owner);
    const isWon = pipeline.stages.find(s => s.id === opp.stage)?.isWon;
    if (existing) {
      existing.deals += 1;
      existing.value += opp.amount;
      if (isWon) existing.won += 1;
    } else {
      acc.push({
        name: opp.owner,
        deals: 1,
        value: opp.amount,
        quota: 100000, // Default quota - should come from organization settings
        won: isWon ? 1 : 0,
      });
    }
    return acc;
  }, [] as Array<{ name: string; deals: number; value: number; quota: number; won: number }>);

  // Monthly projections based on selected period (full year or quarter)
  const getMonthsForPeriod = (periodStr: string): string[] => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    if (periodStr.startsWith('FY')) {
      return monthNames;
    }
    const quarterNum = parseInt(periodStr.charAt(1), 10);
    const startMonth = (quarterNum - 1) * 3;
    return monthNames.slice(startMonth, startMonth + 3);
  };

  const isFullYear = selectedPeriod.startsWith('FY');
  const periodMonths = getMonthsForPeriod(selectedPeriod);
  const monthCount = periodMonths.length;
  const monthlyProjections = periodMonths.map((month, index) => {
    // Calculate projections based on weighted pipeline distributed across months
    // Nearer months get higher confidence, further months get lower
    const monthFactor = isFullYear
      ? Math.max(0.3, 1 - (index * 0.06)) // Gradual decline over 12 months
      : (index + 1) / 3; // Increasing confidence within a quarter
    const baseValue = weightedPipeline / monthCount;
    return {
      month,
      committed: Math.round(baseValue * 0.7 * monthFactor),
      bestCase: Math.round(baseValue * 1.1 * monthFactor),
      worstCase: Math.round(baseValue * 0.5 * monthFactor),
    };
  });

  return (
    <div className="space-y-6">
      {/* Header with View Selector */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{t('platform.salesPipeline.forecasts.title', 'Sales Reports & Forecasts')}</h3>
          <p className="text-sm text-gray-600">{t('platform.salesPipeline.forecasts.description', 'Comprehensive view of sales performance and projections')}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-gray-100 rounded-lg p-1">
            {[
              { id: 'forecast', label: t('platform.salesPipeline.forecasts.views.forecast', 'Forecast') },
              { id: 'performance', label: t('platform.salesPipeline.forecasts.views.performance', 'Performance') },
              { id: 'analytics', label: t('platform.salesPipeline.forecasts.views.analytics', 'Analytics') },
            ].map((view) => (
              <button
                key={view.id}
                onClick={() => setReportView(view.id as typeof reportView)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  reportView === view.id
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {view.label}
              </button>
            ))}
          </div>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            {timePeriodOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <ExportMenu
            onDownloadCsv={() => {
              if (reportView === 'forecast') {
                downloadForecastCsv({
                  pipelineName: pipeline.name,
                  period: selectedPeriod,
                  totalPipeline,
                  weightedPipeline,
                  pipelineCoverage,
                  wonRevenue: wonDeals.reduce((s, o) => s + o.amount, 0),
                  winRate,
                  wonDealsCount: wonDeals.length,
                  monthlyProjections,
                  forecastByStage: activeStages.map(stage => {
                    const stageOpps = opportunities.filter(o => o.stage === stage.id);
                    const stageTotal = stageOpps.reduce((s, o) => s + o.amount, 0);
                    return {
                      name: stage.name,
                      probability: stage.probability,
                      deals: stageOpps.length,
                      total: stageTotal,
                      weighted: stageTotal * stage.probability / 100,
                      percentage: totalPipeline > 0 ? (stageTotal / totalPipeline) * 100 : 0,
                    };
                  }),
                  wonDeals: wonDeals.map(o => ({ name: o.name, amount: o.amount })),
                  lostDeals: lostDeals.map(o => ({ name: o.name, amount: o.amount })),
                  wonStageName: wonStage?.name,
                  lostStageName: lostStage?.name,
                });
              } else if (reportView === 'performance') {
                downloadPerformanceCsv({
                  pipelineName: pipeline.name,
                  period: selectedPeriod,
                  winRate,
                  wonDealsCount: wonDeals.length,
                  closedDealsCount: closedDeals,
                  avgDealSize,
                  avgCycleDays,
                  wonRevenue: wonDeals.reduce((s, o) => s + o.amount, 0),
                  salesReps,
                  wonDeals: wonDeals.map(o => ({ name: o.name, company: o.company, owner: o.owner, amount: o.amount, source: o.source })),
                });
              } else {
                const atRisk = dealsByAge.filter(b => b.min >= 61);
                downloadAnalyticsCsv({
                  pipelineName: pipeline.name,
                  period: selectedPeriod,
                  dealsByAge: dealsByAge.map(b => ({ label: b.label, count: b.count, value: b.value, min: b.min })),
                  atRiskCount: atRisk.reduce((s, b) => s + b.count, 0),
                  atRiskValue: atRisk.reduce((s, b) => s + b.value, 0),
                  winCount: wonDeals.length,
                  wonRevenue: wonDeals.reduce((s, o) => s + o.amount, 0),
                  avgWonDealSize: avgDealSize,
                  winRate,
                  lossCount: lostDeals.length,
                  lostRevenue: lostDeals.reduce((s, o) => s + o.amount, 0),
                  avgLostDealSize: lostDeals.length > 0 ? lostDeals.reduce((s, o) => s + o.amount, 0) / lostDeals.length : 0,
                  lossRate: closedDeals > 0 ? 100 - winRate : 0,
                  healthScores: [
                    { label: 'Coverage', score: Math.min(100, Math.round(pipelineCoverage / 3)), target: '3x quota', status: pipelineCoverage >= 300 ? 'healthy' : 'warning' },
                    { label: 'Velocity', score: avgCycleDays <= 45 ? 85 : avgCycleDays <= 60 ? 60 : 40, target: '<45 days', status: avgCycleDays <= 45 ? 'healthy' : 'warning' },
                    { label: 'Win Rate', score: winRate, target: '>40%', status: winRate >= 40 ? 'healthy' : 'warning' },
                    { label: 'Activity', score: 75, target: 'Active engagement', status: 'healthy' },
                  ],
                });
              }
            }}
            onPrint={() => {
              if (reportView === 'forecast') {
                printForecastView({
                  pipelineName: pipeline.name,
                  period: selectedPeriod,
                  totalPipeline,
                  weightedPipeline,
                  pipelineCoverage,
                  wonRevenue: wonDeals.reduce((s, o) => s + o.amount, 0),
                  winRate,
                  wonDealsCount: wonDeals.length,
                  monthlyProjections,
                  forecastByStage: activeStages.map(stage => {
                    const stageOpps = opportunities.filter(o => o.stage === stage.id);
                    const stageTotal = stageOpps.reduce((s, o) => s + o.amount, 0);
                    return {
                      name: stage.name,
                      probability: stage.probability,
                      deals: stageOpps.length,
                      total: stageTotal,
                      weighted: stageTotal * stage.probability / 100,
                      percentage: totalPipeline > 0 ? (stageTotal / totalPipeline) * 100 : 0,
                    };
                  }),
                  wonDeals: wonDeals.map(o => ({ name: o.name, amount: o.amount })),
                  lostDeals: lostDeals.map(o => ({ name: o.name, amount: o.amount })),
                  wonStageName: wonStage?.name,
                  lostStageName: lostStage?.name,
                });
              } else if (reportView === 'performance') {
                printPerformanceView({
                  pipelineName: pipeline.name,
                  period: selectedPeriod,
                  winRate,
                  wonDealsCount: wonDeals.length,
                  closedDealsCount: closedDeals,
                  avgDealSize,
                  avgCycleDays,
                  wonRevenue: wonDeals.reduce((s, o) => s + o.amount, 0),
                  salesReps,
                  wonDeals: wonDeals.map(o => ({ name: o.name, company: o.company, owner: o.owner, amount: o.amount, source: o.source })),
                });
              } else {
                const atRisk = dealsByAge.filter(b => b.min >= 61);
                printAnalyticsView({
                  pipelineName: pipeline.name,
                  period: selectedPeriod,
                  dealsByAge: dealsByAge.map(b => ({ label: b.label, count: b.count, value: b.value, min: b.min })),
                  atRiskCount: atRisk.reduce((s, b) => s + b.count, 0),
                  atRiskValue: atRisk.reduce((s, b) => s + b.value, 0),
                  winCount: wonDeals.length,
                  wonRevenue: wonDeals.reduce((s, o) => s + o.amount, 0),
                  avgWonDealSize: avgDealSize,
                  winRate,
                  lossCount: lostDeals.length,
                  lostRevenue: lostDeals.reduce((s, o) => s + o.amount, 0),
                  avgLostDealSize: lostDeals.length > 0 ? lostDeals.reduce((s, o) => s + o.amount, 0) / lostDeals.length : 0,
                  lossRate: closedDeals > 0 ? 100 - winRate : 0,
                  healthScores: [
                    { label: 'Coverage', score: Math.min(100, Math.round(pipelineCoverage / 3)), target: '3x quota', status: pipelineCoverage >= 300 ? 'healthy' : 'warning' },
                    { label: 'Velocity', score: avgCycleDays <= 45 ? 85 : avgCycleDays <= 60 ? 60 : 40, target: '<45 days', status: avgCycleDays <= 45 ? 'healthy' : 'warning' },
                    { label: 'Win Rate', score: winRate, target: '>40%', status: winRate >= 40 ? 'healthy' : 'warning' },
                    { label: 'Activity', score: 75, target: 'Active engagement', status: 'healthy' },
                  ],
                });
              }
            }}
            printLabel={
              reportView === 'forecast' ? t('platform.salesPipeline.export.printForecast', 'Print Forecast')
              : reportView === 'performance' ? t('platform.salesPipeline.export.printPerformance', 'Print Performance')
              : t('platform.salesPipeline.export.printAnalytics', 'Print Analytics')
            }
          />
        </div>
      </div>

      {/* Forecast View */}
      {reportView === 'forecast' && (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <p className="text-sm text-gray-600">{t('platform.salesPipeline.forecasts.totalPipeline', 'Total Pipeline')}</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{formatCurrency(totalPipeline)}</p>
              <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                <TrendingUp className="h-3 w-3" />
                {t('platform.salesPipeline.forecasts.fromLastQuarter', '+15% from last quarter')}
              </p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <p className="text-sm text-gray-600">{t('platform.salesPipeline.forecasts.weightedPipeline', 'Weighted Pipeline')}</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{formatCurrency(weightedPipeline)}</p>
              <p className="text-sm text-gray-500 mt-2">{t('platform.salesPipeline.forecasts.basedOnProbability', 'Based on probability')}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <p className="text-sm text-gray-600">{t('platform.salesPipeline.forecasts.pipelineCoverage', 'Pipeline Coverage')}</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{pipelineCoverage}%</p>
              <p className={`text-sm mt-2 ${pipelineCoverage >= 300 ? 'text-green-600' : pipelineCoverage >= 200 ? 'text-yellow-600' : 'text-red-600'}`}>
                {pipelineCoverage >= 300 ? t('platform.salesPipeline.forecasts.healthy', 'Healthy') : pipelineCoverage >= 200 ? t('platform.salesPipeline.forecasts.atRisk', 'At Risk') : t('platform.salesPipeline.forecasts.critical', 'Critical')} (3x {t('platform.salesPipeline.forecasts.target', 'target')})
              </p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <p className="text-sm text-gray-600">{t('platform.salesPipeline.forecasts.wonRevenue', 'Won Revenue')}</p>
              <p className="text-3xl font-bold text-green-700 mt-2">
                {formatCurrency(wonDeals.reduce((sum, o) => sum + o.amount, 0))}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                {wonDeals.length} {t('platform.salesPipeline.common.deals', 'deals')} | {winRate}% {t('platform.salesPipeline.forecasts.winRate', 'win rate')}
              </p>
            </div>
          </div>

          {/* Monthly Projections */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
              <Calendar className="h-5 w-5 text-gray-400" />
              {t('platform.salesPipeline.forecasts.monthlyRevenueProjections', 'Monthly Revenue Projections')}
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-600">{t('platform.salesPipeline.forecasts.month', 'Month')}</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-600">{t('platform.salesPipeline.forecasts.committed', 'Committed')}</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-600">{t('platform.salesPipeline.forecasts.bestCase', 'Best Case')}</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-600">{t('platform.salesPipeline.forecasts.worstCase', 'Worst Case')}</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-600">{t('platform.salesPipeline.forecasts.confidence', 'Confidence')}</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyProjections.map((proj) => (
                    <tr key={proj.month} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium text-gray-900">{proj.month}</td>
                      <td className="py-3 px-4 text-right text-gray-900">{formatCurrency(proj.committed)}</td>
                      <td className="py-3 px-4 text-right text-green-600">{formatCurrency(proj.bestCase)}</td>
                      <td className="py-3 px-4 text-right text-orange-600">{formatCurrency(proj.worstCase)}</td>
                      <td className="py-3 px-4 text-center">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                          {Math.round((proj.committed / proj.bestCase) * 100)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 font-medium">
                    <td className="py-3 px-4 text-gray-900">{selectedPeriod.startsWith('FY') ? 'Annual' : selectedPeriod} Total</td>
                    <td className="py-3 px-4 text-right text-gray-900">
                      {formatCurrency(monthlyProjections.reduce((s, p) => s + p.committed, 0))}
                    </td>
                    <td className="py-3 px-4 text-right text-green-600">
                      {formatCurrency(monthlyProjections.reduce((s, p) => s + p.bestCase, 0))}
                    </td>
                    <td className="py-3 px-4 text-right text-orange-600">
                      {formatCurrency(monthlyProjections.reduce((s, p) => s + p.worstCase, 0))}
                    </td>
                    <td className="py-3 px-4 text-center">-</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Forecast by Stage */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="font-medium text-gray-900 mb-4">{t('platform.salesPipeline.forecasts.forecastByStage', 'Forecast by Stage')}</h4>
            <div className="space-y-4">
              {activeStages.map((stage) => {
                const stageOpps = opportunities.filter(o => o.stage === stage.id);
                const stageTotal = stageOpps.reduce((sum, o) => sum + o.amount, 0);
                const percentage = totalPipeline > 0 ? (stageTotal / totalPipeline) * 100 : 0;

                return (
                  <div key={stage.id}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className={`w-3 h-3 rounded-full ${stage.color}`}></span>
                        <span className="text-sm font-medium text-gray-700">{stage.name}</span>
                        <span className="text-xs text-gray-400">({stage.probability}% prob)</span>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                          {stageOpps.length} deals
                        </span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-gray-500">{formatCurrency(stageTotal)}</span>
                        <span className="text-xs text-gray-400">
                          Weighted: {formatCurrency(stageTotal * stage.probability / 100)}
                        </span>
                      </div>
                    </div>
                    <div className="w-full h-3 bg-gray-200 rounded-full">
                      <div
                        className={`h-full ${stage.color.replace('bg-', 'bg-').replace('-100', '-500')} rounded-full`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Won/Lost Summary in Forecast View */}
          {(wonDeals.length > 0 || lostDeals.length > 0) && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="font-medium text-gray-900 mb-4">{t('platform.salesPipeline.forecasts.closedDeals', 'Closed Deals')}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {wonStage && (
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-green-500"></span>
                        <span className="text-sm font-medium text-green-800">{wonStage.name}</span>
                      </div>
                      <span className="text-sm font-bold text-green-700">{wonDeals.length} {t('platform.salesPipeline.common.deals', 'deals')}</span>
                    </div>
                    <p className="text-2xl font-bold text-green-700">{formatCurrency(wonDeals.reduce((sum, o) => sum + o.amount, 0))}</p>
                    <p className="text-xs text-green-600 mt-1">{t('platform.salesPipeline.forecasts.winRate', 'Win Rate')}: {winRate}%</p>
                    {wonDeals.length > 0 && (
                      <div className="mt-3 space-y-1">
                        {wonDeals.slice(0, 5).map(opp => (
                          <div key={opp.id} className="flex justify-between text-xs text-green-700">
                            <span className="truncate mr-2">{opp.name}</span>
                            <span className="font-medium flex-shrink-0">{formatCurrency(opp.amount)}</span>
                          </div>
                        ))}
                        {wonDeals.length > 5 && (
                          <p className="text-xs text-green-600">+{wonDeals.length - 5} {t('platform.salesPipeline.common.more', 'more')}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {lostStage && (
                  <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-red-500"></span>
                        <span className="text-sm font-medium text-red-800">{lostStage.name}</span>
                      </div>
                      <span className="text-sm font-bold text-red-700">{lostDeals.length} {t('platform.salesPipeline.common.deals', 'deals')}</span>
                    </div>
                    <p className="text-2xl font-bold text-red-700">{formatCurrency(lostDeals.reduce((sum, o) => sum + o.amount, 0))}</p>
                    <p className="text-xs text-red-600 mt-1">{t('platform.salesPipeline.forecasts.lossRate', 'Loss Rate')}: {closedDeals > 0 ? 100 - winRate : 0}%</p>
                    {lostDeals.length > 0 && (
                      <div className="mt-3 space-y-1">
                        {lostDeals.slice(0, 5).map(opp => (
                          <div key={opp.id} className="flex justify-between text-xs text-red-700">
                            <span className="truncate mr-2">{opp.name}</span>
                            <span className="font-medium flex-shrink-0">{formatCurrency(opp.amount)}</span>
                          </div>
                        ))}
                        {lostDeals.length > 5 && (
                          <p className="text-xs text-red-600">+{lostDeals.length - 5} {t('platform.salesPipeline.common.more', 'more')}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Performance View */}
      {reportView === 'performance' && (
        <>
          {/* Performance Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <p className="text-sm text-gray-600">Win Rate</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{winRate}%</p>
              <p className="text-sm text-gray-500 mt-2">{wonDeals.length} won / {closedDeals} closed</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <p className="text-sm text-gray-600">Avg Deal Size</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{formatCurrency(avgDealSize)}</p>
              <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                <TrendingUp className="h-3 w-3" />
                +8% vs last quarter
              </p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <p className="text-sm text-gray-600">Avg Sales Cycle</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{avgCycleDays} days</p>
              <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                <TrendingDown className="h-3 w-3" />
                -5 days vs last quarter
              </p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <p className="text-sm text-gray-600">Total Won Revenue</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {formatCurrency(wonDeals.reduce((sum, o) => sum + o.amount, 0))}
              </p>
              <p className="text-sm text-gray-500 mt-2">{wonDeals.length} closed deals</p>
            </div>
          </div>

          {/* Sales Rep Performance Table */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
              <Users className="h-5 w-5 text-gray-400" />
              Sales Rep Performance
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Rep</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-600">Open Deals</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-600">Won</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-600">Win Rate</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-600">Pipeline Value</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-600">Quota</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-600">Attainment</th>
                  </tr>
                </thead>
                <tbody>
                  {salesReps.map((rep) => {
                    const attainment = Math.round((rep.value / rep.quota) * 100);
                    return (
                      <tr key={rep.name} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium text-gray-900">{rep.name}</td>
                        <td className="py-3 px-4 text-right text-gray-600">{rep.deals}</td>
                        <td className="py-3 px-4 text-right text-gray-600">{rep.won}</td>
                        <td className="py-3 px-4 text-right text-gray-600">{Math.round((rep.won / rep.deals) * 100)}%</td>
                        <td className="py-3 px-4 text-right text-gray-900">{formatCurrency(rep.value)}</td>
                        <td className="py-3 px-4 text-right text-gray-600">{formatCurrency(rep.quota)}</td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-24 h-2 bg-gray-200 rounded-full">
                              <div
                                className={`h-full rounded-full ${attainment >= 100 ? 'bg-green-500' : attainment >= 75 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                style={{ width: `${Math.min(attainment, 100)}%` }}
                              />
                            </div>
                            <span className={`text-sm font-medium ${attainment >= 100 ? 'text-green-600' : attainment >= 75 ? 'text-yellow-600' : 'text-red-600'}`}>
                              {attainment}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Won Deals Detail */}
          {wonDeals.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                {t('platform.salesPipeline.forecasts.wonDeals', 'Won Deals')} — {selectedPeriod.startsWith('FY') ? selectedPeriod.replace('FY ', '') : selectedPeriod}
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Deal</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Company</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Owner</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-600">Amount</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wonDeals.map((deal) => (
                      <tr key={deal.id} className="border-b border-gray-100 hover:bg-green-50">
                        <td className="py-3 px-4 font-medium text-gray-900">{deal.name}</td>
                        <td className="py-3 px-4 text-gray-600">{deal.company}</td>
                        <td className="py-3 px-4 text-gray-600">{deal.owner}</td>
                        <td className="py-3 px-4 text-right font-medium text-green-700">{formatCurrency(deal.amount)}</td>
                        <td className="py-3 px-4 text-gray-500">{deal.source || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-green-50 font-medium">
                      <td colSpan={3} className="py-3 px-4 text-green-800">Total Won Revenue</td>
                      <td className="py-3 px-4 text-right text-green-800">{formatCurrency(wonDeals.reduce((sum, o) => sum + o.amount, 0))}</td>
                      <td className="py-3 px-4"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Analytics View */}
      {reportView === 'analytics' && (
        <>
          {/* Deal Aging Analysis */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="h-5 w-5 text-gray-400" />
              Deal Aging Analysis
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {dealsByAge.map((bucket) => (
                <div key={bucket.label} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`w-3 h-3 rounded-full ${bucket.color}`}></span>
                    <span className="text-sm font-medium text-gray-700">{bucket.label}</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{bucket.count} deals</p>
                  <p className="text-sm text-gray-600 mt-1">{formatCurrency(bucket.value)}</p>
                </div>
              ))}
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-yellow-800">At-Risk Deals</p>
                  <p className="text-sm text-yellow-700 mt-1">
                    {dealsByAge.filter(b => b.min >= 61).reduce((sum, b) => sum + b.count, 0)} deals have been open for over 60 days with {formatCurrency(dealsByAge.filter(b => b.min >= 61).reduce((sum, b) => sum + b.value, 0))} at stake.
                    Consider accelerating or re-qualifying these opportunities.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Win/Loss Analysis */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                Win Analysis
              </h4>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <span className="text-sm text-gray-700">Total Won Deals</span>
                  <span className="font-bold text-green-700">{wonDeals.length}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <span className="text-sm text-gray-700">Won Revenue</span>
                  <span className="font-bold text-green-700">{formatCurrency(wonDeals.reduce((sum, o) => sum + o.amount, 0))}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <span className="text-sm text-gray-700">Avg Won Deal Size</span>
                  <span className="font-bold text-green-700">{formatCurrency(avgDealSize)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <span className="text-sm text-gray-700">Win Rate</span>
                  <span className="font-bold text-green-700">{winRate}%</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                <X className="h-5 w-5 text-red-500" />
                Loss Analysis
              </h4>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                  <span className="text-sm text-gray-700">Total Lost Deals</span>
                  <span className="font-bold text-red-700">{lostDeals.length}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                  <span className="text-sm text-gray-700">Lost Revenue</span>
                  <span className="font-bold text-red-700">{formatCurrency(lostDeals.reduce((sum, o) => sum + o.amount, 0))}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                  <span className="text-sm text-gray-700">Avg Lost Deal Size</span>
                  <span className="font-bold text-red-700">
                    {formatCurrency(lostDeals.length > 0 ? lostDeals.reduce((sum, o) => sum + o.amount, 0) / lostDeals.length : 0)}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                  <span className="text-sm text-gray-700">Loss Rate</span>
                  <span className="font-bold text-red-700">{closedDeals > 0 ? 100 - winRate : 0}%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Pipeline Health Score */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-gray-400" />
              Pipeline Health Score
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[
                { label: 'Coverage', score: Math.min(100, Math.round(pipelineCoverage / 3)), target: '3x quota', status: pipelineCoverage >= 300 ? 'healthy' : 'warning' },
                { label: 'Velocity', score: avgCycleDays <= 45 ? 85 : avgCycleDays <= 60 ? 60 : 40, target: '<45 days', status: avgCycleDays <= 45 ? 'healthy' : 'warning' },
                { label: 'Win Rate', score: winRate, target: '>40%', status: winRate >= 40 ? 'healthy' : 'warning' },
                { label: 'Activity', score: 75, target: 'Active engagement', status: 'healthy' },
              ].map((metric) => (
                <div key={metric.label} className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">{metric.label}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      metric.status === 'healthy' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {metric.status === 'healthy' ? 'Healthy' : 'Needs Attention'}
                    </span>
                  </div>
                  <div className="flex items-end gap-2">
                    <span className="text-3xl font-bold text-gray-900">{metric.score}</span>
                    <span className="text-sm text-gray-500 mb-1">/ 100</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Target: {metric.target}</p>
                  <div className="w-full h-2 bg-gray-200 rounded-full mt-2">
                    <div
                      className={`h-full rounded-full ${metric.score >= 70 ? 'bg-green-500' : metric.score >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                      style={{ width: `${metric.score}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// PIPELINE SELECTOR COMPONENT
// ============================================================================

interface PipelineSelectorProps {
  pipelines: Pipeline[];
  activePipelineId: string;
  onSelectPipeline: (pipelineId: string) => void;
  onManagePipelines: () => void;
  onCreatePipeline: () => void;
}

function PipelineSelector({
  pipelines,
  activePipelineId,
  onSelectPipeline,
  onManagePipelines,
  onCreatePipeline,
}: PipelineSelectorProps) {
  const { t } = useAppTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const activePipeline = pipelines.find(p => p.id === activePipelineId) || pipelines[0];

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
      >
        <Target className="h-4 w-4 text-gray-500" />
        <span className="font-medium text-gray-900">{activePipeline?.name || t('platform.salesPipeline.pipeline.selectPipeline', 'Select Pipeline')}</span>
        <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
            <div className="p-2">
              <p className="px-3 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wide">
                {t('platform.salesPipeline.pipeline.pipelines', 'Pipelines')}
              </p>
              {pipelines.map((pipeline) => {
                const isActive = pipeline.id === activePipelineId;
                return (
                  <button
                    key={pipeline.id}
                    onClick={() => {
                      onSelectPipeline(pipeline.id);
                      setIsOpen(false);
                    }}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-left ${
                      isActive
                        ? 'bg-blue-50 text-blue-700'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <div>
                      <span className="font-medium">{pipeline.name}</span>
                      {pipeline.isDefault && (
                        <span className="ml-2 text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                          {t('platform.salesPipeline.pipeline.default', 'Default')}
                        </span>
                      )}
                      {pipeline.description && (
                        <p className="text-xs text-gray-500 mt-0.5">{pipeline.description}</p>
                      )}
                    </div>
                    {isActive && <CheckCircle className="h-4 w-4 text-blue-600" />}
                  </button>
                );
              })}
            </div>
            <div className="border-t border-gray-200 p-2">
              <button
                onClick={() => {
                  onCreatePipeline();
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md"
              >
                <Plus className="h-4 w-4" />
                {t('platform.salesPipeline.pipeline.createNewPipeline', 'Create New Pipeline')}
              </button>
              <button
                onClick={() => {
                  onManagePipelines();
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-md"
              >
                <Settings className="h-4 w-4" />
                {t('platform.salesPipeline.pipeline.managePipelines', 'Manage Pipelines')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// CREATE PIPELINE MODAL
// ============================================================================

interface CreatePipelineModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (pipeline: Omit<Pipeline, 'id' | 'createdAt' | 'updatedAt'>) => void;
}

function CreatePipelineModal({ isOpen, onClose, onSubmit }: CreatePipelineModalProps) {
  const { t } = useAppTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [template, setTemplate] = useState<'new-business' | 'expansion' | 'partnerships' | 'renewals' | 'custom'>('new-business');

  const templates = {
    'new-business': DEFAULT_NEW_BUSINESS_PIPELINE.stages,
    'expansion': DEFAULT_EXPANSION_PIPELINE.stages,
    'partnerships': DEFAULT_PARTNERSHIPS_PIPELINE.stages,
    'renewals': DEFAULT_RENEWALS_PIPELINE.stages,
    'custom': [
      { id: 'stage_1', name: 'Stage 1', position: 0, probability: 25, color: 'bg-gray-100' },
      { id: 'stage_2', name: 'Stage 2', position: 1, probability: 50, color: 'bg-blue-100' },
      { id: 'stage_3', name: 'Stage 3', position: 2, probability: 75, color: 'bg-yellow-100' },
      { id: 'closed_won', name: 'Closed Won', position: 3, probability: 100, color: 'bg-green-100', isWon: true },
      { id: 'closed_lost', name: 'Closed Lost', position: 4, probability: 0, color: 'bg-red-100', isLost: true },
    ],
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    onSubmit({
      name: name.trim(),
      description: description.trim() || undefined,
      isDefault: false,
      stages: templates[template].map((s, idx) => ({ ...s, id: `${name.toLowerCase().replace(/\s+/g, '_')}_${s.id}`, position: idx })),
    });

    setName('');
    setDescription('');
    setTemplate('new-business');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('platform.salesPipeline.pipeline.createNewPipeline', 'Create New Pipeline')}</DialogTitle>
          <DialogDescription>
            {t('platform.salesPipeline.pipeline.createPipelineDescription', 'Set up a new pipeline with custom stages for your sales process.')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('platform.salesPipeline.settings.pipelineName', 'Pipeline Name')}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('platform.salesPipeline.pipeline.placeholders.pipelineName', 'e.g., Enterprise Sales')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('platform.salesPipeline.pipeline.descriptionOptional', 'Description (Optional)')}</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('platform.salesPipeline.pipeline.placeholders.description', 'e.g., Pipeline for large enterprise deals')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('platform.salesPipeline.pipeline.startFromTemplate', 'Start from Template')}</label>
            <div className="space-y-2">
              {[
                { value: 'new-business', label: t('platform.salesPipeline.templates.newBusiness', 'New Business'), desc: t('platform.salesPipeline.templates.newBusinessDesc', 'Standard sales pipeline with discovery to close') },
                { value: 'expansion', label: t('platform.salesPipeline.templates.expansion', 'Expansion'), desc: t('platform.salesPipeline.templates.expansionDesc', 'Upsells and cross-sells to existing customers') },
                { value: 'partnerships', label: t('platform.salesPipeline.templates.partnerships', 'Partnerships'), desc: t('platform.salesPipeline.templates.partnershipsDesc', 'Strategic partnerships and channel deals') },
                { value: 'renewals', label: t('platform.salesPipeline.templates.renewals', 'Renewals'), desc: t('platform.salesPipeline.templates.renewalsDesc', 'Contract renewal tracking') },
                { value: 'custom', label: t('platform.salesPipeline.templates.custom', 'Custom'), desc: t('platform.salesPipeline.templates.customDesc', 'Start with basic stages') },
              ].map((opt) => (
                <label
                  key={opt.value}
                  className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    template === opt.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="template"
                    value={opt.value}
                    checked={template === opt.value}
                    onChange={(e) => setTemplate(e.target.value as typeof template)}
                    className="mt-1"
                  />
                  <div>
                    <span className="font-medium text-gray-900">{opt.label}</span>
                    <p className="text-sm text-gray-500">{opt.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              {t('platform.salesPipeline.common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {t('platform.salesPipeline.pipeline.createPipeline', 'Create Pipeline')}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// MANAGE PIPELINES MODAL
// ============================================================================

interface ManagePipelinesModalProps {
  isOpen: boolean;
  onClose: () => void;
  pipelines: Pipeline[];
  onDeletePipeline: (pipelineId: string) => void;
  onSetDefault: (pipelineId: string) => void;
  onEditStages: (pipeline: Pipeline) => void;
}

function ManagePipelinesModal({
  isOpen,
  onClose,
  pipelines,
  onDeletePipeline,
  onSetDefault,
  onEditStages,
}: ManagePipelinesModalProps) {
  const { t } = useAppTranslation();
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{t('platform.salesPipeline.pipeline.managePipelines', 'Manage Pipelines')}</DialogTitle>
          <DialogDescription>
            {t('platform.salesPipeline.pipeline.managePipelinesDescription', 'View, edit, and delete your sales pipelines.')}
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="space-y-3">
            {pipelines.map((pipeline) => (
              <div
                key={pipeline.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{pipeline.name}</span>
                    {pipeline.isDefault && (
                      <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                        {t('platform.salesPipeline.pipeline.default', 'Default')}
                      </span>
                    )}
                  </div>
                  {pipeline.description && (
                    <p className="text-sm text-gray-500 mt-1">{pipeline.description}</p>
                  )}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {pipeline.stages.filter(s => !s.isWon && !s.isLost).map((stage) => (
                      <span
                        key={stage.id}
                        className={`text-xs px-2 py-0.5 rounded ${stage.color} text-gray-700`}
                      >
                        {stage.name}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => onEditStages(pipeline)}
                    className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                    title={t('platform.salesPipeline.pipeline.editStages', 'Edit Stages')}
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  {!pipeline.isDefault && (
                    <button
                      onClick={() => onSetDefault(pipeline.id)}
                      className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg"
                      title={t('platform.salesPipeline.pipeline.setAsDefault', 'Set as Default')}
                    >
                      <CheckCircle className="h-4 w-4" />
                    </button>
                  )}
                  {pipelines.length > 1 && !pipeline.isDefault && (
                    <button
                      onClick={() => {
                        if (window.confirm(t('platform.salesPipeline.pipeline.confirmDelete', `Are you sure you want to delete "${pipeline.name}"? Opportunities will be moved to the default pipeline.`))) {
                          onDeletePipeline(pipeline.id);
                        }
                      }}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      title={t('platform.salesPipeline.pipeline.deletePipeline', 'Delete Pipeline')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            {t('platform.salesPipeline.common.close', 'Close')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function SalesPipelinePage() {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const [activeTab, setActiveTab] = useState<SalesTab>(() => {
    // Check for sub-tab navigation from notifications or URL params
    const urlParams = new URLSearchParams(window.location.search);
    const urlTab = urlParams.get('tab');
    const storedTab = localStorage.getItem('navigate_sub_tab');
    if (storedTab) {
      localStorage.removeItem('navigate_sub_tab');
    }
    const requestedTab = urlTab || storedTab;
    const validTabs: SalesTab[] = ['pipeline', 'leads', 'opportunities', 'sales_intel', 'products', 'activities', 'forecasts', 'settings'];
    if (requestedTab && validTabs.includes(requestedTab as SalesTab)) {
      return requestedTab as SalesTab;
    }
    return 'pipeline';
  });

  // State for Sales Intel tab - selected opportunity
  const [salesIntelOpportunityId, setSalesIntelOpportunityId] = useState<string | null>(null);

  // State for organizations (synced from /api/cbos)
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [orgLoading, setOrgLoading] = useState(true);

  // State for users (for opportunity owner assignment)
  const [users, setUsers] = useState<OrganizationUser[]>([]);

  // State for multiple pipelines
  const [pipelines, setPipelines] = useState<Pipeline[]>(DEFAULT_PIPELINES);
  const [activePipelineId, setActivePipelineId] = useState<string>('new-business');

  // Get the currently active pipeline
  const pipeline = pipelines.find(p => p.id === activePipelineId) || pipelines[0];

  // State for data
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [activities, setActivities] = useState<Activity[]>(mockActivities);
  const [products, setProducts] = useState<Product[]>([]);

  // Modal states
  const [addOpportunityOpen, setAddOpportunityOpen] = useState(false);
  const [addOpportunityStage, setAddOpportunityStage] = useState<string | undefined>();
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [scheduleActivityOpen, setScheduleActivityOpen] = useState(false);
  const [pipelineSettingsOpen, setPipelineSettingsOpen] = useState(false);
  const [addNoteOpen, setAddNoteOpen] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState<Opportunity | null>(null);
  const [opportunityDetailOpen, setOpportunityDetailOpen] = useState(false);
  const [pipelineSelectorOpen, setPipelineSelectorOpen] = useState(false);
  const [managePipelinesOpen, setManagePipelinesOpen] = useState(false);
  const [createPipelineOpen, setCreatePipelineOpen] = useState(false);
  const [emailIntegrationOpen, setEmailIntegrationOpen] = useState(false);

  // Subscriptions created from won deals
  const [subscriptions, setSubscriptions] = useState<CreatedSubscription[]>([]);
  const [showDemoRecords, setShowDemoRecords] = useState(false);

  // Filter opportunities by active pipeline (strict match — no pipelineId defaults to first pipeline)
  // Also exclude demo records unless explicitly shown
  const defaultPipelineId = pipelines.find(p => p.isDefault)?.id || pipelines[0]?.id || 'new-business';
  const pipelineOpportunities = opportunities.filter(
    opp => (opp.pipelineId || defaultPipelineId) === activePipelineId
      && (showDemoRecords || !opp.isDemoRecord)
  );

  // Fetch organizations from /api/cbos and create opportunities
  const fetchOrganizations = useCallback(async () => {
    setOrgLoading(true);
    let orgs: Organization[] = [];
    try {
      // /api/cbos is the healthcare org directory — it 401s for Triveny
      // platform JWTs. Catch + continue with an empty org list so the
      // backend opportunities fetch below STILL runs. Previously this
      // throw aborted the whole try-block, leaving the pipeline empty
      // even though the seeded demo data was fetchable.
      const response = await apiClient.get('/api/cbos');
      orgs = response.data.map((item: any) => ({
        id: item.id,
        name: item.name,
        short_name: item.short_name,
        customer_status: item.customer_status || 'DEMO',
        created_at: item.created_at,
        city: item.city,
        state: item.state,
        website: item.website,
        logo_url: item.logo_url,
        mrr: item.mrr ? parseFloat(item.mrr) : undefined,
      }));
    } catch (cbosErr) {
      console.warn('Skipping /api/cbos (likely 401 on Triveny platform JWT); continuing with empty org list', cbosErr);
    }
    setOrganizations(orgs);

    try {
      // Fetch all staff users for opportunity owner assignment
      fetchUsers();

      // Create org-derived opportunities only for orgs with meaningful sales data
      // (active customer status or MRR). Skip plain prospects to avoid $0 clutter.
      const defaultPid = pipelines.find(p => p.isDefault)?.id || pipelines[0]?.id || 'new-business';
      const ACTIVE_STATUSES = ['DEMO', 'TRIAL', 'ACTIVE', 'INACTIVE'];
      const orgOpportunities: Opportunity[] = orgs
        .filter((org) => {
          // Only create auto-opportunities for orgs with MRR or active sales status
          const hasRevenue = org.mrr && org.mrr > 0;
          const isActiveSales = ACTIVE_STATUSES.includes(org.customer_status || '');
          return hasRevenue || isActiveSales;
        })
        .map((org) => ({
          id: `org_${org.id}`,
          name: `${org.name} Deal`,
          company: org.name,
          companyId: org.id,
          amount: org.mrr ? org.mrr * 12 : 0,
          stage: STATUS_TO_STAGE_MAP[org.customer_status || 'DEMO'] || 'qualification',
          probability: DEFAULT_PIPELINE.stages.find(
            s => s.id === (STATUS_TO_STAGE_MAP[org.customer_status || 'DEMO'] || 'qualification')
          )?.probability || 25,
          closeDate: '',
          owner: 'Unassigned',
          lastActivity: org.created_at?.split('T')[0] || new Date().toISOString().split('T')[0],
          source: 'Organization Sync',
          createdAt: org.created_at,
          pipelineId: defaultPid,
        }));

      const orgById = new Map(orgs.map(o => [o.id, o]));

      // ===== BACKEND-FIRST LOADING =====
      // Backend is the single source of truth for opportunities.
      // Org-derived opportunities are ONLY used as fallback when backend is unavailable.
      let finalOpps: Opportunity[];
      let backendAvailable = false;
      try {
        const oppResponse = await apiClient.get('/api/platform/sales/opportunities');
        const apiOpps = oppResponse.data?.opportunities || [];
        backendAvailable = true;
        // Backend returns only opportunities for the active org (filtered by cbo_id).
        // Use backend data as-is — do NOT merge with org-derived or localStorage data.
        finalOpps = apiOpps;
        console.log(`✅ Loaded ${apiOpps.length} opportunities from backend API (org-isolated)`);
      } catch {
        console.warn('Backend opportunities API unavailable, using org-derived fallback');
        // Only fall back to org-derived opportunities if backend is completely unavailable
        finalOpps = orgOpportunities;
      }

      setOpportunities(finalOpps);

      // Self-healing handled server-side by the stage→status mirror in the
      // backend (sales_pipeline_router._mirror_stage_to_org_status).

      // Fetch marketing contacts as leads
      try {
        const contactsResponse = await contactsApi.getContacts({
          lifecycle_stage: 'lead,subscriber,mql', // Include lead-stage contacts
          status: 'active',
          limit: 100,
        });

        // Extract contacts array from API response - handle both response structures
        const contacts = contactsResponse?.data?.contacts || contactsResponse?.data?.items || [];
        const contactLeads: Lead[] = (contacts || []).map((contact: DirectoryContact) => ({
          id: `contact_${contact.id}`,
          name: `${contact.first_name} ${contact.last_name}`,
          email: contact.email,
          company: contact.company_name || contact.organization_name || '',
          companyId: contact.cbo_id,
          source: 'Marketing Contact',
          score: contact.score || 0,
          status: mapLifecycleToLeadStatus(contact.lifecycle_stage),
          createdAt: contact.created_at?.split('T')[0] || new Date().toISOString().split('T')[0],
        }));

        // Also create leads from prospect organizations (exclude orgs that already have opportunities)
        const allKnownOpps = finalOpps;
        const orgIdsWithOpportunities = new Set(allKnownOpps.filter(o => o.companyId).map(o => o.companyId));
        const orgLeads: Lead[] = orgs
          .filter(org => org.customer_status === 'DEMO' && !orgIdsWithOpportunities.has(org.id))
          .map((org) => ({
            id: `lead_${org.id}`,
            name: org.name,
            email: org.website ? `contact@${org.website.replace(/^https?:\/\//, '').split('/')[0]}` : '',
            company: org.name,
            companyId: org.id,
            source: 'Organization',
            score: 50,
            status: 'new' as const,
            createdAt: org.created_at?.split('T')[0] || new Date().toISOString().split('T')[0],
          }));

        // Fetch persisted leads from the backend (public.sales_leads) and merge:
        // real leads first, then derived (marketing contacts / demo orgs), deduped by id.
        let persistedLeads: Lead[] = [];
        try {
          const apiLeads: any = await leadsAPI.getLeads();
          const arr = Array.isArray(apiLeads) ? apiLeads : (apiLeads?.leads || []);
          persistedLeads = arr.map((l: any) => ({
            id: l.id,
            name: l.name,
            email: l.email || '',
            company: l.company || '',
            source: l.source || 'Manual',
            score: l.score ?? 0,
            status: (l.status || 'new') as Lead['status'],
            createdAt: (l.createdAt || l.created_at || new Date().toISOString()).split('T')[0],
          }));
        } catch (e) {
          console.warn('Failed to fetch persisted leads:', e);
        }
        const derived = [...contactLeads, ...orgLeads];
        const seenIds = new Set(persistedLeads.map(l => l.id));
        setLeads([...persistedLeads, ...derived.filter(l => !seenIds.has(l.id))]);
      } catch (contactErr) {
        console.error('Failed to fetch marketing contacts:', contactErr);
        // Fall back to organization-only leads (exclude orgs with opportunities)
        const allKnownOppsFallback = finalOpps;
        const fallbackOrgIdsWithOpps = new Set(allKnownOppsFallback.filter(o => o.companyId).map(o => o.companyId));
        const orgLeads: Lead[] = orgs
          .filter(org => org.customer_status === 'DEMO' && !fallbackOrgIdsWithOpps.has(org.id))
          .map((org) => ({
            id: `lead_${org.id}`,
            name: org.name,
            email: org.website ? `contact@${org.website.replace(/^https?:\/\//, '').split('/')[0]}` : '',
            company: org.name,
            companyId: org.id,
            source: 'Organization',
            score: 50,
            status: 'new' as const,
            createdAt: org.created_at?.split('T')[0] || new Date().toISOString().split('T')[0],
          }));
        setLeads(orgLeads);
      }
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
      // Fallback to empty state
      setOrganizations([]);
    } finally {
      setOrgLoading(false);
    }
  }, []);

  // Load organizations on mount
  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  // Fetch all staff users across all organizations for opportunity owner assignment
  // Uses a single backend endpoint instead of per-org calls to avoid 403 noise
  const fetchUsers = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/users/all-staff');
      const staffUsers: OrganizationUser[] = response.data || [];
      setUsers(staffUsers);
    } catch (err) {
      // Fallback: fetch from active CBO only if all-staff endpoint not available
      console.warn('all-staff endpoint unavailable, falling back to active CBO users');
      try {
        const cboId = localStorage.getItem('active_cbo_id');
        if (cboId) {
          const orgUsers = await userManagementAPI.listUsers(parseInt(cboId));
          const staffOnly = orgUsers.filter(u => u.user_type !== 'member');
          setUsers(staffOnly);
        }
      } catch (fallbackErr) {
        console.error('Failed to fetch users:', fallbackErr);
      }
    }
  }, []);

  // Load saved pipelines configuration from localStorage/API
  useEffect(() => {
    const loadPipelinesConfig = async () => {
      try {
        // Try to load from API first, with localStorage fallback
        const savedPipelines = await pipelineAPI.getPipelines();
        if (savedPipelines.length > 0) {
          setPipelines(savedPipelines);
        } else {
          // Fallback to localStorage
          const storedPipelines = localStorage.getItem('sales_pipelines_config');
          if (storedPipelines) {
            const parsed = JSON.parse(storedPipelines);
            if (Array.isArray(parsed) && parsed.length > 0) {
              setPipelines(parsed);
            }
          }
        }

        const savedActivePipelineId = await pipelineAPI.getActivePipelineId();
        if (savedActivePipelineId) {
          setActivePipelineId(savedActivePipelineId);
        } else {
          const storedActivePipelineId = localStorage.getItem('sales_active_pipeline_id');
          if (storedActivePipelineId) {
            setActivePipelineId(storedActivePipelineId);
          }
        }
      } catch (error) {
        console.error('Failed to load pipelines config:', error);
      }
    };
    loadPipelinesConfig();
  }, []);

  // Saved opportunities are now loaded and merged inside fetchOrganizations()
  // to avoid race condition where fetchOrganizations would overwrite saved data

  // Load products from backend API first, fall back to localStorage
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const response = await apiClient.get('/api/platform/sales/products');
        const apiProducts = response.data?.products || [];
        if (apiProducts.length > 0) {
          setProducts(apiProducts);
          return;
        }
      } catch { /* backend unavailable, fall through */ }
      // Fallback to localStorage
      try {
        const saved = localStorage.getItem(salesPipelineAPI.STORAGE_KEYS.PRODUCTS);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setProducts(parsed);
            // Sync to backend for persistence
            apiClient.post('/api/platform/sales/products/sync', { products: parsed }).catch(() => {});
          }
        }
      } catch (error) {
        console.error('Failed to load saved products:', error);
      }
    };
    loadProducts();
  }, []);

  // Load activities from backend API (with pagination)
  const loadActivities = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/platform/sales/activities', {
        params: { page: 1, page_size: 500 },
      });
      const apiActivities = response.data?.activities || [];
      setActivities(apiActivities);
    } catch (error) {
      console.error('Failed to load activities from API:', error);
    }
  }, []);

  useEffect(() => {
    loadActivities();
    // Clean up stale activities localStorage cache (no longer used)
    const cboId = localStorage.getItem('active_cbo_id') || '';
    try { localStorage.removeItem(`sales_pipeline_activities_${cboId}`); } catch {}
  }, [loadActivities]);

  // Save pipelines to localStorage when they change
  useEffect(() => {
    pipelineAPI.savePipelines(pipelines);
  }, [pipelines]);

  // Save active pipeline id to localStorage when it changes
  useEffect(() => {
    pipelineAPI.setActivePipeline(activePipelineId);
  }, [activePipelineId]);

  // Save opportunities to localStorage as cache when they change
  useEffect(() => {
    opportunitiesAPI.saveOpportunities(opportunities);
  }, [opportunities]);

  // Save products to localStorage as cache when they change
  useEffect(() => {
    productsAPI.saveProducts(products);
  }, [products]);

  // Activities are loaded from backend API — no localStorage cache needed (too large)

  const tabs: { id: SalesTab; label: string; icon: React.ReactNode }[] = [
    { id: 'pipeline', label: t('platform.salesPipeline.tabs.pipeline', 'Pipeline'), icon: <Target className="h-4 w-4" /> },
    { id: 'leads', label: t('platform.salesPipeline.tabs.leads', 'Leads'), icon: <Users className="h-4 w-4" /> },
    { id: 'opportunities', label: t('platform.salesPipeline.tabs.opportunities', 'Opportunities'), icon: <DollarSign className="h-4 w-4" /> },
    { id: 'sales_intel', label: t('platform.salesPipeline.tabs.salesIntel', 'Sales Intel'), icon: <BarChart3 className="h-4 w-4" /> },
    { id: 'products', label: t('platform.salesPipeline.tabs.productsAndPricing', 'Products & Pricing'), icon: <Package className="h-4 w-4" /> },
    { id: 'activities', label: t('platform.salesPipeline.tabs.activities', 'Activities'), icon: <Calendar className="h-4 w-4" /> },
    { id: 'forecasts', label: t('platform.salesPipeline.tabs.forecasts', 'Forecasts'), icon: <TrendingUp className="h-4 w-4" /> },
    { id: 'settings', label: t('platform.salesPipeline.tabs.settings', 'Settings'), icon: <Settings className="h-4 w-4" /> },
  ];

  // Calculate metrics across all pipelines for top-level stats (exclude demo records)
  const realOpportunities = opportunities.filter(o => !o.isDemoRecord);
  const totalPipeline = realOpportunities.reduce((sum, opp) => sum + opp.amount, 0);
  const allOpenDeals = realOpportunities.filter(o => {
    const oStage = pipelines.flatMap(p => p.stages).find(s => s.id === o.stage);
    return !oStage?.isWon && !oStage?.isLost;
  });
  const openDealsCount = allOpenDeals.length;

  // Current pipeline metrics for the summary bar
  const currentPipelineValue = pipelineOpportunities.reduce((sum, opp) => sum + opp.amount, 0);

  // Handlers
  const handleAddOpportunity = async (oppData: Omit<Opportunity, 'id' | 'lastActivity'>, createNewOrg?: boolean) => {
    let companyId = oppData.companyId;

    // Note: We no longer create a new CBO org when adding an opportunity.
    // Opportunities reference existing organizations only — creating orgs here was duplicative.

    const newOpp: Opportunity = {
      ...oppData,
      companyId,
      id: `opp_${Date.now()}`,
      pipelineId: activePipelineId,  // Associate with current pipeline
      lastActivity: new Date().toISOString().split('T')[0],
      createdAt: new Date().toISOString(),
    };
    setOpportunities(prev => [newOpp, ...prev]);

    // Persist to backend
    apiClient.post('/api/platform/sales/opportunities', newOpp)
      .then(() => {
        console.log(`Saved new opportunity ${newOpp.id} to backend`);
        // If created from an email, link the email activity
        const pendingActivityId = (window as any).__pendingEmailActivityId;
        if (pendingActivityId) {
          handleAssignActivity(pendingActivityId, newOpp.id);
          delete (window as any).__pendingEmailActivityId;
          delete (window as any).__pendingEmailCompany;
        }
      })
      .catch(err => console.error('Failed to save new opportunity to backend:', err));
  };

  const handleOpenAddOpportunity = (stage?: string) => {
    setAddOpportunityStage(stage);
    setAddOpportunityOpen(true);
  };

  const handleStageChange = (opportunityId: string, newStage: string) => {
    const stageConfig = pipeline.stages.find(s => s.id === newStage);

    setOpportunities(prev => prev.map(opp => {
      if (opp.id === opportunityId) {
        // Log activity for stage change
        const stageChangeActivity: Activity = {
          id: `act_${Date.now()}`,
          type: 'stage_change',
          title: `Stage changed to ${stageConfig?.name || newStage}`,
          contact: opp.company,
          opportunityId: opp.id,
          dueDate: new Date().toISOString().split('T')[0],
          completed: true,
          completedAt: new Date().toISOString(),
          priority: 'low',
          createdAt: new Date().toISOString(),
        };
        setActivities(prevAct => [stageChangeActivity, ...prevAct]);

        // Persist activity to backend
        apiClient.post('/api/platform/sales/activities', stageChangeActivity)
          .catch(err => console.error('Failed to persist stage change activity:', err));

        // The backend mirrors stage → customer_status forward-only when the
        // /opportunities/{id}/stage endpoint is called below, so we only need
        // to push the MRR value here on a win.
        if (opp.companyId && stageConfig?.isWon && opp.amount > 0) {
          apiClient.put(`/api/cbos/${opp.companyId}`, {
            mrr: opp.amount / 12, // Store as MRR (deal amount is ARR)
          }).catch(err => {
            console.error('Failed to update org MRR:', err);
          });
        }

        // Persist stage change to backend
        apiClient.put(`/api/platform/sales/opportunities/${opp.id}/stage`, {
          stage: newStage,
          probability: stageConfig?.probability || opp.probability,
        }).catch(err => console.error('Failed to persist stage change:', err));

        return {
          ...opp,
          stage: newStage,
          probability: stageConfig?.probability || opp.probability,
          lastActivity: new Date().toISOString().split('T')[0],
        };
      }
      return opp;
    }));
  };

  const handleSavePipeline = (updatedPipeline: Pipeline) => {
    // Update the specific pipeline in the pipelines array
    setPipelines(prevPipelines =>
      prevPipelines.map(p => p.id === updatedPipeline.id ? updatedPipeline : p)
    );
  };

  // Handler to edit an opportunity (opens detail view in edit mode)
  const handleEditOpportunity = (opportunity: Opportunity) => {
    setSelectedOpportunity(opportunity);
    setOpportunityDetailOpen(true);
  };

  // Handler to delete an opportunity
  const handleDeleteOpportunity = (opportunity: Opportunity) => {
    if (confirm(`Are you sure you want to delete "${opportunity.name}"?`)) {
      setOpportunities(prev => prev.filter(opp => opp.id !== opportunity.id));
      // Also delete any related activities
      setActivities(prev => prev.filter(act => act.opportunityId !== opportunity.id));
      // Persist deletion to backend
      apiClient.delete(`/api/platform/sales/opportunities/${opportunity.id}`)
        .catch(err => console.error('Failed to delete opportunity from backend:', err));
    }
  };

  // Handler to add a new pipeline
  const handleAddPipeline = (newPipeline: Omit<Pipeline, 'id' | 'createdAt' | 'updatedAt'>) => {
    const pipeline: Pipeline = {
      ...newPipeline,
      id: `pipeline_${Date.now()}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setPipelines(prev => [...prev, pipeline]);
    setActivePipelineId(pipeline.id);
  };

  // Handler to delete a pipeline
  const handleDeletePipeline = (pipelineId: string) => {
    // Prevent deleting the last pipeline
    if (pipelines.length <= 1) return;

    // Move opportunities to the default pipeline
    const defaultPipeline = pipelines.find(p => p.isDefault) || pipelines[0];
    setOpportunities(prev =>
      prev.map(opp =>
        opp.pipelineId === pipelineId
          ? { ...opp, pipelineId: defaultPipeline.id, stage: defaultPipeline.stages[0]?.id || 'prospecting' }
          : opp
      )
    );

    // Remove the pipeline
    setPipelines(prev => prev.filter(p => p.id !== pipelineId));

    // Switch to default pipeline if active pipeline was deleted
    if (activePipelineId === pipelineId) {
      setActivePipelineId(defaultPipeline.id);
    }
  };

  // Handler to move opportunity to a different pipeline
  const handleMoveOpportunityToPipeline = (opportunityId: string, targetPipelineId: string) => {
    const targetPipeline = pipelines.find(p => p.id === targetPipelineId);
    if (!targetPipeline) return;

    const newStage = targetPipeline.stages[0]?.id || 'prospecting';
    const newProbability = targetPipeline.stages[0]?.probability || 10;

    setOpportunities(prev =>
      prev.map(opp =>
        opp.id === opportunityId
          ? {
              ...opp,
              pipelineId: targetPipelineId,
              stage: newStage,
              probability: newProbability,
            }
          : opp
      )
    );

    // Also update selectedOpportunity so the detail modal reflects the new pipeline
    setSelectedOpportunity(prev =>
      prev && prev.id === opportunityId
        ? { ...prev, pipelineId: targetPipelineId, stage: newStage, probability: newProbability }
        : prev
    );

    // Auto-switch to the target pipeline so the deal doesn't disappear
    setActivePipelineId(targetPipelineId);

    // Persist pipeline move to backend
    apiClient.put(`/api/platform/sales/opportunities/${opportunityId}`, {
      pipelineId: targetPipelineId,
      stage: newStage,
      probability: newProbability,
    }).catch(err => console.error('Failed to persist pipeline move:', err));
  };

  // Handler to set a pipeline as default
  const handleSetDefaultPipeline = (pipelineId: string) => {
    setPipelines(prev =>
      prev.map(p => ({
        ...p,
        isDefault: p.id === pipelineId,
      }))
    );
  };

  const handleAddLead = async (leadData: Omit<Lead, 'id' | 'createdAt'>) => {
    const newLead: Lead = {
      ...leadData,
      id: `lead_${Date.now()}`,
      createdAt: new Date().toISOString().split('T')[0],
    };
    setLeads([newLead, ...leads]);
    // Persist to public.sales_leads (localStorage fallback in the client)
    try {
      await leadsAPI.createLead({
        id: newLead.id,
        name: newLead.name,
        email: newLead.email,
        company: newLead.company,
        source: newLead.source,
        score: newLead.score,
        status: newLead.status,
      } as any);
    } catch (e) {
      console.error('Failed to persist lead:', e);
    }
  };

  const handleConvertLead = (lead: Lead) => {
    // Create opportunity from lead
    const newOpp: Opportunity = {
      id: `opp_${Date.now()}`,
      name: `${lead.company} Deal`,
      company: lead.company,
      companyId: lead.companyId,
      amount: 0,
      stage: 'prospecting',
      probability: 10,
      closeDate: '',
      owner: users.length > 0 ? users[0].name : 'Unassigned',
      lastActivity: new Date().toISOString().split('T')[0],
      createdAt: new Date().toISOString(),
    };
    setOpportunities(prev => [newOpp, ...prev]);
    // Persist new opportunity to backend
    apiClient.post('/api/platform/sales/opportunities', newOpp)
      .catch(err => console.error('Failed to persist converted lead opportunity:', err));
    // Update lead status to qualified (local + persist to backend for real leads)
    setLeads(leads.map(l => l.id === lead.id ? { ...l, status: 'qualified' as const } : l));
    apiClient.put(`/api/platform/sales/leads/${lead.id}`, { status: 'converted' })
      .catch(err => console.error('Failed to persist lead conversion:', err));
    // Switch to pipeline tab and show success message
    setActiveTab('pipeline');
    alert(`Lead "${lead.name}" has been converted to opportunity "${newOpp.name}" in the Prospecting stage.`);
  };

  const handleEmailLead = (lead: Lead) => {
    // Open email client with lead's email
    if (lead.email) {
      window.open(`mailto:${lead.email}?subject=Following up from our conversation`, '_blank');
      // Update lead status to contacted
      setLeads(leads.map(l => l.id === lead.id ? { ...l, status: 'contacted' as const } : l));
    } else {
      alert('No email address available for this lead');
    }
  };

  const handleCallLead = (lead: Lead) => {
    // For now, open tel: link - in future could integrate with VoIP
    // We don't have phone directly on leads, so check if we have related contact info
    alert(`Calling ${lead.name}...\n\nIn a production environment, this would initiate a call via your integrated phone system.`);
    // Update lead status to contacted
    setLeads(leads.map(l => l.id === lead.id ? { ...l, status: 'contacted' as const } : l));
  };

  const handleScheduleActivity = (activityData: Omit<Activity, 'id'>) => {
    const newActivity: Activity = {
      ...activityData,
      id: `act_${Date.now()}`,
      createdAt: new Date().toISOString(),
    };
    setActivities([newActivity, ...activities]);
    // Persist to backend
    apiClient.post('/api/platform/sales/activities', newActivity)
      .catch(err => console.error('Failed to persist activity:', err));
  };

  const handleCompleteActivity = (activity: Activity) => {
    const completedAt = new Date().toISOString();
    setActivities(activities.map(a => a.id === activity.id ? { ...a, completed: true, completedAt } : a));
    // Persist to backend
    apiClient.put(`/api/platform/sales/activities/${activity.id}`, { completed: true, completedAt })
      .catch(err => console.error('Failed to persist activity completion:', err));
  };

  const handleAddNote = (noteData: Omit<Activity, 'id'>) => {
    const newNote: Activity = {
      ...noteData,
      id: `note_${Date.now()}`,
    };
    setActivities([newNote, ...activities]);
    // Persist to backend
    apiClient.post('/api/platform/sales/activities', newNote)
      .catch(err => console.error('Failed to persist note:', err));
  };

  const handleAssignActivity = async (activityId: string, opportunityId: string) => {
    try {
      // Find the opportunity to get its organization_id
      const opp = opportunities.find(o => o.id === opportunityId);
      const params: any = { opportunity_id: opportunityId };
      if (opp?.companyId) {
        params.organization_id = opp.companyId;
      }
      await apiClient.post(`/api/crm-activities/activities/${activityId}/associate`, null, { params });

      // Auto-archive assigned email to clean up inbox (keeps it in CRM, moves to Outlook Archive)
      const activity = activities.find(a => a.id === activityId);
      if (activity?.type === 'email') {
        try {
          await apiClient.post('/api/crm-activities/outlook/emails/archive', { activity_ids: [activityId] });
        } catch (archiveErr) {
          console.warn('Auto-archive after assignment failed (non-critical):', archiveErr);
        }
      }

      // Remove from inbox (archived) and reload to get updated state
      setActivities(prev => prev.filter(a => a.id !== activityId));
    } catch (error: any) {
      console.error('Failed to assign activity:', error);
      alert(`Failed to assign: ${error?.response?.data?.detail || error?.message}`);
      try {
        const response = await apiClient.get('/api/platform/sales/activities');
        setActivities(response.data?.activities || []);
      } catch (_) {}
    }
  };

  const handleAssignOrg = async (activityId: string, organizationId: number) => {
    try {
      await apiClient.post(`/api/crm-activities/activities/${activityId}/associate`, null, {
        params: { organization_id: organizationId },
      });
      setActivities(prev => prev.map(a =>
        a.id === activityId ? { ...a, organizationId } : a
      ));
    } catch (error: any) {
      console.error('Failed to assign org:', error);
      alert(`Failed to assign organization: ${error?.response?.data?.detail || error?.message}`);
    }
  };

  const handleToggleShareActivity = async (activityId: string, share: boolean) => {
    // Optimistic update
    setActivities(prev => prev.map(a =>
      a.id === activityId ? { ...a, sharedWithOrg: share } : a
    ));
    try {
      const path = share ? 'share' : 'unshare';
      await apiClient.post(`/api/platform/sales/activities/${activityId}/${path}`);
    } catch (error: any) {
      // Revert on failure
      setActivities(prev => prev.map(a =>
        a.id === activityId ? { ...a, sharedWithOrg: !share } : a
      ));
      console.error('Failed to toggle activity sharing:', error);
      alert(`Failed to ${share ? 'share' : 'unshare'} activity: ${error?.response?.data?.detail || error?.message}`);
    }
  };

  const handleAutoAssociate = async () => {
    const unassignedIds = activities
      .filter(a => a.type === 'email' && !a.opportunityId)
      .map(a => a.id);
    if (unassignedIds.length === 0) return;
    try {
      await apiClient.post('/api/crm-activities/activities/auto-associate', unassignedIds);
      // Reload activities to get updated associations
      const res = await apiClient.get('/api/platform/sales/activities');
      const apiActivities = res.data?.activities || [];
      if (apiActivities.length > 0) {
        setActivities(apiActivities);
      }
    } catch (error) {
      console.error('Failed to auto-associate:', error);
    }
  };

  const handleCreateOpportunityFromEmail = (activity: Activity) => {
    // Extract domain from email to suggest company name
    const fromEmail = activity.metadata?.from_email || '';
    const domain = fromEmail.split('@')[1] || '';
    const companyGuess = domain ? domain.split('.')[0] : '';
    // Pre-fill and open AddOpportunity modal
    setAddOpportunityStage(pipeline.stages[0]?.id || 'prospecting');
    setAddOpportunityOpen(true);
    // Store the source activity id to link after creation
    (window as any).__pendingEmailActivityId = activity.id;
    (window as any).__pendingEmailCompany = companyGuess;
  };

  const handleDeleteActivities = async (ids: string[]) => {
    try {
      // Use Outlook-synced endpoint — deletes from CRM AND moves to Deleted Items in Outlook
      const res = await apiClient.post('/api/crm-activities/outlook/emails/delete', { activity_ids: ids });
      const crmCount = res.data?.deleted_from_crm || 0;
      const outlookCount = res.data?.outlook?.moved_to_trash || 0;
      console.log(`Deleted ${crmCount} from CRM, ${outlookCount} moved to Outlook trash`);
      setActivities(prev => prev.filter(a => !ids.includes(a.id)));
    } catch (error: any) {
      console.error('Failed to delete activities:', error);
      alert(`Failed to delete: ${error?.response?.data?.detail || error?.message}`);
      try {
        const response = await apiClient.get('/api/platform/sales/activities');
        setActivities(response.data?.activities || []);
      } catch (_) {}
    }
  };

  const handleArchiveActivities = async (ids: string[]) => {
    try {
      // Use Outlook-synced endpoint — archives in CRM AND moves to Archive in Outlook
      const res = await apiClient.post('/api/crm-activities/outlook/emails/archive', { activity_ids: ids });
      const crmCount = res.data?.archived_in_crm || 0;
      const outlookCount = res.data?.outlook?.archived_in_outlook || 0;
      console.log(`Archived ${crmCount} in CRM, ${outlookCount} archived in Outlook`);
      setActivities(prev => prev.filter(a => !ids.includes(a.id)));
    } catch (error: any) {
      console.error('Failed to archive activities:', error);
      alert(`Failed to archive: ${error?.response?.data?.detail || error?.message}`);
      try {
        const response = await apiClient.get('/api/platform/sales/activities');
        setActivities(response.data?.activities || []);
      } catch (_) {}
    }
  };

  const handleSyncEmails = async () => {
    try {
      // Sync can take 1-2 minutes for many emails — use extended timeout
      const syncRes = await apiClient.post('/api/crm-activities/email-integrations/outlook/sync', null, {
        timeout: 120000,
      });
      console.log('Email sync completed:', syncRes.data);
      // Reload activities after sync
      const response = await apiClient.get('/api/platform/sales/activities');
      setActivities(response.data?.activities || []);
    } catch (error: any) {
      console.error('Failed to sync emails:', error);
      const detail = error?.response?.data?.detail || error?.message || 'Unknown error';
      alert(`Email sync failed: ${detail}`);
    }
  };

  const handleCreateTask = async (task: any) => {
    try {
      const res = await apiClient.post('/api/platform/sales/tasks', task);
      const newTask = res.data?.task;
      if (newTask) {
        setActivities(prev => [...prev, {
          id: newTask.id,
          type: 'task' as const,
          title: newTask.title,
          description: newTask.description,
          priority: newTask.priority || 'medium',
          completed: false,
          dueDate: newTask.due_date,
          opportunityId: newTask.opportunity_id,
          assignedToUserId: newTask.assigned_to_user_id,
          assignedToName: newTask.assigned_to_name,
          taskStatus: newTask.task_status || 'not_started',
          sourceActivityId: newTask.source_activity_id,
          createdAt: newTask.created_at,
        }]);
      } else {
        // Reload activities to pick up new task
        const response = await apiClient.get('/api/platform/sales/activities');
        setActivities(response.data?.activities || []);
      }
    } catch (error: any) {
      console.error('Failed to create task:', error);
      alert(`Failed to create task: ${error?.response?.data?.detail || error?.message}`);
    }
  };

  const handleUpdateTask = async (taskId: string, updates: any) => {
    try {
      await apiClient.put(`/api/platform/sales/tasks/${taskId}`, updates);
      // Reload to get updated data
      const response = await apiClient.get('/api/platform/sales/activities');
      setActivities(response.data?.activities || []);
    } catch (error: any) {
      console.error('Failed to update task:', error);
      alert(`Failed to update task: ${error?.response?.data?.detail || error?.message}`);
    }
  };

  const handleCreateTaskFromEmail = (activity: Activity) => {
    // Create a task linked to this email via the backend create-task-from-activity endpoint
    const doCreate = async () => {
      try {
        const res = await apiClient.post(`/api/platform/sales/activities/${activity.id}/create-task`, {
          title: `Follow up: ${activity.title || '(No Subject)'}`,
          description: activity.description?.substring(0, 500),
          priority: 'medium',
        });
        const newTask = res.data?.task;
        if (newTask) {
          setActivities(prev => [...prev, {
            id: newTask.id,
            type: 'task' as const,
            title: newTask.title,
            description: newTask.description,
            priority: newTask.priority || 'medium',
            completed: false,
            dueDate: newTask.due_date,
            opportunityId: newTask.opportunity_id,
            assignedToUserId: newTask.assigned_to_user_id,
            assignedToName: newTask.assigned_to_name,
            taskStatus: newTask.task_status || 'not_started',
            sourceActivityId: newTask.source_activity_id,
            createdAt: newTask.created_at,
          }]);
        } else {
          const response = await apiClient.get('/api/platform/sales/activities');
          setActivities(response.data?.activities || []);
        }
      } catch (error: any) {
        console.error('Failed to create task from email:', error);
        alert(`Failed to create task: ${error?.response?.data?.detail || error?.message}`);
      }
    };
    doCreate();
  };

  const handleUpdateOpportunity = (updatedOpp: Opportunity) => {
    setOpportunities(prev => prev.map(o => o.id === updatedOpp.id ? updatedOpp : o));
    setSelectedOpportunity(updatedOpp);

    // Persist full opportunity to backend
    apiClient.put(`/api/platform/sales/opportunities/${updatedOpp.id}`, updatedOpp)
      .then(() => console.log(`Saved opportunity ${updatedOpp.id} to backend`))
      .catch(err => console.error('Failed to persist opportunity to backend:', err));

    // Also sync MRR to org record for any org-linked deal
    if (updatedOpp.companyId && updatedOpp.amount > 0) {
      apiClient.put(`/api/cbos/${updatedOpp.companyId}`, { mrr: updatedOpp.amount / 12 })
        .catch(err => console.error('Failed to persist MRR to org:', err));
    }
  };

  const handleOpenOpportunityDetail = (opportunity: Opportunity) => {
    setSelectedOpportunity(opportunity);
    setOpportunityDetailOpen(true);
  };

  // Handle subscription creation from won deals
  const handleCreateSubscription = (subscription: CreatedSubscription) => {
    setSubscriptions(prev => [...prev, subscription]);
    console.log('Subscription created:', subscription);
    // TODO: Send to backend API when available
    // await apiClient.post('/api/platform/revops/subscriptions', subscription);
  };

  // Product handlers
  const handleAddProduct = (productData: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>) => {
    const newProduct: Product = {
      ...productData,
      id: `prod_${Date.now()}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setProducts(prev => [...prev, newProduct]);
    // Persist to backend
    apiClient.post('/api/platform/sales/products', newProduct)
      .catch(err => console.error('Failed to persist product:', err));
  };

  const handleUpdateProduct = (product: Product) => {
    setProducts(prev => prev.map(p => p.id === product.id ? product : p));
    // Persist to backend
    apiClient.put(`/api/platform/sales/products/${product.id}`, product)
      .catch(err => console.error('Failed to persist product update:', err));
  };

  const handleDeleteProduct = (productId: string) => {
    if (confirm('Are you sure you want to delete this product?')) {
      setProducts(prev => prev.filter(p => p.id !== productId));
      // Persist to backend
      apiClient.delete(`/api/platform/sales/products/${productId}`)
        .catch(err => console.error('Failed to delete product from backend:', err));
    }
  };

  const handleAssignProductToOpportunity = (product: Product, opportunityId: string) => {
    // Convert product to line item and add to opportunity
    const lineItem: OpportunityLineItem = {
      id: `li_${Date.now()}`,
      productId: product.id,  // Link to product catalog
      itemName: product.name,
      description: product.description,
      pricingModel: product.pricingModel,
      quantity: 1,
      unitPrice: product.unitPrice,
      billingType: product.billingType,
      billingInterval: product.billingInterval,
      discountValue: 0,
      lineTotal: product.unitPrice,
      discountAmount: 0,
      netAmount: product.unitPrice,
      monthlyRecurring: product.billingType === 'recurring' ? product.unitPrice : 0,
      annualRecurring: product.billingType === 'recurring' ? product.unitPrice * 12 : 0,
    };

    setOpportunities(prev => prev.map(opp => {
      if (opp.id === opportunityId) {
        const lineItems = [...(opp.lineItems || []), lineItem];
        const totalAmount = lineItems.reduce((sum, li) => sum + li.netAmount, 0);
        const updated = {
          ...opp,
          lineItems,
          amount: totalAmount,
          lastActivity: new Date().toISOString().split('T')[0],
        };
        // Persist to backend
        apiClient.put(`/api/platform/sales/opportunities/${opportunityId}`, updated)
          .catch(err => console.error('Failed to persist product assignment:', err));
        return updated;
      }
      return opp;
    }));
  };

  return (
    // Triveny.ai users get the .triveny-platform wrapper which activates the
    // design-system overlay in src/styles/triveny-platform.css. WeaveIn users
    // see the unmodified Tailwind styling. No data/logic changes — pure visual.
    <div className={`p-6 max-w-7xl mx-auto${config.isTriveny ? ' triveny-platform' : ''}`}>
      {config.isTriveny && (
        <AgentAssistantPanel
          agentKey="sales.pipeline_manager"
          title="Pipeline assistant"
          greeting="I can read your pipeline, surface at-risk deals, and advance stages. What would you like to do?"
          examples={["What's stuck in my pipeline?", 'Which deals are closing this month?', 'Advance Acme to Proposal']}
        />
      )}
      {/* Header — title hidden for Triveny (TvPillarBar shows "ENGAGE · Pipeline"); controls kept. */}
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        {!config.isTriveny && (
          <div>
            {!tabbed && <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2"><Target className="h-6 w-6 text-blue-600" />{t('platform.salesPipeline.pageTitle', 'Sales Pipeline')}</h1>}
            <p className="text-gray-600 mt-1">{t('platform.salesPipeline.pageDescription', 'Manage leads, opportunities, and track sales performance')}</p>
          </div>
        )}
        <div className="flex items-center gap-3 md:ml-auto">
          <PipelineSelector
            pipelines={pipelines}
            activePipelineId={activePipelineId}
            onSelectPipeline={setActivePipelineId}
            onManagePipelines={() => setManagePipelinesOpen(true)}
            onCreatePipeline={() => setCreatePipelineOpen(true)}
          />
        </div>
      </div>

      {/* Pipeline Summary Bar */}
      {pipelines.length > 1 && (
        <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <p className="text-sm text-gray-600">{t('platform.salesPipeline.summary.currentPipeline', 'Current Pipeline')}</p>
                <p className="font-semibold text-gray-900">{pipeline.name}</p>
              </div>
              <div className="h-8 w-px bg-gray-200" />
              <div>
                <p className="text-sm text-gray-600">{t('platform.salesPipeline.summary.dealsInPipeline', 'Deals in Pipeline')}</p>
                <p className="font-semibold text-gray-900">{pipelineOpportunities.length}</p>
              </div>
              <div className="h-8 w-px bg-gray-200" />
              <div>
                <p className="text-sm text-gray-600">{t('platform.salesPipeline.summary.pipelineValue', 'Pipeline Value')}</p>
                <p className="font-semibold text-gray-900">{formatCurrency(currentPipelineValue)}</p>
              </div>
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-medium">{pipelines.length}</span> {t('platform.salesPipeline.summary.pipelines', 'pipelines')} • <span className="font-medium">{opportunities.length}</span> {t('platform.salesPipeline.summary.totalDeals', 'total deals')}
            </div>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-8">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <DollarSign className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('platform.salesPipeline.stats.totalPipelineValue', 'Total Pipeline Value')}</p>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(totalPipeline)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Target className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('platform.salesPipeline.stats.openDeals', 'Open Deals')}</p>
              <p className="text-xl font-bold text-gray-900">{openDealsCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Users className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('platform.salesPipeline.stats.activeLeads', 'Active Leads')}</p>
              <p className="text-xl font-bold text-gray-900">{leads.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Calendar className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('platform.salesPipeline.stats.tasksDue', 'Tasks Due')}</p>
              <p className="text-xl font-bold text-gray-900">{activities.filter(a => !a.completed).length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Opportunity Detail Full Page View */}
      {opportunityDetailOpen && selectedOpportunity && (
        <OpportunityDetailPage
          onClose={() => {
            setOpportunityDetailOpen(false);
            setSelectedOpportunity(null);
          }}
          opportunity={selectedOpportunity}
          pipeline={pipelines.find(p => p.id === (selectedOpportunity.pipelineId || defaultPipelineId)) || pipeline}
          pipelines={pipelines}
          activities={activities}
          products={products}
          users={users}
          onUpdate={handleUpdateOpportunity}
          onStageChange={handleStageChange}
          onAddActivity={handleScheduleActivity}
          onCreateSubscription={handleCreateSubscription}
          onMoveToPipeline={handleMoveOpportunityToPipeline}
        />
      )}

      {/* Normal page content - hidden when viewing opportunity detail */}
      {!opportunityDetailOpen && (
      <>

      {/* Demo Records Toggle */}
      {opportunities.some(o => o.isDemoRecord) && (
        <div className="mb-4 flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg w-fit">
          <input
            type="checkbox"
            id="showDemoRecords"
            checked={showDemoRecords}
            onChange={(e) => setShowDemoRecords(e.target.checked)}
            className="h-4 w-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
          />
          <label htmlFor="showDemoRecords" className="text-sm text-amber-800 cursor-pointer">
            {t('platform.salesPipeline.demo.showDemoRecords', 'Show demo records')}
            <span className="ml-1 text-xs text-amber-600">({opportunities.filter(o => o.isDemoRecord).length})</span>
          </label>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-4 sm:mb-6 overflow-x-auto -mx-2 sm:mx-0 px-2 sm:px-0">
        <nav className="flex space-x-2 sm:space-x-8 min-w-max">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1 sm:gap-2 py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.icon}
              <span className="hidden sm:inline">{tab.label}</span>
              {tab.id === 'activities' && (() => {
                const now = new Date();
                const pending = activities.filter(ev => {
                  if (ev.type !== 'meeting') return false;
                  const meta = ev.metadata || {};
                  if ((meta as any).is_organizer) return false;
                  const response = (meta as any).my_response || 'none';
                  if (response === 'accepted' || response === 'declined' || response === 'tentativelyAccepted') return false;
                  if (ev.activityDate && new Date(ev.activityDate) < new Date(now.getFullYear(), now.getMonth(), now.getDate())) return false;
                  return true;
                }).length;
                return pending > 0 ? (
                  <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold bg-amber-500 text-white rounded-full">
                    {pending}
                  </span>
                ) : null;
              })()}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'pipeline' && (
        <PipelineTab
          opportunities={pipelineOpportunities}
          pipeline={pipeline}
          onAddOpportunity={handleOpenAddOpportunity}
          onStageChange={handleStageChange}
          onOpenSettings={() => setPipelineSettingsOpen(true)}
          onOpportunityClick={handleOpenOpportunityDetail}
          onEditOpportunity={handleEditOpportunity}
          onDeleteOpportunity={handleDeleteOpportunity}
          loading={orgLoading}
        />
      )}
      {activeTab === 'leads' && (
        <LeadsTab
          leads={leads}
          onAddLead={() => setAddLeadOpen(true)}
          onConvertLead={handleConvertLead}
          onEmailLead={handleEmailLead}
          onCallLead={handleCallLead}
        />
      )}
      {activeTab === 'opportunities' && (
        <OpportunitiesTab
          opportunities={pipelineOpportunities}
          pipeline={pipeline}
          onAddOpportunity={() => handleOpenAddOpportunity()}
          onOpportunityClick={handleOpenOpportunityDetail}
        />
      )}
      {activeTab === 'products' && (
        <ProductsTab
          products={products}
          opportunities={pipelineOpportunities}
          onAddProduct={handleAddProduct}
          onUpdateProduct={handleUpdateProduct}
          onDeleteProduct={handleDeleteProduct}
          onAssignToOpportunity={handleAssignProductToOpportunity}
        />
      )}
      {activeTab === 'activities' && (
        <ActivitiesTab
          activities={activities}
          opportunities={opportunities}
          onScheduleActivity={() => setScheduleActivityOpen(true)}
          onCompleteActivity={handleCompleteActivity}
          onAddNote={() => setAddNoteOpen(true)}
          onOpenEmailIntegration={() => setEmailIntegrationOpen(true)}
          onAssignActivity={handleAssignActivity}
          onCreateOpportunityFromEmail={handleCreateOpportunityFromEmail}
          onAutoAssociate={handleAutoAssociate}
          onDeleteActivities={handleDeleteActivities}
          onArchiveActivities={handleArchiveActivities}
          onSyncEmails={handleSyncEmails}
          onCreateTask={handleCreateTask}
          onUpdateTask={handleUpdateTask}
          onCreateTaskFromEmail={handleCreateTaskFromEmail}
          orgUsers={users.map(u => ({ id: u.id, name: u.name, email: u.email }))}
          onRefreshActivities={loadActivities}
          onAssignOrg={handleAssignOrg}
          onToggleShare={handleToggleShareActivity}
        />
      )}
      {activeTab === 'sales_intel' && (
        <div className="flex flex-col md:flex-row gap-4 h-auto md:h-[calc(100vh-220px)]">
          {/* Opportunity selector sidebar */}
          <div className={`${salesIntelOpportunityId ? 'w-full md:w-80 max-h-[40vh] md:max-h-none' : 'w-full max-w-4xl mx-auto'} bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col`}>
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{t('platform.salesPipeline.salesIntel.selectOpportunity', 'Select Opportunity')}</h3>
              <p className="text-sm text-gray-500 mt-1">{t('platform.salesPipeline.salesIntel.description', 'Choose an opportunity to view sales intelligence, documents, fees, meetings, and activation details.')}</p>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {opportunities
                .filter(o => !o.isDemoRecord)
                .sort((a, b) => b.amount - a.amount)
                .map(opp => {
                  const stage = pipelines.flatMap(p => p.stages).find(s => s.id === opp.stage);
                  const isSelected = salesIntelOpportunityId === opp.id;
                  return (
                    <button
                      key={opp.id}
                      onClick={() => setSalesIntelOpportunityId(opp.id)}
                      className={`w-full text-left p-3 rounded-lg border transition-all ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900 truncate">{opp.company || opp.name}</span>
                        <span className="text-sm font-semibold text-gray-700 ml-2 whitespace-nowrap">
                          ${opp.amount?.toLocaleString() || '0'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        {stage && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${stage.color}`}>
                            {stage.name}
                          </span>
                        )}
                        {opp.owner && opp.owner !== 'Unassigned' && (
                          <span className="text-xs text-gray-500">{opp.owner}</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              {opportunities.filter(o => !o.isDemoRecord).length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <BarChart3 className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p>{t('platform.salesPipeline.salesIntel.noOpportunities', 'No opportunities available')}</p>
                </div>
              )}
            </div>
          </div>

          {/* Opportunity detail panel */}
          {salesIntelOpportunityId && (
            <div className="flex-1 bg-white rounded-lg border border-gray-200 overflow-hidden">
              <OpportunityDetailPanel
                opportunityId={salesIntelOpportunityId}
                onClose={() => setSalesIntelOpportunityId(null)}
                onUpdated={() => fetchOrganizations()}
              />
            </div>
          )}
        </div>
      )}
      {activeTab === 'forecasts' && <ForecastsTab opportunities={opportunities} pipeline={pipeline} />}
      {activeTab === 'settings' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('platform.salesPipeline.settings.title', 'Pipeline Settings')}</h3>
          <p className="text-gray-600 mb-4">
            {t('platform.salesPipeline.settings.configureDescription', 'Configure your sales pipeline stages, probabilities, and automation rules.')}
          </p>
          <button
            onClick={() => setPipelineSettingsOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Settings className="h-4 w-4" />
            {t('platform.salesPipeline.settings.configurePipeline', 'Configure Pipeline')}
          </button>
          <div className="mt-6 border-t border-gray-200 pt-6">
            <h4 className="font-medium text-gray-900 mb-3">{t('platform.salesPipeline.settings.currentStages', 'Current Stages')}</h4>
            <div className="space-y-2">
              {pipeline.stages.map((stage) => (
                <div key={stage.id} className={`flex items-center gap-3 p-3 ${stage.color} rounded-lg`}>
                  <span className="font-medium text-gray-900">{stage.name}</span>
                  <span className="ml-auto text-sm text-gray-600">{stage.probability}%</span>
                  {stage.isWon && <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded">{t('platform.salesPipeline.stages.won', 'Won')}</span>}
                  {stage.isLost && <span className="text-xs bg-red-200 text-red-800 px-2 py-0.5 rounded">{t('platform.salesPipeline.stages.lost', 'Lost')}</span>}
                </div>
              ))}
            </div>
          </div>
          <div className="mt-6 border-t border-gray-200 pt-6">
            <h4 className="font-medium text-gray-900 mb-3">{t('platform.salesPipeline.settings.dataSync', 'Data Sync')}</h4>
            <p className="text-sm text-gray-600 mb-3">
              {t('platform.salesPipeline.settings.dataSyncDescription', 'Organizations are synced from the Administration page.')} {t('platform.salesPipeline.settings.lastSync', 'Last sync')}: {orgLoading ? t('platform.salesPipeline.common.loading', 'Loading...') : t('platform.salesPipeline.settings.justNow', 'Just now')}
            </p>
            <button
              onClick={fetchOrganizations}
              disabled={orgLoading}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${orgLoading ? 'animate-spin' : ''}`} />
              {t('platform.salesPipeline.settings.refreshOrganizations', 'Refresh Organizations')}
            </button>
          </div>
          <div className="mt-6 border-t border-gray-200 pt-6">
            <h4 className="font-medium text-gray-900 mb-3">Meeting Provider Integrations</h4>
            <p className="text-sm text-gray-600 mb-4">
              Configure Zoom, Google Meet, Microsoft Teams, or Jitsi for calendar meetings and recording integration.
            </p>
            <MeetingProviderSettings
              organizationId={0}
              onSettingsChange={() => {}}
            />
          </div>
        </div>
      )}

      </>
      )}

      {/* Modals */}
      <AddOpportunityModal
        isOpen={addOpportunityOpen}
        onClose={() => {
          setAddOpportunityOpen(false);
          setAddOpportunityStage(undefined);
        }}
        onSubmit={handleAddOpportunity}
        pipeline={pipeline}
        organizations={organizations}
        users={users}
        defaultStage={addOpportunityStage}
      />
      <AddLeadModal
        isOpen={addLeadOpen}
        onClose={() => setAddLeadOpen(false)}
        onSubmit={handleAddLead}
      />
      <ScheduleActivityModal
        isOpen={scheduleActivityOpen}
        onClose={() => setScheduleActivityOpen(false)}
        onSubmit={handleScheduleActivity}
        opportunities={opportunities}
      />
      <PipelineSettingsModal
        isOpen={pipelineSettingsOpen}
        onClose={() => setPipelineSettingsOpen(false)}
        pipeline={pipeline}
        onSave={handleSavePipeline}
      />
      <AddNoteModal
        isOpen={addNoteOpen}
        onClose={() => setAddNoteOpen(false)}
        onSubmit={handleAddNote}
        opportunities={opportunities}
      />
      {/* Multiple Pipelines Modals */}
      <CreatePipelineModal
        isOpen={createPipelineOpen}
        onClose={() => setCreatePipelineOpen(false)}
        onSubmit={handleAddPipeline}
      />
      <ManagePipelinesModal
        isOpen={managePipelinesOpen}
        onClose={() => setManagePipelinesOpen(false)}
        pipelines={pipelines}
        onDeletePipeline={handleDeletePipeline}
        onSetDefault={handleSetDefaultPipeline}
        onEditStages={(selectedPipeline) => {
          setActivePipelineId(selectedPipeline.id);
          setManagePipelinesOpen(false);
          setPipelineSettingsOpen(true);
        }}
      />

      {/* Email Integration Modal */}
      <EmailIntegrationSettings
        isOpen={emailIntegrationOpen}
        onClose={() => setEmailIntegrationOpen(false)}
      />
    </div>
  );
}

export default SalesPipelinePage;
